/**
 * Dhivehi (Thaana) message tree. Key shape must match `en.ts` exactly —
 * `completeness.spec.ts` enforces it mechanically.
 *
 * TRANSLATION STATUS — READ BEFORE SHIPPING TO FINANCE STAFF
 *
 * These strings were written by an engineer working from MIFCO's own English
 * vocabulary and from the wording already agreed in `apps/portal`'s Dhivehi
 * tree (which is itself awaiting a native pass). Every key exists and every
 * screen renders correctly in Thaana/RTL — that part is verified by tests.
 * The WORDING still needs a native Dhivehi speaker who knows MIFCO's finance
 * desk, and these are the keys to put in front of them first, because they
 * are the ones a finance officer would ACT on:
 *
 *   nav.items.payouts          'ފައިސާ ދޫކުރުން' for "Payout runs" — reads as
 *                              "disbursement", and loses the sense of a batched
 *                              RUN. If MIFCO staff say "payout run" in English,
 *                              this should become a transliteration instead.
 *   nav.items.journals         'ޖާނަލްތައް' — transliteration of the
 *   nav.items.ledger-check     'ލެޖަރ ޗެކް'   double-entry accounting terms.
 *                              Confirm the finance desk uses these and not the
 *                              Dhivehi equivalents.
 *   common.netWeight,          'ސާފު ބަރުދަން' for "net weight". Understood, but
 *   dashboard.ops.colNetSoFar  confirm against the weighbridge wording.
 *   dashboard.ops.throughput   'ތްރޫޕުޓް' — a straight transliteration; there
 *                              may be an established operations word.
 *   dashboard.exec.colMetric   'މިންގަނޑު' for the "Metric" column heading.
 *   common.required            'ލާޒިމް'.
 *   common.actions             'ޢަމަލުތައް' as a table column heading.
 *
 * Added for the reports.* / assistant.* screens (audit trail, management pack,
 * transaction search, vessel activity, the assistant chat and daily brief).
 * None of these state a wrong number on their own, but the wording is
 * engineering-confident, not native-confident:
 *
 *   reports.transactions.colSettledFrom   'ސެޓްލްކުރި ތަން' for "Settled from"
 *                                         on the transaction search table — a
 *                                         wallet/bank settlement-source column.
 *                                         Confirm this reads as that and not as
 *                                         something about the collection site.
 *   reports.transactions.colSubtotal,     'ސަބްޓޯޓަލް' / 'ޑިސްކައުންޓް' —
 *   reports.transactions.colDiscount      common loanwords in Maldivian retail;
 *                                         confirm MIFCO's own invoices use them.
 *   reports.audit.list.entityTypeLabel,   'އެންޓިޓީ ބާވަތް' / 'އެންޓިޓީ' —
 *   reports.audit.list.entityLabel,       transliteration of "entity", a term
 *   reports.audit.list.colEntityId        with no established Dhivehi word in
 *                                         this codebase; an auditor-facing
 *                                         screen, so worth a second look.
 *   reports.pack.colRegular,              'އާދައިގެ' for the "regular" landing
 *   reports.transactions.channelRegular   channel (as opposed to "sports").
 *                                         Confirm against the term MIFCO's
 *                                         collection desk already uses.
 *   assistant.chat.colReadFrom            'ބޭނުންކުރި ވިއު' for "Read from" —
 *                                         names the reporting view a citation
 *                                         came from; a new phrase, not reused
 *                                         from elsewhere in the app.
 *   assistant.chat.alertBodyAfter,        Long, technical sentences about what
 *   assistant.brief.notNarratedBody       the assistant tool loop can and
 *                                         cannot do. Translated in full rather
 *                                         than left in English because they
 *                                         set expectations about data access,
 *                                         but they are exactly the kind of
 *                                         long compound sentence a native
 *                                         speaker should read end to end.
 *   assistant.chat.warningFlaggedDefault, These state, in Dhivehi, that a
 *   assistant.brief.flaggedDefaultReason  currency figure in an answer/brief
 *                                         appeared in no tool result — the
 *                                         single most important sentence on
 *                                         this whole screen to get right,
 *                                         precisely because it is a warning
 *                                         about a possibly-wrong number.
 *   assistant.brief.narratedBadge,        'ބަޔާންކުރެވިފައި' / 'އަމިއްލައަށް
 *   assistant.brief.renderedBadge         ތައްޔާރުކުރެވިފައި' distinguishing
 *                                         "narrated by the model" from
 *                                         "rendered deterministically" — a
 *                                         nuance worth checking reads clearly
 *                                         as two different things at a glance.
 *   assistant.chat.emptyDescription,      The example question ("How did
 *   assistant.chat.questionPlaceholder    Kooddoo do this month compared with
 *                                         last month?") — translated for tone
 *                                         and naturalness, not literal risk.
 *
 * Added for the assistant chat MODAL (`assistant.modal.*`). Vocabulary is
 * reused from `assistant.chat.*` wherever the same idea already had a word
 * there — އެސިސްޓަންޓް, ސުވާލު, ވާހަކަ, ބަދަލު for a conversation turn, ވިއު,
 * މިންގަނޑު, 'ގުޅޭ ގޮތެއް ނުވި' for an unreachable service — so the modal and
 * the full page do not read as two different products. The genuinely new
 * wordings, worth a native eye:
 *
 *   assistant.modal.starter2,             Three example questions written FOR
 *   .starter3, .starter4                  a Dhivehi speaker rather than
 *                                         translated from the English ones.
 *                                         They are what the modal offers a
 *                                         first-time user to tap, so they must
 *                                         read as questions a MIFCO manager
 *                                         would actually ask out loud.
 *   assistant.modal.colFilters            'ފިލްޓަރކުރި ގޮތް', built to sit
 *                                         beside the existing
 *                                         assistant.chat.colGroupedBy
 *                                         ('ގްރޫޕްކުރި ގޮތް') in the same
 *                                         citation table.
 *   assistant.modal.unitMinutes,          'މިނިޓް' / 'ޢަދަދު' / 'އިންސައްތަ'
 *   .unitCount, .unitPercent              as citation unit names. `kg` and
 *                                         `laari` stay Latin by the rule
 *                                         below; these three do not, so
 *                                         confirm they read as units and not
 *                                         as values.
 *   assistant.modal.disabledTitle,        'ނިއްވާލެވިފައި' for a feature flag
 *   .disabledDescription                  that is switched off — the same word
 *                                         used for switching a light off.
 *                                         Confirm it does not read as broken.
 *   assistant.modal.providerDownBody      Says the reporting data is FINE and
 *                                         only the model provider failed. The
 *                                         distinction is the whole point of
 *                                         the sentence: a reader must not
 *                                         conclude the numbers are suspect.
 *   assistant.modal.composerHint,         'Enter', 'Shift', 'Ctrl K' stay
 *   assistant.modal.shortcutHint          Latin — they are the keycaps printed
 *                                         on the keyboard, not prose.
 *
 * Deliberately NOT translated, in both locales (see en.ts rule 4): MVR, laari,
 * kg, kg/h, ERP, API, CSV, PDF, the `rpt` view names, and the document-number
 * prefixes. `reports.audit.list` also keeps the `fish_bill` / `bill.finalize`
 * style filter placeholders as literal, untranslated examples of the
 * identifier format a user would type — they are not prose, and rendering
 * them in Thaana would misrepresent what the API actually expects.
 *
 * Added for the finance.* screens (journals, ledger check, wallet accounts
 * and statement, payout runs list/detail). Every key exists and renders in
 * Thaana; wording below is engineering-confident, not native-confident:
 *
 *   finance.payouts.detail.stepDraftLabel,  The five payout-run status words
 *   .stepApprovedLabel, .stepFileGeneratedLabel,
 *   .stepConfirmedLabel, .stepReconciledLabel,
 *   finance.payouts.list.statusDraft (+ siblings)
 *                                          transliterated ('ޑްރާފްޓް',
 *                                          'ފާސްކުރެވިފައި', 'ފައިލް
 *                                          ތައްޔާރުކުރެވިފައި',
 *                                          'ކޮންފާމްކުރެވިފައި',
 *                                          'ރިކޮންސައިލްކުރެވިފައި'), used
 *                                          identically in the stepper and the
 *                                          filter dropdown. Confirm MIFCO's
 *                                          finance desk actually uses these.
 *   finance.payouts.* "Run"                'ބުރު' (round/cycle) for a payout
 *   (runCardTitle, detailLabelRun,          batch, throughout — "Run no",
 *   confirmApproveLabel, etc.)             "New run", "Approve run", the
 *                                          breadcrumb. A semantic fit, not an
 *                                          established term of art.
 *   common.debits, common.credits          'ޑެބިޓް' / 'ކްރެޑިޓް'
 *                                          (transliterated), while
 *                                          common.dr / common.cr / common.drCr
 *                                          are left as plain 'Dr' / 'Cr' /
 *                                          'Dr/Cr' — no confident Thaana
 *                                          abbreviation for these short forms.
 *   finance.wallets.list.kindBankClearing  'ބޭންކް ކްލިއަރިންގ'
 *                                          (transliterated banking jargon).
 *   finance.payouts.detail.detailRail,     'ފައިސާ ދޭ ރެއިލް' / 'ރެއިލް' for
 *   finance.payouts.list.colRail           "payment rail" (the ACH/network a
 *                                          transfer travels over) — an
 *                                          obscure term with no known
 *                                          established Dhivehi equivalent.
 *   common.source, common.kind             'މަޞްދަރު' / 'ބާވަތް' — generic,
 *                                          but label journal/account columns
 *                                          a finance officer reads directly.
 *   finance.payouts.detail.statUnmatched   'ދާވާނުވި' — coined to match
 *                                          .unmatchedHelp's existing phrase
 *                                          'ދާވާ ނުވާ' ("does not
 *                                          correspond"); not an established
 *                                          short label.
 *   finance.payouts.detail.toastGeneratedBody  'ބައިޓް' for "bytes" — a
 *                                          straightforward transliteration,
 *                                          included here only because it
 *                                          ships alongside the SHA-256 hash
 *                                          in a toast a finance officer reads
 *                                          to confirm a bank file.
 *
 * Added for the billing.* screens (bills list/detail, invoices list/detail,
 * rate admin). Every key exists and renders in Thaana; wording below is
 * engineering-confident, not native-confident — these are financial screens
 * a finance officer acts on directly:
 *
 *   billing.bills.detail.reversedBadge,     'ރިވަރސްކުރެވިފައި' / 'ރިވަރސްކުރީ'
 *   billing.bills.detail.reversedByPrefix   coined by adding a passive/past
 *                                          suffix onto the established
 *                                          transliteration 'ރިވަރސަލް'
 *                                          (finance.journals.sourceReversal).
 *                                          No prior adjective/verb form of
 *                                          this word exists elsewhere in
 *                                          either app.
 *   billing.invoices.detail.invoiced       'އިންވޮއިސްކުރި' — coined the same
 *                                          way, a verb form built from the
 *                                          established noun 'އިންވޮއިސް'
 *                                          (nav.items.invoices). No prior
 *                                          verb form exists to check against.
 *   billing.invoices.detail.method         'ދެއްކިގޮތް' ("the way it was
 *                                          paid") for the payment-method
 *                                          column heading — a paraphrase,
 *                                          since no short Dhivehi noun for
 *                                          "method" is established anywhere
 *                                          in either app's tree.
 *   billing.invoices.list.statusOpen       'ނުދައްކާ' ("not paid") for an
 *                                          invoice's "Open" status — read as
 *                                          the opposite of Paid/Part paid
 *                                          rather than a literal rendering
 *                                          of "open". Confirm this is how
 *                                          finance actually describes an
 *                                          unpaid invoice.
 *   billing.rates.resolverOn               'ތާރީޚް' ("Date") for the bare
 *                                          "On" label on the rate-resolver
 *                                          form's date field — inferred from
 *                                          layout, not from a matching
 *                                          established short label.
 *   billing.rates.statePending              'އަދި ނުފަށާ' ("not yet started")
 *                                          for a rate published with a
 *                                          future effective date — a
 *                                          paraphrase; no established single
 *                                          word for "pending" in this sense.
 *   billing.rates.matchedBy                'ދިމާވީ ގޮތް' for "Matched by" on
 *                                          the rate resolver, built from the
 *                                          'ދާވެ' ("matches/corresponds")
 *                                          root used throughout finance.* —
 *                                          a semantic fit, not a term seen
 *                                          in this exact form before.
 *   billing.bills.list.note,                Long explanatory sentences about
 *   billing.bills.detail.draftAlertBody,   gapless numbering and
 *   billing.bills.detail.finalizeConfirmMessage,  immutability, translated
 *   billing.invoices.detail.recordConfirmMessage,  for meaning rather than
 *   billing.rates.alertBody,               word-for-word. Exactly the kind
 *   billing.rates.closeConfirmMessage      of compound sentence a native
 *                                          speaker should read end to end
 *                                          before this ships to finance
 *                                          staff.
 *   common.chartTimeAxis                   'ވަގުތު' ("time") for the plain X
 *                                          axis legend label on every
 *                                          `<UiChart timeAxis>` chart —
 *                                          straightforward, but confirm a
 *                                          MIFCO reader would not expect a
 *                                          more specific word like "date".
 */
const dv = {
  app: {
    name: 'MIFCO FCMS',
    // System codename, kept in Latin script like the rest of the industry —
    // not a re-statement of the brand, which the mark itself already carries.
    systemLabel: 'FCMS',
    subtitle: 'ބެކް އޮފީސް',
    fullName: 'މަސް ބަލައިގަތުން މެނޭޖްކުރާ ނިޒާމް',
  },

  common: {
    loading: 'ލޯޑްވަނީ…',
    saving: 'ރައްކާކުރަނީ…',
    save: 'ރައްކާކުރޭ',
    saveChanges: 'ބަދަލުތައް ރައްކާކުރޭ',
    cancel: 'ކެންސަލް',
    close: 'ބަންދުކުރޭ',
    back: 'ފަހަތަށް',
    next: 'ކުރިއަށް',
    confirm: 'ޔަގީންކުރޭ',
    yes: 'އާނ',
    no: 'ނޫން',
    ok: 'އޯކޭ',
    edit: 'އެޑިޓްކުރޭ',
    delete: 'ޑިލީޓްކުރޭ',
    remove: 'ނަގާ',
    create: 'އުފައްދާ',
    add: 'އިތުރުކުރޭ',
    search: 'ހޯދާ',
    filter: 'ފިލްޓަރ',
    applyFilters: 'ފިލްޓަރކުރޭ',
    clearFilters: 'ފިލްޓަރތައް ސާފުކުރޭ',
    reset: 'ރީސެޓް',
    refresh: 'އައުކުރޭ',
    refreshing: 'އައުކުރަނީ…',
    retry: 'އަލުން މަސައްކަތްކުރޭ',
    export: 'އެކްސްޕޯޓް',
    exportCsv: 'CSV އެކްސްޕޯޓް',
    download: 'ޑައުންލޯޑްކުރޭ',
    print: 'ޕްރިންޓްކުރޭ',
    copy: 'ކޮޕީކުރޭ',
    copied: 'ކޮޕީވެއްޖެ',
    viewAll: 'ހުރިހާ އެއްޗެއް ބައްލަވާ',
    viewDetails: 'ތަފްޞީލް ބައްލަވާ',
    details: 'ތަފްޞީލް',
    actions: 'ޢަމަލުތައް',
    status: 'ހާލަތު',
    all: 'ހުރިހާ',
    none: 'ނެތް',
    optional: 'އިޚްތިޔާރީ',
    required: 'ލާޒިމް',
    unknown: 'ނޭނގޭ',
    notAvailable: 'ލިބެން ނެތް',
    empty: 'އަދި މިތާ އެއްޗެއް ނެތް',
    noResults: 'އެއްވެސް ނަތީޖާއެއް ނުފެނުނު',
    somethingWrong: 'މައްސަލައެއް ދިމާވެއްޖެ',
    loadMore: 'އިތުރަށް ދައްކާ',
    dateFrom: 'ފެށޭ ތާރީޚް',
    dateTo: 'ނިމޭ ތާރީޚް',
    site: 'ކަލެކްޝަން ޕޮއިންޓް',
    siteChip: 'ކަލެކްޝަން ޕޮއިންޓް',
    allSites: 'ހުރިހާ ކަލެކްޝަން ޕޮއިންޓް',
    vessel: 'ދޯނި',
    fisherman: 'މަސްވެރިޔާ',
    fish: 'މަސް',
    ice: 'ސުޓޯ',
    diesel: 'ޑީސަލް',
    fishType: 'މަހުގެ ބާވަތް',
    amount: 'އަދަދު',
    rate: 'ރޭޓް',
    weight: 'ބަރުދަން',
    netWeight: 'ސާފު ބަރުދަން',
    total: 'ޖުމުލަ',
    notes: 'ނޯޓު',
    reference: 'ރެފަރެންސް',
    createdAt: 'އުފެއްދި ތާރީޚް',
    updatedAt: 'އައުކުރި ތާރީޚް',
    issuedAt: 'ދޫކުރި ތާރީޚް',
    dash: '—',
    kg: '{{value}} kg',
    never: 'ދުވަހަކުވެސް ނުވޭ',
    pcs: 'ޢަދަދު',
    gross: 'ގްރޮސް',
    net: 'ސާފު',
    captured: 'ނެގުނު',
    device: 'ޑިވައިސް',
    app: 'އެޕް',
    scale: 'ސްކޭލް',
    lastSeen: 'ފަހުން ފެނުނު ވަގުތު',
    lastAddress: 'ފަހު އެޑްރެސް',
    registered: 'ރަޖިސްޓަރީކުރި ތާރީޚް',
    session: 'ސެޝަން',
    revoke: 'ބާތިލްކުރޭ',
    revokeDevice: 'ޑިވައިސް ބާތިލްކުރޭ',
    revokeConfirmTitle: 'މި ޑިވައިސް ބާތިލްކުރައްވަނީތޯ؟',
    deviceRevoked: 'ޑިވައިސް ބާތިލްކުރެވިއްޖެ',
    deviceName: 'ޑިވައިސްގެ ނަން',
    value: 'އަގު',
    entries: 'އެންޓްރީ',
    grade: 'ގްރޭޑް',
    source: 'މަޞްދަރު',
    description: 'ތަފްޞީލު',
    memo: 'ނޯޓު',
    account: 'އެކައުންޓް',
    kind: 'ބާވަތް',
    dr: 'Dr',
    cr: 'Cr',
    drCr: 'Dr/Cr',
    debits: 'ޑެބިޓް',
    credits: 'ކްރެޑިޓް',
    amountLaari: '{{value}} laari',
    name: 'ނަން',
    nameEn: 'ނަން (އިނގިރޭސި)',
    nameDv: 'ނަން (ދިވެހި)',
    code: 'ކޯޑް',
    island: 'ރަށް',
    atoll: 'އަތޮޅު',
    active: 'އެކްޓިވް',
    partner: 'ޕާޓްނަރ',
    legacyRef: 'މަސްވެރިޔާ ސިސްޓަމްގެ ރެފަރެންސް',
    chartTimeAxis: 'ވަގުތު',
  },

  nav: {
    groups: {
      overview: 'ޚުލާސާ',
      collection: 'މަސް ބަލައިގަތުން',
      devices: 'ސްކޭލް އަދި ޑިވައިސްތައް',
      billing: 'ބިލްކުރުން',
      finance: 'ފައިނޭންސް',
      sports: 'ސްޕޯޓްސް ފިޝިންގ',
      reports: 'ރިޕޯޓް',
      integrations: 'އިންޓަގްރޭޝަން',
      masterdata: 'މާސްޓަރ ޑޭޓާ',
      settings: 'ސެޓިންގްސް',
    },
    items: {
      'dashboard-executive': 'އެގްޒެކެޓިވް ޑޭޝްބޯޑް',
      'dashboard-operations': 'އޮޕަރޭޝަންސް',
      'assistant-brief': 'ދުވަހުގެ ޚުލާސާ',
      sessions: 'ސެޝަންތައް',
      'manual-entry': 'މެނުއަލް އެންޓްރީ',
      queue: 'ސަފު ބޯޑް',
      devices: 'ޑިވައިސް ރަޖިސްޓަރީ',
      'device-health': 'ޑިވައިސްތަކުގެ ހާލަތު',
      'ingest-conflicts': 'ޑޭޓާ ފުށުއެރުންތައް',
      'csv-import': 'CSV އިމްޕޯޓް',
      bills: 'ބިލްތައް',
      invoices: 'އިންވޮއިސްތައް',
      rates: 'ރޭޓްތައް',
      wallets: 'ވޮލެޓް އެކައުންޓްތައް',
      journals: 'ޖާނަލްތައް',
      payouts: 'ފައިސާ ދޫކުރުން',
      'ledger-check': 'ލެޖަރ ޗެކް',
      'sports-requirements': 'ޝަރުތުތައް',
      'sports-registrations': 'ރަޖިސްޓްރޭޝަންތައް',
      'sports-quota-tokens': 'ކޯޓާ ޓޯކަން',
      'report-pack': 'މެނޭޖްމަންޓް ޕެކް',
      'report-transactions': 'މުޢާމަލާތް ހޯދުން',
      'report-vessels': 'ދޯނިތަކުގެ ހަރަކާތް',
      'report-audit': 'އޮޑިޓް ޓްރެއިލް',
      partners: 'ޕާޓްނަރުން',
      'api-keys': 'API ކީތައް',
      'request-log': 'ރިކުއެސްޓް ލޮގް',
      webhooks: 'ވެބްހުކް',
      'api-docs': 'API ޑޮކިއުމަންޓޭޝަން',
      'erp-mapping': 'ERP އަދި އެކްސްޕޯޓް',
      'md-collection-points': 'ކަލެކްޝަން ޕޮއިންޓްތައް',
      'md-vessels': 'ދޯނިތައް',
      'md-fishermen': 'މަސްވެރިން',
      'md-fish-types': 'މަހުގެ ބާވަތްތައް',
      'md-scales': 'ސްކޭލްތައް',
      announcements: 'އިއުލާންތައް',
      'profile-changes': 'ޕްރޮފައިލް ބަދަލުތައް',
      'my-sessions': 'އަހަރެންގެ ސެޝަންތައް',
      'my-security': 'ޓޫ-ފެކްޓަރ',
    },
  },

  /** Full-page states shown outside a normal screen: 403, 404, auth frame. */
  errors: {
    forbiddenTitle: 'ތިޔަ ބޭފުޅާއަށް ނުފެންނާނެ',
    forbiddenSubtitle: 'މި ސްކްރީން ތިޔަ ބޭފުޅާގެ ރޯލުގައި ނުހިމެނޭ',
    forbiddenMeansTitle: 'މީގެ މާނައަކީ',
    forbiddenAddressPrefix: 'ތިޔަ ބޭފުޅާ އެދުނު އެޑްރެހަކީ',
    forbiddenBody:
      'ސްކްރީނެއް ފޮރުވުމަކީ ފަސޭހައަކަށް ކުރާ ކަމެއް — ސާވަރުން ކޮންމެ ރިކުއެސްޓަކަށްވެސް ހަމަ އެ ގަވާއިދުތައް ތަންފީޒުކުރާތީ، އެއްވެސް މަޢުލޫމާތެއް ފެނިފައެއް ނުވޭ. މި ސްކްރީން ބޭނުންވާނަމަ، އެޑްމިނިސްޓްރޭޓަރަކަށް ތިޔަ ބޭފުޅާގެ ރޯލަށް އެ ހުއްދަ ދެވިދާނެ.',
    notFoundTitle: 'ޞަފްޙާ ނުފެނުނު',
    notFoundSubtitle: 'މި އެޑްރެހުގައި އެއްވެސް އެއްޗެއް ނެތް',
  },


  shell: {
    sectionsLabel: 'ބައިތައް',
    skipToContent: 'ކޮންޓެންޓަށް ދާ',
    openNav: 'މެނޫ ހުޅުވާ',
    closeNav: 'މެނޫ ބަންދުކުރޭ',
    primaryNav: 'މައި މެނޫ',
    catalogErrorTitle: 'ރެފަރެންސް ޑޭޓާ ލޯޑެއް ނުކުރެވުނު',
    renderErrorTitle: 'މި ސްކްރީން ހުއްޓިއްޖެ',
    renderErrorBody: 'ސަފުހާ ދައްކާލުމުގައި މައްސަލައެއް ދިމާވެއްޖެ. ސާވަރަށް އެއްވެސް އެއްޗެއް ފޮނުވިފައެއް ނުވޭ.',
    renderErrorRetry: 'އަލުން ދައްކާލަން މަސައްކަތްކުރޭ',
    accountLoading: 'ތިޔަބޭފުޅާގެ އެކައުންޓް ލޯޑްވަނީ',
    locale: {
      toDhivehi: 'ދިވެއްސަށް ބަދަލުކުރޭ',
      toEnglish: 'އިނގިރޭސިއަށް ބަދަލުކުރޭ',
    },
    theme: {
      toDark: 'ޑާކް ތީމަށް ބަދަލުކުރޭ',
      toSystem: 'ޑިވައިސްގެ ތީމާ އެއްގޮތަށް',
      toLight: 'ލައިޓް ތީމަށް ބަދަލުކުރޭ',
      change: 'ތީމް ބަދަލުކުރޭ',
    },
    site: {
      label: 'ކަލެކްޝަން ޕޮއިންޓް',
      all: 'ހުރިހާ ކަލެކްޝަން ޕޮއިންޓް',
      allHint: 'ހުރިހާ ކަލެކްޝަން ޕޮއިންޓެއް',
      mine: 'އަހަރެންގެ ކަލެކްޝަން ޕޮއިންޓްތައް',
    },
    user: {
      menuLabel: 'އެކައުންޓް',
      mySessions: 'އަހަރެންގެ ސެޝަންތައް',
      twoFactorOn: 'ޓޫ-ފެކްޓަރ (އޮން)',
      twoFactorOff: 'ޓޫ-ފެކްޓަރ (އޮފް)',
      signOut: 'ސައިން އައުޓް',
      signOutTitle: 'ސައިން އައުޓް ކުރައްވަނީތޯ؟',
      signOutMessage: 'ރައްކާނުކުރައްވާ ހުރި ހުރިހާ އެއްޗެއް ގެއްލޭނެ.',
      signedOut: 'ސައިން އައުޓް ވެއްޖެ',
    },
  },

  roles: {
    superadmin: 'ސުޕަރ އެޑްމިން',
    md_executive: 'މެނޭޖިންގ ޑިރެކްޓަރ',
    hq_ops: 'ހެޑް އޮފީސް އޮޕަރޭޝަންސް',
    site_supervisor: 'ސައިޓް ސުޕަވައިޒަރ',
    collector_operator: 'ކަލެކްޓަރ އޮޕަރޭޓަރ',
    finance: 'ފައިނޭންސް',
    auditor: 'އޮޑިޓަރ',
    partner: 'ޕާޓްނަރ',
  },

  dashboard: {
    exec: {
      title: 'އެގްޒެކެޓިވް ޑޭޝްބޯޑް',
      period: 'މުއްދަތު',
      range: {
        last30: 'ފާއިތުވި 30 ދުވަސް',
        last90: 'ފާއިތުވި 90 ދުވަސް',
        thisMonth: 'މި މަހު މިހާތަނަށް',
        lastMonth: 'ފާއިތުވި ފުރިހަމަ މަސް',
      },
      comparedWith: '{{range}} އާ އަޅާކިޔާފައި',
      noComparison: 'އަޅާކިޔުމެއް ނެތް — {{range}} ގައި އެއްވެސް ރެކޯޑެއް ނެތް',
      errorTitle: 'ޑޭޝްބޯޑް ލޯޑެއް ނުކުރެވުނު',
      tilesLabel: 'މުހިންމު އަދަދުތައް',
      collectedToday: 'މިއަދު ބަލައިގަތް',
      collectedTodayHelp:
        'މިއަދު ފެންނަ ހުރިހާ ކަލެކްޝަން ޕޮއިންޓަކުން ބަލައިގަތް ސާފު ބަރުދަން (rpt.site_daily).',
      vsYesterday: 'އިއްޔެއާ އަޅާކިޔާއިރު',
      vsPrevious: 'ކުރީ މުއްދަތާ އަޅާކިޔާއިރު',
      purchaseValue: 'ގަތް މަހުގެ އަގު',
      purchaseValueHelp:
        'ހޮއްވެވި މުއްދަތުގައި ގަތް މަހުގެ ޖުމުލަ އަގު، ބިލްތައް ފައިނަލްކުރުމުގެ ކުރިން.',
      payoutsPaid: 'ދޫކުރި ފައިސާ',
      payoutsPaidHelp: 'މި މުއްދަތުގައި ނިންމި ފައިސާ ދޫކުރުންތަކުގެ ޖުމުލަ (rpt.payouts_daily).',
      activeVessels: 'ހަރަކާތްތެރި ދޯނިތައް',
      landedToday: 'މިއަދު މަސް ބޭލި',
      activeVesselsHelp:
        'މިއަދު މަދުވެގެން އެއް ސެޝަން އޮތް ދޯނިތަކުގެ އަދަދު. ރިޕޯޓިންގ ވިއުތަކުގައި ކުރީ ދުވަހުގެ ދޯނީގެ އަދަދު ނުހުންނާތީ އަޅާކިޔުމެއް ނުދައްކާ.',
      sportsShare: 'ސްޕޯޓްސް ފިޝިންގގެ ހިއްސާ',
      sportsShareHelp: 'ބަލައިގަތް ބަރުދަނުގެ ތެރެއިން ސްޕޯޓްސް ފިޝިންގ މެދުވެރިކޮށް ލިބުނު ބައި.',
      devicesOffline: 'އޮފްލައިން ޑިވައިސްތައް',
      devicesOfflineHelp: 'ކަނޑައެޅިފައިވާ ވަގުތުގެ ތެރޭގައި ހާޓްބީޓެއް ނުފޮނުވާ ސްކޭލްތައް.',
      trendsLabel: 'ޓްރެންޑްތައް',
      bySiteTitle: 'ކަލެކްޝަން ޕޮއިންޓަކަށް ބަލައިގަތް މިންވަރު',
      bySiteSubtitle: 'ދުވާލަކަށް ބޭލި ސާފު ބަރުދަން',
      bySiteBlended: 'އެއްކޮށްފައި — {{count}} އަށްވުރެ ގިނަ ކަލެކްޝަން ޕޮއިންޓް',
      bySiteChartLabel: 'ދުވާލަކަށް ބޭލި ސާފު ބަރުދަން، ކޮންމެ ކަލެކްޝަން ޕޮއިންޓަކަށް އެއް ރޮނގު',
      bySiteEmpty: 'މި މުއްދަތުގައި މަހެއް ބާލާފައެއް ނުވޭ',
      seriesCollected: 'ބަލައިގަތް',
      valueTitle: 'ގަތް މަހުގެ އަގު',
      valueSubtitle: 'މި މުއްދަތާއި ކުރީ މުއްދަތު',
      valueSubtitleAlone: 'ދުވާލަކަށް ގަތް މަހުގެ އަގު',
      valueChartLabel: 'މި މުއްދަތާއި ކުރީ މުއްދަތުގެ ދުވަހުން ދުވަހަށް ގަތް މަހުގެ އަގު',
      valueEmpty: 'މި މުއްދަތުގައި މަހެއް ގަނެފައެއް ނުވޭ',
      seriesThisPeriod: 'މި މުއްދަތު',
      seriesPreviousPeriod: 'ކުރީ މުއްދަތު',
      compositionLabel: 'ބައިތައް',
      fishMixTitle: 'މަހުގެ ބާވަތްތައް',
      fishMixSubtitle: 'ބާވަތަކަށް ބަރުދަނުގެ ހިއްސާ',
      fishMixChartLabel: 'މަހުގެ ބާވަތަށް ބަލައި ބަލައިގަތް ބަރުދަނުގެ ހިއްސާ',
      fishMixCentre: 'އެންމެ ގިނައީ {{code}}',
      fishMixEmpty: 'ބައިކުރާނެ މަހެއް ނެތް',
      sitesTitle: 'ބަރުދަނަށް ބަލައި ކަލެކްޝަން ޕޮއިންޓްތައް',
      sitesSubtitle: 'މުއްދަތުގެ ޖުމުލަ',
      sitesChartLabel: 'ކަލެކްޝަން ޕޮއިންޓަކަށް ބޭލި ސާފު ބަރުދަން',
      sitesEmpty: 'ތަރުތީބުކުރާނެ މަހެއް ނެތް',
      comparisonTitle: 'މުއްދަތުތައް އަޅާކިޔުން',
      comparisonSubtitle: '{{current}} އާއި {{previous}}',
      comparisonCaption:
        'ހޮއްވެވި މުއްދަތުގެ މުހިންމު އަދަދުތައް، އެއްވަރުގެ ކުރީ މުއްދަތަކާ އަޅާކިޔާފައި',
      colMetric: 'މިންގަނޑު',
      colThisPeriod: 'މި މުއްދަތު',
      colPrevious: 'ކުރީ މުއްދަތު',
      colChange: 'ބަދަލު',
      rowCollected: 'ބަލައިގަތް',
      rowPurchaseValue: 'ގަތް މަހުގެ އަގު',
      rowBillsIssued: 'ދޫކުރި ބިލްތައް',
      rowPayoutsPaid: 'ދޫކުރި ފައިސާ',
      rowSportsShare: 'ސްޕޯޓްސް ފިޝިންގގެ ހިއްސާ',
      rowDevicesOffline: 'އޮފްލައިން ޑިވައިސްތައް',
      comparisonEmpty: 'އަދި އަޅާކިޔާނެ އެއްޗެއް ނެތް.',
      thisPeriodCaption: 'ހޮއްވެވި މުއްދަތުގެ މުހިންމު އަދަދުތައް',
      noPreviousData:
        '{{range}} ގައި މަހެއް ބަލައިގަނެ، ބިލްކޮށް، ފައިސާ ދޫކޮށްފައެއް ނުވޭ. އެހެންކަމުން މި މުއްދަތާ އަޅާކިޔާނެ ކުރީ މުއްދަތެއް ނެތް. ތިރީގައި މިވަނީ ހަމައެކަނި ހޮއްވެވި މުއްދަތުގެ އަދަދުތައް.',
      provenance:
        'މިތާ ހުރި ހުރިހާ އަދަދެއް ނަގާފައިވަނީ ވޯކަރު އައުކުރާ rpt ރިޕޯޓިންގ ވިއުތަކުން.',
      periodTotals: 'މުއްދަތުގެ ޖުމުލަ:',
    },

    ops: {
      title: 'އޮޕަރޭޝަންސް',
      subtitle: 'މިއަދު މިހާތަނަށް ބޭލި މިންވަރާއި މިވަގުތު ހިނގަމުންދާ ކަންކަން',
      live: 'ލައިވް',
      notLive: 'އަޕްޑޭޓެއް ނުލިބޭ',
      refreshedAgo: '{{when}} އައުކުރެވިފައި',
      never: 'އަދި ނުކުރެވޭ',
      errorTitle: 'އޮޕަރޭޝަންސް ސްކްރީން ލޯޑެއް ނުކުރެވުނު',
      todayLabel: 'މިއަދު މިހާތަނަށް',
      tilesLabel: 'މިހާރު',
      landedToday: 'މިއަދު ބޭލި',
      landedTodayHelp:
        'ރާއްޖެ ގަޑިން މެންދަމު 12 އިން ފެށިގެން، ފެންނަ ކަލެކްޝަން ޕޮއިންޓްތަކުގައި ބޭލި ސާފު ބަރުދަން. ކަލެކްޓަރ ދޯނިތައް ޓެލީ ބަންދުކުރަނީ އޮފްލައިންކޮށް ކަމަށްވާތީ، ފައިނަލްކޮށްފައިވާ ސެޝަންތައްވެސް މީގައި ހިމެނޭ — ހަމައެކަނި ހުޅުވިފައިވާ ސެޝަންތަކެއް ނޫން.',
      valueToday: 'މިއަދު ބޭލި މަހުގެ އަގު',
      valueTodayHelp:
        'ކޮންމެ އެންޓްރީއެއް ނަގަމުން ފްރީޒްކުރި ރޭޓުން ބަލާއިރު މިއަދުގެ ބަރުދަނުގެ އަގު. މީގެ ފަހަތުގައިވާ ބިލްތައް އަދި ޑްރާފްޓްކަމުގައި ވެދާނެ.',
      sessionsToday: 'މިއަދުގެ ސެޝަންތައް',
      sinceMidnight: 'ރާއްޖެ ގަޑިން 00:00 އިން ފެށިގެން',
      reportingSites: 'ރިޕޯޓްކުރި {{count}} ކަލެކްޝަން ޕޮއިންޓް',
      throughput: 'ތްރޫޕުޓް',
      lastMinutes: 'ފާއިތުވި {{minutes}} މިނެޓް',
      throughputHelp:
        'އެ ވަގުތުކޮޅުގެ ސާފު ބަރުދަން، ގަޑިއިރަކަށް ބަދަލުކޮށްފައި. މިއީ ރޭޓެއް ކަމަށްވާތީ، ފަހަކުން އެއްޗެއް ނުކިރާނަމަ ސުމަކަށް ދާނެ — މިއަދުގެ ޖުމުލަ ހުންނާނީ މަތީގައި.',
      kgPerHour: '{{value}} kg/h',
      openSessions: 'ހުޅުވިފައިވާ ސެޝަންތައް',
      openSessionsHelp:
        'އަދިވެސް ކިރަމުންދާ ސެޝަންތައް. ޑިވައިސްއަކުން އަންނަ ޓެލީ އަންނަނީ ބަންދުކޮށްފައި ކަމަށްވާތީ، މަސައްކަތު ދުވަހުގެ ބޮޑުބައި މި ޓައިލްއަކުން ނުފެންނާނެ.',
      openNoneNow: 'މިވަގުތު ކިރަމުންދާ އެއްޗެއް ނެތް',
      openWeight: 'ތިލަފަތުގައި {{weight}}',
      openStragglers: 'މިއަދުގެ ކުރިން ހުޅުވިފައި {{count}}',
      waitingInQueue: 'ސަފުގައި މަޑުކޮށްގެން',
      devicesOffline: 'އޮފްލައިން ޑިވައިސްތައް',
      depthTitle: 'ކަލެކްޝަން ޕޮއިންޓަކަށް ސަފުގެ ދިގުމިން',
      depthSubtitle: 'މިހާރު މަޑުކޮށްގެން ތިބި އަދި ޚިދުމަތްދެވެމުންދާ',
      depthWaiting: 'މަޑުކޮށްގެން',
      depthServing: 'ޚިދުމަތްދެނީ {{count}}',
      depthEmptyTitle: 'ސަފުގައި ހަރަކާތެއް ނެތް',
      depthEmptyDescription: 'އެއްވެސް ކަލެކްޝަން ޕޮއިންޓެއްގައި މީހަކު މަޑުކޮށްގެންނެއް ނެތް.',
      sessionsTitle: 'މިއަދުގެ ސެޝަންތަކާއި އަދިވެސް ހުޅުވިފައިވާ ސެޝަންތައް',
      sessionsTableLabel: 'މިއަދުގެ ސެޝަންތަކާއި، ކޮންމެ ދުވަހަކުން ވިޔަސް އަދިވެސް ހުޅުވިފައިވާ ސެޝަންތައް',
      colSite: 'ކަލެކްޝަން ޕޮއިންޓް',
      colVessel: 'ދޯނި',
      colStatus: 'ހާލަތު',
      colStarted: 'ފެށި ވަގުތު',
      colEntries: 'އެންޓްރީ',
      colNetSoFar: 'މިހާތަނަށް ސާފު ބަރުދަން',
      stragglerBadge: '{{day}} އިން ފެށިގެން ހުޅުވިފައި',
      sessionsEmptyTitle: 'މިއަދު އަދި އެއްވެސް އެއްޗެއް ނުބާލާ',
      sessionsEmptyDescription:
        'ކަލެކްޓަރ ދޯންޏަކުން ސިންކްކުރާއިރު ނުވަތަ ސުޕަވައިޒަރަކު ސެޝަނެއް ހުޅުވާއިރު މިތަނުން ފެންނާނެ. ކުރީގެ ދުވަސްތައް ހުންނާނީ ކަލެކްޝަން → ސެޝަންތަކުގައި.',
      healthTitle: 'ޑިވައިސްތަކުގެ ހާލަތު',
      healthTableLabel: 'ސްކޭލް ޑިވައިސްތަކުގެ ހާޓްބީޓް ހާލަތު',
      colDevice: 'ޑިވައިސް',
      colLink: 'ކަނެކްޝަން',
      colLastHeartbeat: 'ފަހު ހާޓްބީޓް',
      colApp: 'އެޕް',
      colPending: 'ފޮނުވަން ހުރި',
      colConnectivity: 'ކަނެކްޓިވިޓީ',
      healthEmptyTitle: 'ރަޖިސްޓަރީކޮށްފައިވާ ޑިވައިސްއެއް ނެތް',
      healthEmptyDescription: 'ޑިވައިސް ރަޖިސްޓަރީން އެންރޯލްމަންޓް ކޯޑެއް ދޫކުރައްވާ.',
      note: 'މިއަދުގެ ޖުމުލަ އަލުން ފެށޭނީ ރާއްޖެ ގަޑިން މެންދަމު 12 ގައި. ތްރޫޕުޓް ބަލާފައިވަނީ {{window}} ގެ ތެރޭގައި. ކޮންމެ ކަލެކްޝަން ޕޮއިންޓެއްގެ އެވްރެޖް މަޑުކުރާ ވަގުތު ހުންނާނީ ސަފު ބޯޑުގައި.',
    },
  },

  billing: {
    bills: {
      title: 'ބިލްތައް',
      type: 'ބާވަތް',
      channel: 'ޗެނަލް',
      settledFrom: 'ސެޓްލްކުރި ތަން',

      list: {
        subtitle: 'މަސް، ސުޓޯ އަދި ޑީސަލް — ޑްރާފްޓް، ފައިނަލްކުރި ޑޮކިއުމަންޓް، އަދި ބާތިލްކުރި ބިލްތައް',
        searchPlaceholder: 'މި ސަފުހާގައިވާ ބިލް ނަންބަރު',
        chipBillNo: 'މި ސަފުހާގެ ބިލް ނަންބަރު',
        errorTitle: 'ބިލްތައް ލޯޑެއް ނުކުރެވުނު',
        colBillNo: 'ބިލް ނަންބަރު',
        anyType: 'ކޮންމެވެސް ބާވަތެއް',
        anyStatus: 'ކޮންމެވެސް ހާލަތެއް',
        statusDraft: 'ޑްރާފްޓް',
        statusFinalized: 'ފައިނަލްކޮށްފައި',
        statusVoided: 'ބާތިލްކޮށްފައި',
        anyChannel: 'ކޮންމެވެސް ޗެނަލެއް',
        channelRegular: 'އާދައިގެ',
        channelSports: 'ސްޕޯޓްސް',
        noNumberBadge: 'ޑްރާފްޓް — އަދި ނަންބަރެއް ނެތް',
        reversedBadge: 'ރިވަރސްކުރެވިފައި',
        pageTotal: 'މި ސަފުހާ',
        emptyTitle: 'މި ފިލްޓަރތަކާ ދިމާވާ ބިލެއް ނެތް',
        emptyDescription: 'ތާރީޚް ރޭންޖް ފުޅާކުރައްވާ، ނުވަތަ ހާލަތު ފިލްޓަރ ސާފުކުރައްވާ.',
        note: 'ޑްރާފްޓެއްގައި ޑޮކިއުމަންޓް ނަންބަރެއް ނުހުންނާނެއެވެ: ނަންބަރު ދޫކުރެވެނީ ފައިނަލައިޒް ޓްރާންސެކްޝަންގެ ތެރެއިންނެވެ، މިއީ ކޮންމެ ސައިޓެއްގެ ސީރީޒް ވެސް ދޭތެރެއެއް ނެތި ދެމެހެއްޓުމަށްޓަކައެވެ.',
      },

      detail: {
        title: 'ބިލް',
        draftTitle: 'ޑްރާފްޓް ބިލް {{id}}',
        sessionButton: 'ސެޝަން',
        pdfButton: 'PDF',
        voidButton: 'ބާތިލްކުރޭ',
        finalizeButton: 'ފައިނަލްކުރޭ',
        errorTitle: 'މި ބިލް ލޯޑެއް ނުކުރެވުނު',
        voidedTitle: 'މި ބިލް ބާތިލްކޮށްފި',
        reversedByPrefix: 'ރިވަރސްކުރީ',
        voidedBody: 'ޑޮކިއުމަންޓަށް ބަދަލެއް ނާދޭ، އަދި ސީރީޒްގައި އޭގެ މަޤާމު ދަމަހައްޓައެވެ.',
        draftAlertTitle: 'ޑްރާފްޓް — އަދި ޑޮކިއުމަންޓަކަށް ނުވޭ',
        draftAlertBody:
          'ޑްރާފްޓެއްގައި ނަންބަރެއް ނުހުންނާނެ، އަދި ލެޖަރއަށް އަދި އެއްވެސް އެއްޗެއް ޕޯސްޓެއް ނުކުރެވެއެވެ. ފައިނަލްކުރުމުން ޓްރާންސެކްޝަންގެ ތެރެއިން ނަންބަރު ދޫކުރެވެއެވެ، މިއީ ކޮންމެ ސައިޓެއްގެ ސީރީޒް ދޭތެރެއެއް ނެތި ދެމެހެއްޓުމަށެވެ.',
        totalsTitle: 'ޖުމުލަތައް',
        subtotal: 'ސަބްޓޯޓަލް',
        discount: 'ޑިސްކައުންޓް',
        linesSumTo: 'ލައިންތަކުގެ ޖުމުލަ',
        quantity: 'ޢަދަދު',
        finalizedAt: 'ފައިނަލްކުރި ތާރީޚް',
        verifyCode: 'ޔަގީންކުރާ ކޯޑް',
        journal: 'ޖާނަލް',
        voidReason: 'ބާތިލްކުރި ސަބަބު',
        reasonLabel: 'ސަބަބު',
        reasonHint: 'ލާޒިމް. މިއީ ރިވަރސަލް ޑޮކިއުމަންޓްގައި އަދި އޮޑިޓް ޓްރެއިލްގައި ވެސް ފެންނާނެ.',
        linesTitle: 'ލައިންތައް',
        linesTableLabel: 'ބިލްގެ ލައިންތައް',
        colSeq: '#',
        colDescription: 'ތަފްޞީލު',
        colPcs: 'ޢަދަދު',
        lockedBadge: 'ލޮކްކޮށްފައި',
        linesEmptyTitle: 'މި ބިލްގައި ލައިނެއް ނެތް',
        linesEmptyDescription:
          'ސަޕްލައި ބިލެއްގައި ހުންނާނީ އެންމެ ލައިނެއް؛ ފިޝް ބިލެއް ބިނާވެފައި ވަނީ ސެޝަންގެ އެންޓްރީތަކުގެ މައްޗަށެވެ.',
        voidCardTitle: 'މި ބިލް ބާތިލްކުރޭ',
        voidCardSubtitle: 'ރިވަރސަލް ޑޮކިއުމަންޓެއް ދޫކުރެވޭނެ',
        voidSubmit: 'ބިލް ބާތިލްކުރޭ',
        finalizeConfirmTitle: 'މި ބިލް ފައިނަލްކުރައްވަނީތޯ؟',
        finalizeConfirmMessage:
          'ފައިނަލްކުރުމުން ޑޮކިއުމަންޓް ނަންބަރު ދޫކުރެވި، ޑަބަލް-އެންޓްރީ ޖާނަލް ޕޯސްޓުކުރެވި، ބިލް ބަދަލެއް ނުކުރެވޭ ގޮތަށްވެއެވެ. ފަހުން އިސްލާހެއް ބޭނުންވެއްޖެނަމަ، އެއީ ރިވަރސަލް ޑޮކިއުމަންޓެއް ދޫކުރާ ބާތިލެއް — ބިލް އަމިއްލައަށް ދުވަހަކުވެސް އެޑިޓެއް ނުކުރެވޭނެ.',
        finalizeConfirmLabel: 'ބިލް ފައިނަލްކުރޭ',
        finalizeToastTitle: 'ބިލް ފައިނަލްކުރެވިއްޖެ',
        finalizeErrorTitle: 'ފައިނަލްނުކުރެވުނު',
        voidConfirmTitle: 'މި ބިލް ބާތިލްކުރައްވަނީތޯ؟',
        voidConfirmMessage:
          'މީގެ ދެކޮޅަށް ރިވަރސަލް ޑޮކިއުމަންޓެއް ދޫކުރެވި، ވޮލެޓް ހަރަކާތް ރިވަރސްކުރެވޭނެ. އަސްލު ބިލްގެ ނަންބަރު ދެމިހުރެ، ސީރީޒްގައި ދެމިއޮންނާނެ — އެއްވެސް އެއްޗެއް ޑިލީޓެއް ނުކުރެވޭނެ.',
        voidToastTitle: 'ބިލް ބާތިލްކުރެވިއްޖެ',
        voidToastBody: 'ރިވަރސަލް ޑޮކިއުމަންޓެއް ދޫކުރެވިއްޖެ.',
        voidErrorTitle: 'ބިލް ބާތިލެއް ނުކުރެވުނު',
        pdfErrorTitle: 'PDF ތައްޔާރެއް ނުކުރެވުނު',
      },
    },

    invoices: {
      title: 'އިންވޮއިސްތައް',
      paid: 'ދެއްކި',
      outstanding: 'ބާކީ',

      list: {
        subtitle: 'ދޭންޖެހޭ މިންވަރު، ދައްކާފައިވާ މިންވަރު، އަދި ބާކީ ހުރި މިންވަރު',
        verifyButton: 'ކެޝްޑް ޖުމުލަތައް ޗެކްކުރޭ',
        driftAlertTitleSingular:
          '{{count}} އިންވޮއިސްގެ ޖުމުލަ، ރެކޯޑްކުރެވިފައިވާ ފައިސާ ދެއްކުންތަކާ ދާވަން ނުވެ',
        driftAlertTitlePlural:
          '{{count}} އިންވޮއިސްގެ ޖުމުލަތައް، ރެކޯޑްކުރެވިފައިވާ ފައިސާ ދެއްކުންތަކާ ދާވަން ނުވެ',
        driftLine: 'ކެޝްޑް {{cached}}, ހިސާބުކުރި {{derived}}',
        driftBody:
          'މިއީ ކެޝްއެއް ލެޖަރއާ ދާވަން ނުވުން، ފައިސާ ގެއްލުމެއް ނޫނެވެ. ދެން ހިނގާ ފައިސާ ދޫކުރުމުގެ ބުރުގެ ކުރިން ފައިނޭންސުން މިކަން ބައްލަވަން ޖެހޭނެ.',
        okTitle: 'ކޮންމެ ކެޝްޑް ޖުމުލައެއް ވެސް ދާވެއޭ',
        okToast: 'ކޮންމެ އިންވޮއިސްގެ ޖުމުލައެއް ވެސް، އޭގެ ފައިސާ ދެއްކުންތަކާ ދާވެއޭ',
        verifyErrorTitle: 'ޗެކް ފުރިހަމައެއް ނުވި',
        errorTitle: 'އިންވޮއިސްތައް ލޯޑެއް ނުކުރެވުނު',
        colInvoiceNo: 'އިންވޮއިސް ނަންބަރު',
        anyStatus: 'ކޮންމެވެސް ހާލަތެއް',
        statusOpen: 'ނުދައްކާ',
        statusPartPaid: 'ބައި ދައްކާފައި',
        statusPaid: 'ދައްކާފައި',
        statusCancelled: 'ކެންސަލްކޮށްފައި',
        emptyTitle: 'މި ފިލްޓަރތަކާ ދިމާވާ އިންވޮއިސްއެއް ނެތް',
        outstandingOnPage: 'މި ސަފުހާގެ ބާކީ',
      },

      detail: {
        title: 'އިންވޮއިސް',
        due: 'ދޭންޖެހޭ',
        settled: 'ސެޓްލްވެފައި',
        recordPaymentButton: 'ފައިސާ ދެއްކުން ރެކޯޑްކުރޭ',
        errorTitle: 'މި އިންވޮއިސް ލޯޑެއް ނުކުރެވުނު',
        balanceTitle: 'ބާކީ',
        invoiced: 'އިންވޮއިސްކުރި',
        paymentsSumTo: 'ފައިސާ ދެއްކުންތަކުގެ ޖުމުލަ',
        recordPaymentTitle: 'ފައިސާ ދެއްކުމެއް ރެކޯޑްކުރޭ',
        paymentErrorTitle: 'ފައިސާ ދެއްކުން ބަލައިނުގަނެވުނު',
        method: 'ދެއްކިގޮތް',
        methodCash: 'ކޭޝް',
        methodBankTransfer: 'ބޭންކް ޓްރާންސްފަރ',
        methodWalletOffset: 'ވޮލެޓުން ދެއްކި',
        referenceHint: 'ބޭންކް ރެފަރެންސް ނުވަތަ ރަސީދު ނަންބަރު.',
        receivedOn: 'ލިބުނު ތާރީޚް',
        paymentsTitle: 'ފައިސާ ދެއްކުންތައް',
        paymentsEmptyTitle: 'ފައިސާ ދެއްކުމެއް ރެކޯޑްކުރެވިފައި ނެތް',
        paymentsEmptyDescription: 'މި އިންވޮއިސްއަށް އަދި އެއްވެސް ފައިސާއެއް ލިބިފައި ނުވެއެވެ.',
        paymentsTableLabel: 'މި އިންވޮއިސްއަށް ދެއްކި ފައިސާ ދެއްކުންތައް',
        colReceived: 'ލިބުނު ތާރީޚް',
        invoiceLabel: 'އިންވޮއިސް',
        recordConfirmTitle: 'މި ފައިސާ ދެއްކުން ރެކޯޑްކުރައްވަނީތޯ؟',
        recordConfirmMessage:
          'ފައިސާ ދެއްކުމަކުން، އިންވޮއިސްގެ ދެކޮޅަށް ބެލެންސްވެފައިވާ ޖާނަލެއް ޕޯސްޓުކުރެވެއެވެ. ހިނގާފައި ނުވާ ފައިސާ ދެއްކުމެއް ރެކޯޑްކުރެވިއްޖެނަމަ، އިސްލާހުކުރަން ޖެހޭނީ ރިވަރސަލްއަކުން، ޑިލީޓްކޮށްގެނެއް ނޫނެވެ.',
        recordToast: 'ފައިސާ ދެއްކުން ރެކޯޑްކުރެވިއްޖެ',
        recordErrorTitle: 'ފައިސާ ދެއްކުން ރެކޯޑެއް ނުކުރެވުނު',
      },
    },

    rates: {
      title: 'ރޭޓްތައް',
      subtitle: 'ތާރީޚަކުން ފެށިގެން ހިނގާ، ކޮންމެ އެންޓްރީއެއް ނަގާ ވަގުތު ފްރީޒްކުރެވޭ',
      tableLabel: 'ޕަބްލިޝްކުރި ރޭޓްތައް',
      kind: 'ބާވަތް',
      grade: 'ގްރޭޑް',
      colWithIce: 'ސުޓޯއާއެކު',
      colState: 'ހާލަތު',
      colNote: 'ނޯޓު',
      anyKind: 'ކޮންމެ ބާވަތެއް',
      anyGrade: 'ކޮންމެ ގްރޭޑެއް',
      anySite: 'ކޮންމެ ސައިޓެއް',
      anyLabel: 'ކޮންމެ',
      allLabel: 'ހުރިހާ',
      openLabel: 'ހުޅުވިފައި',
      withIceBadge: 'ސުޓޯއާއެކު',
      stateActive: 'އެކްޓިވް',
      stateExpired: 'މުއްދަތު ހަމަވެފައި',
      statePending: 'އަދި ނުފަށާ',
      alertTitle: 'ޕަބްލިޝްކުރުމުން ދުވަހަކުވެސް ތާރީޚަށް ބަދަލެއް ނާންނާނެ',
      alertBody:
        'އެންޓްރީއެއް ނަގާ ވަގުތު ރޭޓެއް ހޯދި، އެ އެންޓްރީއަށް ފްރީޒްކުރެވެއެވެ. އައު ރޭޓެއް ޕަބްލިޝްކުރުމުން — ކުރިއަށް އޮތް ތާރީޚަކަށް ދޫކުރި ރޭޓެއް ކަމުގައި ވިޔަސް — ބަދަލުވަނީ ދެން ވާނެ ކަންތައް، މީގެ ކުރިން ވެފައިވާ އެއްވެސް ކަމަކަށް ބަދަލެއް ނާންނާނެ. މިއީ މި ސްކްރީނުގައި އެޑިޓް ފިއްތުމެއް ނެތި ވަނީ ސަބަބެވެ.',
      resolverTitle: 'ހިނގާ ރޭޓަކީ ކޮބާ؟',
      resolverSubtitle: 'ޓޭބަލުން ބެލުމުގެ ބަދަލުގައި، ސާވަރާ ސުވާލުކުރޭ',
      resolverOn: 'ތާރީޚް',
      withIceSwitch: 'ސުޓޯއާއެކު',
      resolveButton: 'ހޯދާ',
      resolveErrorTitle: 'ރޭޓެއް ނުފެނުނު',
      matchedBy: 'ދިމާވީ ގޮތް',
      perUnit: '{{unit}} އަކަށް',
      effectiveFrom: 'ފެށޭ ތާރީޚް',
      effectiveTo: 'ނިމޭ ތާރީޚް',
      specificity0: 'ޑިފޯލްޓް — ސައިޓެއް ނެތް، މަހުގެ ބާވަތެއް ނެތް',
      specificity1: 'މަހުގެ ބާވަތް',
      specificity2: 'ސައިޓް އަދި މަހުގެ ބާވަތް',
      specificity3: 'ސައިޓް، މަހުގެ ބާވަތް އަދި ގްރޭޑް',
      activeOn: 'އެކްޓިވްވާ ތާރީޚް',
      errorTitle: 'ރޭޓްތައް ލޯޑެއް ނުކުރެވުނު',
      emptyTitle: 'މި ފިލްޓަރތަކާ ދިމާވާ ރޭޓެއް ނެތް',
      closeAction: 'ބަންދުކުރޭ',
      publishTitle: 'ރޭޓެއް ޕަބްލިޝްކުރޭ',
      publishErrorTitle: 'ރޭޓް ޕަބްލިޝްނުކުރެވުނު',
      fishTypeHint: 'ކޮންމެ ބާވަތަކަށް ވެސް ހިނގާ ރޭޓަކަށްޓަކައި ހުސްކޮށްލައްވާ.',
      rateHint: 'މަހަށާއި ސުޓޯއަށް ކިލޯއަކަށް، ޑީސަލްއަށް ލީޓަރަކަށް.',
      withIceSwitchLabel: 'ސުޓޯއާއެކު ބާލި މަހަށް ހިނގާ',
      effectiveFromHint: 'ކުރިއަށް އޮތް ތާރީޚަކަށްވެސް ވެދާނެ. މީގެ ކުރިން ނަގާފައިވާ އެންޓްރީތަކުގައި ހުންނާނީ އޭގެ އަމިއްލަ ރޭޓް.',
      effectiveToHint: 'ނިމުމެއް ނެތްކަމަށް ބޭނުންނަމަ ހުސްކޮށްލައްވާ.',
      publishSubmit: 'ރޭޓް ޕަބްލިޝްކުރޭ',
      publishToastTitle: 'ރޭޓް ޕަބްލިޝްކުރެވިއްޖެ',
      publishToastBody: 'އޭގެ ފެށޭ ތާރީޚުން ފެށިގެން ނަގާ އެންޓްރީތަކުގައި މި ރޭޓް ބޭނުންކުރެވޭނެ.',
      closeConfirmTitle: 'މި ރޭޓް ބަންދުކުރައްވަނީތޯ؟',
      closeConfirmMessage:
        'ތިޔަބޭފުޅާ ހޮއްވަވާ ތާރީޚުން ފެށިގެން ރޭޓް ހިނގުން ހުއްޓޭނެ. މީގެ ދަށުން މިހާރު ނަގާފައިވާ އެންޓްރީތަކުގައި ރޭޓް ފްރީޒްކުރެވިފައި ދެމިހުންނާނެ — ބަންދުކުރުމުން ދުވަހަކުވެސް ތާރީޚަށް ބަދަލެއް ނާންނާނެ.',
      closeConfirmLabel: 'ރޭޓް ބަންދުކުރޭ',
      closingOn: 'ބަންދުކުރާ ތާރީޚް',
      closeToastTitle: 'ރޭޓް ބަންދުކުރެވިއްޖެ',
      closeErrorTitle: 'ރޭޓް ބަންދެއް ނުކުރެވުނު',
    },
  },

  finance: {
    journals: {
      title: 'ޖާނަލްތައް',
      subtitle: 'ކޮންމެ ފައިސާގެ ހަރަކާތެއް ވެސް، ބެލެންސްވެފައިވާ ޑަބަލް-އެންޓްރީ ޕޯސްޓިންގއެއްގެ ގޮތުގައި',
      tableLabel: 'ލެޖަރ ޖާނަލްތައް',
      anySource: 'ކޮންމެ މަޞްދަރެއް',
      sourceFishBill: 'މަސް ބިލް',
      sourceIceBill: 'ސުޓޯ ބިލް',
      sourceDieselBill: 'ޑީސަލް ބިލް',
      sourcePayout: 'ފައިސާ ދޫކުރުން',
      sourceAdjustment: 'އެޑްޖަސްޓްމަންޓް',
      sourceReversal: 'ރިވަރސަލް',
      errorTitle: 'ޖާނަލްތައް ލޯޑެއް ނުކުރެވުނު',
      colJournalNo: 'ޖާނަލް ނަންބަރު',
      colOccurred: 'ހިނގި ތާރީޚް',
      colReverses: 'ރިވަރސްކުރާ',
      emptyTitle: 'މި ފިލްޓަރތަކާ ދާވަން ޖާނަލެއް ނެތް',
      modalTitleFallback: 'ޖާނަލް',
      modalErrorTitle: 'ޖާނަލް ލޯޑެއް ނުކުރެވުނު',
      factPosted: 'ޕޯސްޓްކުރި ތާރީޚް',
      entriesTableLabel: 'ޖާނަލްގެ އެންޓްރީތައް',
      entriesEmptyTitle: 'މި ޖާނަލްގައި އެންޓްރީއެއް ނެތް',
      entriesEmptyDescription:
        'ޖާނަލެއްގައި އެންޓްރީއެއް ނެތި ނުވާނެ — ސަޕޯޓަށް ޖާނަލް ނަންބަރު ދެއްވާ.',
      balancedBadge: 'ބެލެންސްވެފައި',
      notBalancedBadge: 'ބެލެންސެއް ނުވޭ',
    },

    ledgerCheck: {
      title: 'ލެޖަރ ޗެކް',
      subtitle: 'ޖާނަލްތަކުން ކޮންމެ ބެލެންސެއް އަލުން ހިސާބުކޮށް، ތަފާތެއް ހުރިނަމަ ރިޕޯޓުކުރޭ',
      runAgain: 'އަލުން ހިންގާ',
      runCheck: 'ޗެކް ހިންގާ',
      lastRun: 'އެންމެ ފަހުން ހިންގީ {{time}}',
      infoTitle: 'މީގައި ބައްލަވަނީ މުޅި ލެޖަރ',
      infoBody:
        'ކޮންމެ އެކައުންޓެއް ވެސް އޭގެ ޖާނަލްތަކުން އަލުން ހިސާބުކުރެވޭ، އަދި ކޮންމެ ޖާނަލެއްގެ ވެސް ހުރިހާ އެންޓްރީ ޖަމާކުރެވޭ. ބިޒީ މަހެއްގައި ވަގުތުކޮޅެއް ހޭދަވެދާނެ؛ އެއްވެސް އެއްޗެއް ރައްކާނުކުރެވޭ.',
      errorTitle: 'ލެޖަރ ޗެކް ފުރިހަމައެއް ނުވި',
      runningLabel: 'ހުރިހާ ބެލެންސެއް އަލުން ހިސާބުކުރަނީ',
      notCheckedTitle: 'މި ސެޝަންގައި ލެޖަރ ޗެކްކޮށްފައެއް ނުވެ',
      notCheckedDescription: 'ޖާނަލްތަކުން ހުރިހާ ބެލެންސެއް އަލުން ހިސާބުކުރުމަށް ޗެކް ހިންގާ.',
      okTitle: 'ހިސާބުތައް ބެލެންސްވެއޭ',
      okBody:
        'ކޮންމެ ކެޝްޑް ބެލެންސެއް ވެސް އޭގެ ޖާނަލްތަކާ ދާވެ، ކޮންމެ ޖާނަލެއްގެ ވެސް ޑެބިޓް ކްރެޑިޓާ ހަމަވެ، އަދި ހުރިހާ އެކައުންޓެއްގެ ޖުމުލަ ސުމަކަށްވެއެވެ.',
      badTitle: 'ލެޖަރ ޗެކްގައި މައްސަލަތަކެއް ފެނިއްޖެ',
      badBody:
        'މިކަންކަން ދޭހަނުވަނީސް ފައިސާ ދޫކުރުމުގެ ބުރެއް ނުހިންގާ. ދާވަނީ ކޮންކަމެއްކަން ކޮންމެ ސެކްޝަނެއްގައި ވެސް ސީދާ ބުނެދީފައި ވެއެވެ.',
      tilesLabel: 'ލެޖަރ ޗެކްގެ ނަތީޖާ',
      statAccounts: 'ބެލި އެކައުންޓް',
      statJournals: 'ބެލި ޖާނަލް',
      statEntries: 'ބެލި އެންޓްރީ',
      statGrandTotal: 'ބޮޑު ޖުމުލަ',
      grandTotalHelp: 'ހުރިހާ އެކައުންޓެއްގެ ބެލެންސްގެ ޖުމުލަ. ޑަބަލް-އެންޓްރީ ލެޖަރއެއްގައި މިއީ އަބަދުވެސް ސުމެއް.',
      statOrphans: 'އެކަނިވެފައިވާ އެންޓްރީ',
      orphansHelp: 'ފުށަކުން ޖާނަލެއް ނެތް ލެޖަރ އެންޓްރީތައް. މިފަދަ އެއްޗެއް ދުވަހަކުވެސް ހުރެގެން ނުވާނެ.',
      driftCardTitle: 'ލެޖަރއާ ދާވަން ނުވާ ކެޝްޑް ބެލެންސްތައް',
      driftTableLabel: 'ކެޝްޑް ބެލެންސް، އަލުން ހިސާބުކުރި ބެލެންސާ ތަފާތުވާ އެކައުންޓްތައް',
      colCached: 'ކެޝްޑް',
      colDerived: 'ހިސާބުކުރި',
      colDifference: 'ތަފާތު',
      unbalancedCardTitle: 'ބެލެންސް ނުވާ ޖާނަލްތައް',
      unbalancedTableLabel: 'ޑެބިޓް ކްރެޑިޓާ ހަމަނުވާ ޖާނަލްތައް',
      colJournal: 'ޖާނަލް',
      colLegs: 'އެންޓްރީ',
      otherFindingsTitle: 'އެހެނިހެން ފާހަގަތައް',
    },

    wallets: {
      balance: 'ބެލެންސް',
      balanceErrorTitle: 'ބެލެންސް ކިޔައެއް ނުލެވުނު',

      list: {
        title: 'ވޮލެޓް އެކައުންޓްތައް',
        subtitle: 'ބެލެންސްތައް ނަގަނީ ލެޖަރއިން؛ ރައްކާކޮށްފައިވާ އަދަދު އެއީ ކެޝްއެއް',
        journalsButton: 'ޖާނަލްތައް',
        ledgerCheckButton: 'ލެޖަރ ޗެކް',
        readBalancesButton: 'މި ސަފްހާގެ ބެލެންސްތައް ކިޔާ',
        searchPlaceholder: 'އެކައުންޓް ލޭބަލް',
        anyKind: 'ކޮންމެ ބާވަތެއް',
        kindFishermanWallet: 'މަސްވެރިޔާގެ ވޮލެޓް',
        kindBankClearing: 'ބޭންކް ކްލިއަރިންގ',
        kindFishPayable: 'މަހަށް ދޭންޖެހޭ ފައިސާ',
        kindIceRevenue: 'ސުޓޯގެ އާމްދަނީ',
        kindDieselRevenue: 'ޑީސަލްގެ އާމްދަނީ',
        kindPartnerPayable: 'ޕާޓްނަރަށް ދޭންޖެހޭ ފައިސާ',
        kindCash: 'ކޭޝް',
        kindReceivable: 'ލިބެންޖެހޭ ފައިސާ',
        chipLabelContains: 'ލޭބަލްގައި ހިމެނޭ',
        errorTitle: 'އެކައުންޓްތައް ލޯޑެއް ނުކުރެވުނު',
        colOwner: 'ވެރިފަރާތް',
        colLedgerCheck: 'ލެޖަރ ޗެކް',
        emptyTitle: 'ދާވަން އެކައުންޓެއް ނެތް',
        syncMatches: 'ލެޖަރއާ ދާވެއޭ',
        syncDiffers: 'ކެޝްޑް ތަފާތު',
        notRead: 'ކިޔައިފައި ނުވެ',
        readingBalance: 'ބެލެންސް ކިޔަނީ',
        actionStatement: 'ސްޓޭޓްމަންޓް',
        note:
          'ބެލެންސް "ކެޝްޑް ތަފާތު" ދައްކާ ރޯއެއް، ފައިސާ ގެއްލުނުކަން މާނައެއް ނޫނެވެ — މިއީ ރައްކާކޮށްފައިވާ ބެލެންސް، ޖާނަލްތަކުގެ ޖުމުލައާ ދާވަން ނުވުމެވެ. ލެޖަރ ޗެކް ސްކްރީނުން އެއްފަހަރާ ހުރިހާ އެކައުންޓެއް އަލުން ހިސާބުކުރެވޭ، އެއީ މިކަން ބައްލަވަން އެންމެ ރަނގަޅު ތަނެވެ.',
      },

      statement: {
        title: 'އެކައުންޓް ސްޓޭޓްމަންޓް',
        breadcrumbStatement: 'ސްޓޭޓްމަންޓް',
        labelStored: 'ރައްކާކޮށްފައިވާ ބެލެންސް',
        labelDerived: 'ލެޖަރއިން އަލުން ހިސާބުކުރި',
        labelAgreement: 'ދާވުން',
        agreementMatches: 'ދާވެއޭ',
        agreementDiffers: 'ކެޝްޑް ބެލެންސް ލެޖަރއާ ތަފާތު',
        labelAsOf: 'މިހާތަނަށް',
        driftAlertTitle: 'މި އެކައުންޓްގެ ކެޝްޑް ބެލެންސް، ޖާނަލްތަކާ ދާވަން ނުވެ',
        driftAlertBody:
          'ލެޖަރއީ ރެކޯޑް؛ ކެޝްޑް އަދަދު އެއީ ފަސޭހަކަމަށް ބަހައްޓާ އެއްޗެއް. ފައިސާ ދޫކުރުމުގެ ބުރެއްގައި މި އެކައުންޓް ހިމަނުމުގެ ކުރިން ލެޖަރ ޗެކް ހިންގާ.',
        errorTitle: 'ސްޓޭޓްމަންޓް ލޯޑެއް ނުކުރެވުނު',
        tableLabel: 'އެކައުންޓް ސްޓޭޓްމަންޓް ލައިންތައް',
        colDate: 'ތާރީޚް',
        colMovement: 'ހަރަކާތް',
        emptyTitle: 'މި މުއްދަތުގައި ހަރަކާތެއް ނެތް',
        emptyDescription: 'ތާރީޚް ރޭންޖު ފުޅާކުރައްވާ، ނުވަތަ އެއްކޮށް ސާފުކުރައްވާ.',
      },
    },

    payouts: {
      list: {
        subtitle: 'ޑްރާފްޓް، ފާސްކުރުން، ބޭންކް ފައިލް ތައްޔާރުކުރުން، ކޮންފާމްކުރުން، ރިކޮންސައިލްކުރުން',
        newRun: 'އައު ބުރު',
        anyStatus: 'ކޮންމެ ހާލަތެއް',
        statusDraft: 'ޑްރާފްޓް',
        statusApproved: 'ފާސްކުރެވިފައި',
        statusFileGenerated: 'ފައިލް ތައްޔާރުކުރެވިފައި',
        statusConfirmed: 'ކޮންފާމްކުރެވިފައި',
        statusReconciled: 'ރިކޮންސައިލްކުރެވިފައި',
        errorTitle: 'ފައިސާ ދޫކުރުމުގެ ބުރުތައް ލޯޑެއް ނުކުރެވުނު',
        colRunNo: 'ބުރު ނަންބަރު',
        colPeriodFrom: 'މުއްދަތު ފެށޭ ތާރީޚް',
        colPeriodTo: 'މުއްދަތު ނިމޭ ތާރީޚް',
        colRail: 'ރެއިލް',
        emptyTitle: 'ފައިސާ ދޫކުރުމުގެ ބުރެއް ނެތް',
        emptyDescription: 'މުއްދަތެއްގެ ބިލްތައް ފައިނަލްކުރެވުމުން، އެ މުއްދަތަށް ބުރެއް އުފައްދާ.',
        modalTitle: 'އައު ފައިސާ ދޫކުރުމުގެ ބުރު',
        modalNote:
          'ފައިނަލްކުރެވިފައިވާ ބިލްތަކުން، މުއްދަތުގައި ޕޮޒިޓިވް ވޮލެޓް ބެލެންސެއް ހުރި ކޮންމެ މަސްވެރިޔެއް ބުރުގައި ހިމެނޭ. ބުރު އުފެއްދެނީ ޑްރާފްޓެއްގެ ގޮތުގައި، ފާސްކޮށް ބޭންކް ފައިލް ތައްޔާރުވަންދެން އެއްވެސް ފަރާތަކަށް ފައިސާއެއް ނުދޭ.',
        sitePlaceholder: 'ކަލެކްޝަން ޕޮއިންޓެއް ހޮއްވަވާ',
        createErrorTitle: 'ބުރު އުފެއްދިފައެއް ނުވެ',
        createSubmit: 'ޑްރާފްޓް ބުރު އުފައްދާ',
        toastCreatedTitle: 'ޑްރާފްޓް ބުރު އުފެއްދިއްޖެ',
        toastCreatedBody: 'ފާސްކުރުމުގެ ކުރިން އައިޓަމްތައް ބައްލަވާ.',
      },

      detail: {
        titleFallback: 'ފައިސާ ދޫކުރުމުގެ ބުރު',
        breadcrumbRun: 'ބުރު',
        errorTitle: 'މި ބުރު ލޯޑެއް ނުކުރެވުނު',
        stepsAriaLabel: 'ބުރުގެ ކުރިއެރުން',
        stepDraftLabel: 'ޑްރާފްޓް',
        stepDraftHint: 'އައިޓަމްތައް ހޮވިއްޖެ. އަދި ފައިސާއެއް ނުދޭ.',
        stepApprovedLabel: 'ފާސްކުރެވިފައި',
        stepApprovedHint: 'ފާސްކުރެވިއްޖެ. އަދިވެސް ފައިސާއެއް ނުދާ.',
        stepFileGeneratedLabel: 'ފައިލް ތައްޔާރުކުރެވިފައި',
        stepFileGeneratedHint: 'ބޭންކް ފައިލް ތައްޔާރުކުރެވިއްޖެ، ޖާނަލްތައް ޕޯސްޓުކުރެވިއްޖެ.',
        stepConfirmedLabel: 'ކޮންފާމްކުރެވިފައި',
        stepConfirmedHint: 'ބޭންކުގެ ނަތީޖާ ތަންފީޒުކުރެވިއްޖެ.',
        stepReconciledLabel: 'ރިކޮންސައިލްކުރެވިފައި',
        stepReconciledHint: 'ލެޖަރއާ ދާވޭތޯ ބަލައި ބަންދުކުރެވިއްޖެ.',
        approveButton: 'ފާސްކުރޭ',
        generateButton: 'ބޭންކް ފައިލް ތައްޔާރުކުރޭ',
        downloadButton: 'ފައިލް ޑައުންލޯޑްކުރޭ',
        reconcileButton: 'ރިކޮންސައިލްކުރޭ',
        tilesLabel: 'ބުރުގެ ޖުމުލަ',
        statPayees: 'ފައިސާ ލިބޭ ފަރާތްތައް',
        statPaid: 'ދެއްކިއްޖެ',
        statFailed: 'ފެއިލްވެއްޖެ',
        statUnmatched: 'ދާވާނުވި',
        unmatchedHelp:
          'ބޭންކުން ފޮނުވި ލައިންތަކުގެ ތެރެއިން، ތިމަން ފޮނުވި އެއްވެސް އެއްޗަކާ ދާވާ ނުވާ ލައިންތައް. މިއީ ދުވަހަކުވެސް ތަންފީޒުނުކުރެވޭނެ.',
        driftAlertTitle: 'އައިޓަމްތަކުގެ އަދަދު ބުރުގެ ޖުމުލައާ ނުދާވެ',
        driftAlertBody:
          'ބުރުން ރިޕޯޓުކުރަނީ {{runTotal}} laari؛ ސްކްރީނުގައިވާ އައިޓަމްތަކުގެ ޖުމުލައީ {{itemsTotal}} laari. ދެ އަދަދު ވެސް ބަދަލުނުކޮށް ދައްކާފައި.',
        runCardTitle: 'ބުރު',
        detailRail: 'ފައިސާ ދޭ ރެއިލް',
        detailSha: 'ފައިލްގެ SHA-256',
        detailLabelRun: 'ބުރު',
        detailLabelPayees: 'ފައިސާ ލިބޭ ފަރާތްތައް',
        detailLabelFailedBecause: 'ފެއިލްވި ސަބަބު',
        detailNotStated: 'ބުނެފައި ނުވެ',
        confirmApproveTitle: 'މި ފައިސާ ދޫކުރުމުގެ ބުރު ފާސްކުރަންތޯ؟',
        confirmApproveMessage:
          'ފާސްކުރުމުން ދައްކާދެނީ ފައިސާ ލިބެނީ ކޮންބައެއްކަން، ކިހާ އަދަދެއްކަން. އަދި ފައިސާއެއް ނުދާނެ — ބޭންކް ފައިލް ތައްޔާރުކުރެވެނީ ވަކި ފިޔަވަޅެއްގެ ގޮތުގައި.',
        confirmApproveLabel: 'ބުރު ފާސްކުރޭ',
        toastApproved: 'ބުރު ފާސްކުރެވިއްޖެ',
        toastApproveErrorTitle: 'ބުރު ފާސްނުކުރެވުނު',
        confirmGenerateTitle: 'ބޭންކް ޓްރާންސްފަރ ފައިލް ތައްޔާރުކުރަންތޯ؟',
        confirmGenerateMessage:
          'މިކަމުން ފައިސާ ދޫކުރުމުގެ ޖާނަލްތައް ޕޯސްޓުކުރެވޭ — ވޮލެޓްތަކުން ޑެބިޓްކުރެވި ބޭންކް ކްލިއަރިންގ އެކައުންޓަށް ކްރެޑިޓްކުރެވޭ — އަދި ބޭންކަށް ފައިލް އުފެއްދެވޭ. މިއީ ފައިސާ ކޮމިޓްވާ ވަގުތެވެ.',
        confirmGenerateLabel: 'ފައިލް ތައްޔާރުކުރޭ',
        toastGeneratedTitle: 'ފައިލް ތައްޔާރުކުރެވިއްޖެ',
        toastGeneratedBody: '{{bytes}} ބައިޓް · SHA-256 {{sha}}…',
        toastGenerateErrorTitle: 'ފައިލް ތައްޔާރެއް ނުކުރެވުނު',
        toastFileLinkErrorTitle: 'ފައިލް ލިންކް ތައްޔާރެއް ނުކުރެވުނު',
        confirmApplyTitle: 'ބޭންކުގެ ކޮންފަރމޭޝަން ޔަގީންކުރަންތޯ؟',
        confirmApplyMessage:
          'އައިޓަމްތަކުގެ ހާލަތު، ދެއްކި ލައިންތަކުގެ ވޮލެޓް ޖާނަލްތައް، އަދި ބުރުގެ ހާލަތު — ހުރިހާ ކަމެއް އެއް ޓްރާންސެކްޝަނުގައި އަޕްޑޭޓްކުރެވޭ. ތިމަން ފޮނުވި އަދަދު ނުވަތަ އެކައުންޓާ ތަފާތު ލައިނެއް، އެއީ އެކްސެޕްޝަނެއްގެ ގޮތުގައި ރިޕޯޓުކުރެވި، ތަންފީޒުނުކުރެވޭނެ.',
        confirmApplyLabel: 'ކޮންފަރމޭޝަން ޔަގީންކުރޭ',
        detailLabelFile: 'ފައިލް',
        detailFileFallback: 'އަޕްލޯޑްކުރި ފައިލް',
        toastAppliedTitle: 'ކޮންފަރމޭޝަން ޔަގީންކުރެވިއްޖެ',
        toastAppliedBody: '{{paid}} ދެއްކިއްޖެ, {{failed}} ފެއިލްވެއްޖެ, {{unmatched}} Unmatched',
        toastApplyErrorTitle: 'ކޮންފަރމޭޝަން ޔަގީނެއް ނުކުރެވުނު',
        confirmReconcileTitle: 'މި ބުރު ރިކޮންސައިލްކުރަންތޯ؟',
        confirmReconcileMessage:
          'ރިކޮންސައިލްކުރުމުން ބުރު ލެޖަރއާ ދާވޭތޯ ބަލައި ބަންދުކުރެވޭ. ބޭންކް ސްޓޭޓްމަންޓް މިތާ ރެކޯޑްކޮށްފައިވާ ގޮތާ ދާވުމުން މިކަން ކުރައްވާ.',
        confirmReconcileLabel: 'ބުރު ރިކޮންސައިލްކުރޭ',
        toastReconciled: 'ބުރު ރިކޮންސައިލްކުރެވިއްޖެ',
        toastReconcileErrorTitle: 'ބުރު ރިކޮންސައިލްނުކުރެވުނު',
        confirmRetryTitle: 'މި ފައިސާ އަލުން ދޭން ޓްރައިކުރަންތޯ؟',
        confirmRetryMessage:
          'މި އައިޓަމް ބުރަށް އަނބުރާ ލެއިވެނީ، ދެން ފައިލްއަކަށް. އެއްވެސް އެއްޗެއް ދެފަހަރު ދެއްކިގެން ނުދާނެ.',
        confirmRetryLabel: 'އައިޓަމް އަލުން ޓްރައިކުރޭ',
        detailLabelPayee: 'ފައިސާ ލިބޭ ފަރާތް',
        toastRetried: 'އައިޓަމް އަލުން ޓްރައިކުރަން ކިޔޫކުރެވިއްޖެ',
        toastRetryErrorTitle: 'އައިޓަމް އަލުން ޓްރައިނުކުރެވުނު',
        uploadCardTitle: 'ބޭންކުގެ ކޮންފަރމޭޝަން އަޕްލޯޑްކުރޭ',
        uploadCardSubtitle: 'ބޭންކުން ފޮނުވި ރޯ ފައިލް',
        uploadNote:
          'ފައިލް ފޮނުވެނީ ބަދަލެއް ނުގެނެސް، ބައިޓަކުން ބައިޓަކަށެވެ — އަލުން ޕެކޭޖްކުރުމުން ފައިލް ހަލާކުވުމަށް މަގުފަހިވެއެވެ. ތިމަން ފޮނުވި އަދަދު ނުވަތަ އެކައުންޓާ ތަފާތު ލައިނެއް، އެކްސެޕްޝަނެއްގެ ގޮތުގައި އަނބުރާ ލައި ތަންފީޒުނުކުރެވޭނެ: ބޭންކުން ތަފާތު އަދަދެއް ދިޔައިއްޔާ، އެއީ ވާހަކަދައްކަންޖެހޭ ކަމެއް، ހާލަތު އަޕްޑޭޓެއް ނޫނެވެ.',
        chooseFileButton: 'ކޮންފަރމޭޝަން ފައިލް ހޮއްވަވާ',
        applyConfirmationButton: 'ކޮންފަރމޭޝަން ޔަގީންކުރޭ',
        confirmationCardTitle: 'ކޮންފަރމޭޝަނުން ކުރި ކަންތައް',
        confirmationSubtitle: '{{paid}} ދެއްކިއްޖެ · {{failed}} ފެއިލްވެއްޖެ · {{unmatched}} Unmatched',
        totalAppliedLabel: 'ޔަގީންކުރި ޖުމުލަ',
        exceptionsLabel: 'އެކްސެޕްޝަންތައް — ތަންފީޒުނުކުރެވުނު',
        itemsCardTitle: 'އައިޓަމްތައް',
        itemsEmptyTitle: 'މި ބުރުގައި އައިޓަމެއް ނެތް',
        itemsEmptyDescription: 'ފައިނަލްކުރި ބިލްތަކުން، މި މުއްދަތުގައި ޕޮޒިޓިވް ވޮލެޓް ބެލެންސެއް ހުރި މަސްވެރިޔެއް ނެތް.',
        itemsTableLabel: 'ފައިސާ ދޫކުރުމުގެ އައިޓަމްތައް',
        colPayee: 'ފައިސާ ލިބޭ ފަރާތް',
        colBank: 'ބޭންކް',
        colBankReference: 'ބޭންކް ރެފަރެންސް',
        colFailure: 'ފެއިލްވުން',
        reasonBadge: 'ސަބަބު',
        retryButton: 'އަލުން ޓްރައިކުރޭ',
        separation:
          'ފައިލް ތައްޔާރުކުރުމަށް {{runPerm}} ބޭނުންވޭ؛ ކޮންފާމްކުރުމަށް {{confirmPerm}} ބޭނުންވޭ. މި ދެކަން ވަކިކޮށްފައި ވަނީ، ފައިސާ ލިބޭ ފަރާތް ހޮވުމާ، ބޭންކުން ފައިސާ ދިންކަން ބުނުން، މި ދެކަން އެއް ފަރާތަކުން ނުކުރެވޭ ގޮތަށެވެ.',
      },
    },
  },

  collection: {
    sessions: {
      list: {
        title: 'ކަލެކްޝަން ސެޝަންތައް',
        subtitle: 'ކޮންމެ ދޯންޏެއްގެ ޒިޔާރަތެއް، ޑްރާފްޓުން ފެށިގެން ބިލްކުރެވެންދެން',
        manualEntry: 'މެނުއަލް އެންޓްރީ',
        statusAny: 'ކޮންމެވެސް ހާލަތެއް',
        statusDraft: 'ޑްރާފްޓް',
        statusOpen: 'ހުޅުވިފައި',
        statusFinalized: 'ފައިނަލްކޮށްފައި',
        statusBilled: 'ބިލްކޮށްފައި',
        searchPlaceholder: 'މި ސަފުހާ ދޯނީގެ ނަމުން ފިލްޓަރކުރޭ',
        siteShort: 'ސައިޓް',
        chipVesselContains: 'ދޯނީގައި ހިމެނޭ',
        errorTitle: 'ސެޝަންތައް ލޯޑެއް ނުކުރެވުނު',
        tableLabel: 'ކަލެކްޝަން ސެޝަންތައް',
        colSource: 'މަސްދަރު',
        colStarted: 'ފެށި ވަގުތު',
        colFlags: 'ފާހަގަތައް',
        flagChainBreak: 'ޗެއިން މެދުކެނޑުން',
        flagTotalsDiffer: 'ޖުމުލަތައް ތަފާތު',
        emptyTitle: 'މި ފިލްޓަރތަކާ ދިމާވާ ސެޝަނެއް ނެތް',
        emptyDescription: 'ފިލްޓަރެއް ސާފުކުރައްވާ، ނުވަތަ އެހެން ކަލެކްޝަން ޕޮއިންޓަކަށް ބަދަލުކުރައްވާ.',
      },
      detail: {
        loadErrorTitle: 'މި ސެޝަން ލޯޑެއް ނުކުރެވުނު',
        breadcrumbList: 'ކަލެކްޝަން ސެޝަންތައް',
        titleWithRef: 'ސެޝަން {{ref}}',
        openBill: 'ބިލް ހުޅުވާ',
        finalize: 'ފައިނަލްކުރޭ',
        chainBreakTitle: 'އިންޓެގްރިޓީ ޗެއިން މެދުކެނޑުން',
        chainBreakBody:
          'މި ސެޝަންގެ {{field}} ޑިވައިސުން ފޮނުވި ކުރީގެ ސެޝަނާ ގުޅިފައެއް ނުވޭ. މާނައަކީ ސެޝަނެއް ގެއްލުނީ، ތަރުތީބު ބަދަލުވީ، ނުވަތަ އަލުން ފޮނުވުނީއެވެ. މި ޗެއިން ބަލަނީ ހެއްކެއްގެ ގޮތުގައި، ފުރިހަމައަށް ޔަގީންކުރެވިފައެއް ނޫން — ޑިވައިސްގެ HMAC ކީ ޑިވައިސްއިން ބޭރެއް ނުވޭ — ވީމާ މިއީ ދިރާސާކުރަން ޖެހޭ ހެއްކެއް، މަކަރު ހެދިކަން ސާބިތުވާ ކަމެއް ނޫން.',
        totalsDiscrepancyTitle: 'ޑިވައިސްގެ ޖުމުލަ އެންޓްރީތަކާ ތަފާތު',
        totalsDiscrepancyBody:
          'ޑިވައިސް ރިޕޯޓްކުރި ޖުމުލައަކީ ފޮނުވި އެންޓްރީތަކުގެ ޖުމުލައެއް ނޫން. ދެ ޖުމުލަވެސް ތިރީގައި ބަދަލެއް ނުގެނެސް ދައްކާފައި ވަނީ.',
        manualSessionTitle: 'މެނުއަލްކޮށް ނެގުނު ސެޝަން',
        manualSessionBody:
          'މި ސެޝަން ނެގިފައިވަނީ ސްކޭލަކުން ނުވެ، އަތުން ޖައްސައިގެންނެވެ. މިއީ މެނުއަލް އެންޓްރީ އިސްތިސްނާއެއްގެ ގޮތުގައި ރިޕޯޓްކުރެވެއެވެ.',
        totalsCardTitle: 'ޖުމުލަތައް',
        totalsCardSubtitle: 'ރިޕޯޓްކުރި ގޮތާއި، އެންޓްރީތަކުން ޖުމުލަކުރި ގޮތް',
        reportedNetWeight: 'ރިޕޯޓްކުރި ސާފު ބަރުދަން',
        summedFromEntries: 'އެންޓްރީތަކުން ޖުމުލަކުރި',
        reportedValue: 'ރިޕޯޓްކުރި އަގު',
        pieces: 'ޢަދަދު',
        voidedCount: '({{count}} ބާތިލްކުރެވިފައި)',
        integrityCardTitle: 'އިންޓެގްރިޓީ',
        integrityCardSubtitle: 'ޑިވައިސްއިން ސާވަރަށް ހެކިވެފައިވާ މަޢުލޫމާތު',
        entriesEmptyTitle: 'މި ސެޝަންގައި އެންޓްރީއެއް ނެތް',
        entriesEmptyDescription: 'މި ދޯނީގެ ޒިޔާރަތާ ދެކޮޅަށް އަދި މަހެއް ބަރުދަން ބަލާފައެއް ނުވޭ.',
        entriesTableLabel: 'މި ސެޝަންގެ ބަރުދަން ބެލި އެންޓްރީތައް',
        colGrade: 'ގްރޭޑް',
        colTare: 'ޓެއަރ',
        colCapture: 'ނެގުނު ގޮތް',
        detailOperator: 'އޮޕަރޭޓަރ',
        detailStarted: 'ފެށި ވަގުތު',
        detailFinalized: 'ފައިނަލްކުރި ވަގުތު',
        detailSource: 'އެންޓްރީ މަސްދަރު',
        detailIce: 'އައިސް ލައިގެން',
        detailTemp: 'މަހުގެ ފިނިހޫނުމިން',
        detailTempValue: '{{value}} °C',
        detailPortalRef: 'ޕޯޓަލް ރެފަރެންސް',
        detailReportedSite: 'ޑިވައިސް ރިޕޯޓްކުރި ސައިޓް',
        integrityChainHead: 'ScalApp ޗެއިން ހެޑް',
        integrityPrevHash: 'ކުރީގެ ސެޝަން ހޭޝް',
        integrityLocalSeq: 'ޑިވައިސް ސީކުއަންސް',
        qualityManual: 'މެނުއަލް',
        qualityUnstable: 'އަންސްޓޭބަލް',
        qualityVoided: 'ބާތިލް',
        qualityLocked: 'ލޮކްކޮށްފައި',
        voidAction: 'ބާތިލްކުރޭ',
        voidReasonFallback: 'ބާތިލްކޮށްފައި',
        finalizeConfirmTitle: 'މި ސެޝަން ފައިނަލްކުރައްވަނީތޯ؟',
        finalizeConfirmMessage:
          'ފައިނަލްކުރުމުން ކޮންމެ އެންޓްރީއެއް ލޮކްވެ، ޑްރާފްޓް ފިޝް ބިލެއް އުފެދޭނެއެވެ. ފައިނަލްކުރުމަށްފަހު އެންޓްރީ އެޑިޓެއް ނުވަތަ ބާތިލެއް ނުކުރެވޭނެއެވެ — އިސްލާހެއް ބޭނުންވެއްޖެނަމަ އެއީ ރިވަރސަލް ޑޮކިއުމަންޓަކަށް ވާނެއެވެ.',
        finalizeConfirmLabel: 'ސެޝަން ފައިނަލްކުރޭ',
        finalizeSuccessTitle: 'ސެޝަން ފައިނަލްކުރެވިއްޖެ',
        finalizeSuccessBody: 'ބިލިންގގައި ޑްރާފްޓް ފިޝް ބިލެއް އިންތިޒާރުކުރަނީ.',
        finalizeErrorTitle: 'ފައިނަލްނުކުރެވުނު',
        voidCardTitle: 'އެންޓްރީ ބާތިލްކުރުން',
        voidCardSubtitle: 'އެންޓްރީ #{{seq}}',
        voidNote:
          'ބާތިލްކުރުމުން އެންޓްރީ ސަބަބާއެކު އަދި ތިޔަބޭފުޅާގެ ނަމާއެކު ތާލީގައި ބާއްވާނެއެވެ. ބިލްކުރުމުން އިސްތިސްނާވެ، އޮޑިޓަށްޓަކައި ރައްކާކުރެވޭނެއެވެ.',
        voidReasonLabel: 'ސަބަބު',
        voidReasonHint: 'ލާޒިމް. މިއީ އޮޑިޓް ޓްރެއިލްގައި ލިޔެވޭނެ.',
        voidSubmit: 'އެންޓްރީ ބާތިލްކުރޭ',
        voidSuccessTitle: 'އެންޓްރީ ބާތިލްކުރެވިއްޖެ',
        voidSuccessBody: 'އެންޓްރީ ތާލީގައި ފާހަގަކޮށް، ނަން ޖަހައިގެން ބާއްވާނެ.',
        voidErrorTitle: 'އެންޓްރީ ބާތިލްނުކުރެވުނު',
      },
    },

    manualEntry: {
      title: 'މެނުއަލް އެންޓްރީ',
      subtitle: 'ސްކޭލް ޑިވައިސްއަކުން ބޭރުން، އަތުން ނެގުނު ބަރުދަންތައް',
      startAnother: 'އިތުރު ސެޝަނެއް ފަށާ',
      openSession: 'ސެޝަން ހުޅުވާ',
      warningTitle: 'މި ސަފުހާގައި ހުރި ކޮންމެ އެންޓްރީއެއް ފާހަގަކުރެވިފައި',
      warningBody:
        'މެނުއަލް އެންޓްރީތައް ބިލަށާއި ރިޕޯޓްތަކަށް {{code}} ފެންނަ ގޮތަށް ގެންދެއެވެ، މެނޭޖްމަންޓް ޕެކްގައި އިސްތިސްނާގެ ގޮތުގައި ލިސްޓުކުރެވެއެވެ، އަދި ތިޔަބޭފުޅާގެ އެކައުންޓާ ދެކޮޅަށް އޮޑިޓް ޓްރެއިލްގައި ލިޔެވެއެވެ. ސްކޭލެއް މަސައްކަތްކުރާ ކޮންމެ ތާކު ސްކޭލެއް ބޭނުންކުރައްވާ.',
      loadErrorTitle: 'އެ ސެޝަން ލޯޑެއް ނުކުރެވުނު',
      openSessionCardTitle: 'ސެޝަނެއް ހުޅުވާ',
      openSessionCardSubtitle: 'މި ތާލީ ނިސްބަތްވާ ދޯނި ހޮއްވަވާ',
      createErrorTitle: 'ސެޝަން ހުޅުވައެއް ނުލެވުނު',
      sitePlaceholder: 'ކަލެކްޝަން ޕޮއިންޓެއް ހޮއްވަވާ',
      vesselEmptyText: 'ދިމާވާ ދޯންޏެއް ނެތް',
      operatorRefLabel: 'އޮޕަރޭޓަރ ރެފަރެންސް',
      operatorRefHint: 'ބަރުދަން ބަލަނީ ކާކުކަން. ސެޝަންގައި ރެކޯޑުކުރެވޭނެ.',
      withIceLabel: 'އައިސް ލައިގެން ބާލާފައި',
      sessionOpenToastTitle: 'ސެޝަން ހުޅުވިއްޖެ',
      sessionOpenToastBody: 'ތިޔަބޭފުޅާ އިތުރުކުރާ ކޮންމެ އެންޓްރީއެއް މެނުއަލްގެ ގޮތުގައި ފާހަގަކުރެވޭނެ.',
      entryCardTitle: 'އެންޓްރީއެއް އިތުރުކުރޭ — {{vessel}}',
      addErrorTitle: 'އެންޓްރީ އިތުރެއް ނުކުރެވުނު',
      gradeDefaultOption: 'ޑިފޯލްޓް ގްރޭޑް',
      piecesLabel: 'ޢަދަދު',
      grossWeightLabel: 'ގްރޮސް ބަރުދަން',
      tareWeightLabel: 'ޓެއަރ ބަރުދަން',
      tareWeightHint: 'ފޮށި ނުވަތަ ގޮށި. ނެތްނަމަ ހުސްކޮށް ބާއްވާ.',
      remarksLabel: 'ރިމާކްސް',
      addEntryButton: 'އެންޓްރީ އިތުރުކުރޭ',
      entriesCardTitle: 'މިހާތަނަށް ހުރި އެންޓްރީތައް',
      entriesTableLabel: 'މި މެނުއަލް ސެޝަންގައި ނެގުނު އެންޓްރީތައް',
      colSeq: '#',
      entriesEmptyTitle: 'އަދި އެއްޗެއް ނުނަގާ',
      entriesEmptyDescription: 'މަތީގައި ފުރަތަމަ ބަރުދަން އިތުރުކުރައްވާ.',
      finalizeButton: 'ސެޝަން ފައިނަލްކުރޭ',
      finalizeConfirmTitle: 'މި މެނުއަލް ސެޝަން ފައިނަލްކުރައްވަނީތޯ؟',
      finalizeConfirmMessage:
        'ފައިނަލްކުރުމުން ކޮންމެ އެންޓްރީއެއް ލޮކްވެ، ޑްރާފްޓް ފިޝް ބިލެއް އުފެދޭނެއެވެ. މި އެންޓްރީތައް އަތުން ނަގާފައިވާތީ، ސެޝަން ރިޕޯޓްކުރެވެނީ މެނުއަލް އެންޓްރީ އިސްތިސްނާއެއްގެ ގޮތުގައެވެ.',
      finalizeConfirmLabel: 'ސެޝަން ފައިނަލްކުރޭ',
      finalizeSuccessTitle: 'ސެޝަން ފައިނަލްކުރެވިއްޖެ',
      finalizeErrorTitle: 'ފައިނަލްނުކުރެވުނު',
    },

    queue: {
      title: 'ސަފު ބޯޑް',
      sitePlaceholder: 'ކަލެކްޝަން ޕޮއިންޓެއް ހޮއްވަވާ',
      closeQueue: 'ސަފު ބަންދުކުރޭ',
      live: 'ލައިވް',
      notLive: 'އަޕްޑޭޓެއް ނުލިބޭ',
      chooseSiteTitle: 'ކަލެކްޝަން ޕޮއިންޓެއް ހޮއްވަވާ',
      chooseSiteDescription: 'ސަފަކީ ކޮންމެ ސައިޓަކަށް ކޮންމެ ދުވަހަކަށް ވަކިވަކިން ހުންނަ އެއްޗެއް.',
      errorTitle: 'ސަފު ބޯޑް ލޯޑެއް ނުކުރެވުނު',
      streamDownTitle: 'ލައިވް ފީޑް ކަނެކްޓެއް ނުވެ',
      streamDownBody:
        'ތިރީގައި ހުރި ބޯޑަކީ ސްނެޕްޝޮޓެއް، އަމިއްލައަކަށް އައުކުރެވެއެއް ނުވާނެ. ދޯންޏެއް ގޮވުމުގެ ކުރިން Refresh އަށް ފިއްތަވާ.',
      statsAriaLabel: 'ސަފުގެ ތަފާސްހިސާބު',
      statWaiting: 'މަޑުކޮށްގެން',
      statServing: 'ޚިދުމަތްދެނީ',
      statAverageWait: 'އެވްރެޖް މަޑުކުރި ވަގުތު',
      statP90Wait: '90 ވަނަ ޕަރސެންޓައިލް މަޑުކުރި ވަގުތު',
      statServedToday: 'މިއަދު {{count}} ޚިދުމަތްދީފި',
      p90Help:
        'ދިހަ ދޯނިން ނުވަ ދޯނި މިއަށްވުރެ މަދު ވަގުތުކޮޅު މަޑުކުރި. އެވްރެޖުން ގޯސް ހަވީރެއް ފޮރުވިދާނެ؛ މިއިން ނުފޮރުވޭނެ.',
      issueCardTitle: 'ޓޯކަނެއް ދޫކުރޭ',
      issueCardSubtitle: 'ކައުންޓަރަށް އަންނަ ދޯންޏަކަށް',
      issueVesselHint: 'އިޚްތިޔާރީ — ދޯންޏަކާނުލާވެސް ޓޯކަނެއް ދޫކުރެވިދާނެ.',
      issueButton: 'ޓޯކަން ދޫކުރޭ',
      loadingLabel: 'ސަފު ލޯޑްވަނީ',
      servingColumn: 'ޚިދުމަތްދެނީ ({{count}})',
      calledColumn: 'ގޮވާލެވިފައި ({{count}})',
      waitingColumn: 'މަޑުކޮށްގެން ({{count}})',
      closedColumn: 'މިއަދު ބަންދުކުރި ({{count}})',
      sinceTime: '{{time}} އިން ފެށިގެން',
      calledTime: '{{time}} ގައި ގޮވާލި',
      doneButton: 'ނިމިއްޖެ',
      startButton: 'ފަށާ',
      noShowButton: 'ވަޑައިނުގަތް',
      callButton: 'ގޮވާލާ',
      nobodyServing: 'ޚިދުމަތްދޭ މީހަކު ނެތް',
      nobodyCalled: 'ގޮވާލެވުނު މީހަކު ނެތް',
      queueEmpty: 'ސަފު ހުސްވެފައި',
      nothingClosed: 'އަދި ބަންދުކުރެވުނު އެއްޗެއް ނެތް',
      noShowConfirmTitle: 'ޓޯކަން {{number}} ވަޑައިނުގަތް ކަމަށް ފާހަގަކުރައްވަނީތޯ؟',
      noShowConfirmMessage:
        'ދޯނި ސަފުގައި ހުރި ތަން ގެއްލި، އައު ޓޯކަނެއް ނަގަން ޖެހޭނެއެވެ. މިކަން ފަހަތަށް ނުޖެއްސޭނެ.',
      noShowConfirmLabel: 'ވަޑައިނުގަތްކަމަށް ފާހަގަކުރޭ',
      tokenErrorTitle: 'ޓޯކަން {{number}} ބަދަލެއް ނުވި',
      issueSuccessTitle: 'ޓޯކަން {{number}} ދޫކުރެވިއްޖެ',
      issueErrorTitle: 'ޓޯކަނެއް ދޫކުރެއް ނުކުރެވުނު',
      closeConfirmTitle: 'މިއަދުގެ ސަފު ބަންދުކުރައްވަނީތޯ؟',
      closeConfirmMessage:
        'މަޑުކޮށްގެން، ގޮވާލެވިފައި ނުވަތަ ޚިދުމަތްދެމުންދާ ކޮންމެ ޓޯކަނެއް ވަޑައިނުގަތް ކަމަށް ފާހަގަކުރެވި، މިއަދުގެ ސަފު ބަންދުވާނެއެވެ. މިއީ ދުވަހުގެ ނިޔަލުގައި ބޭނުންކުރައްވާ ފީޗަރއެއް.',
      closeConfirmLabel: 'ސަފު ބަންދުކުރޭ',
      closeDetailStillOpen: 'އަދިވެސް ހުޅުވިފައި',
      closeSuccessTitle: 'މިއަދުގެ ސަފު ބަންދުކުރެވިއްޖެ',
      closeErrorTitle: 'ސަފު ބަންދެއް ނުކުރެވުނު',
    },
  },

  devices: {
    registry: {
      title: 'ޑިވައިސް ރަޖިސްޓަރީ',
      subtitle: 'ކޮންމެ ScalApp ޑިވައިސްއަކާއި، އެ ޑިވައިސްގެ ޓޯކަން ބަނދެފައިވާ ކަލެކްޝަން ޕޮއިންޓް',
      fleetHealth: 'ޑިވައިސްތަކުގެ ހާލަތު',
      issueEnrolmentCode: 'އެންރޯލްމަންޓް ކޯޑެއް ދޫކުރޭ',
      errorTitle: 'ޑިވައިސް ރަޖިސްޓަރީ ލޯޑެއް ނުކުރެވުނު',
      emptyTitle: 'މި ކަލެކްޝަން ޕޮއިންޓުގައި ޑިވައިސްއެއް ނެތް',
      emptyDescription: 'އެންރޯލްމަންޓް ކޯޑެއް ދޫކުރައްވާފައި، ޑިވައިސްގައި ޖައްސަވާ.',
      actionOpen: 'ހުޅުވާ',
      revokeConfirmMessage:
        'ޑިވައިސްގެ ޓޯކަން ވަގުތުން މަސައްކަތްކުރުން ހުއްޓޭނެ. ޑިވައިސްއިން އޮފްލައިންކޮށް ނަގާފައިވާ ހުރިހާ އެއްޗެއް ބާއްވާނެ، އެކަމަކު އަލުން އެންރޯލްކުރަންދެން ސިންކްކުރެއް ނުކުރެވޭނެ.',
      revokeErrorTitle: 'ޑިވައިސް ބާތިލްކުރެއް ނުކުރެވުނު',
      enrolModalTitle: 'އެންރޯލްމަންޓް ކޯޑެއް ދޫކުރޭ',
      enrolErrorTitle: 'ކޯޑު ދޫކުރެއް ނުކުރެވުނު',
      codeLabel: 'އެންރޯލްމަންޓް ކޯޑް',
      codeInstruction:
        'ޑިވައިސް ބައިންޑްކުރުމަށް މި ކޯޑު ޑިވައިސްގައި ޖައްސަވާ. މުއްދަތު ހަމަވާނެ، އަދި އެންމެ އެއްފަހަރު ބޭނުންކުރެވޭނެ.',
      issuedExpires: 'މުއްދަތު ހަމަވަނީ',
      formSiteHint: 'ޑިވައިސްގެ ޓޯކަން ބަނދެވޭނީ މި ސައިޓަށެވެ، އަދި އެހެން ސައިޓަކަށް ދުވަހަކުވެސް ނުލިޔެވޭނެ.',
      formNameHint: 'ޓެބްލެޓްގައި ލިޔެފައި ހުންނަ ނަން — “Kooddoo jetty 1”.',
      done: 'ނިމިއްޖެ',
      issueCode: 'ކޯޑު ދޫކުރޭ',
    },

    detail: {
      titleFallback: 'ޑިވައިސް',
      breadcrumbList: 'ޑިވައިސް ރަޖިސްޓަރީ',
      rename: 'ނަން ބަދަލުކުރޭ',
      loadErrorTitle: 'މި ޑިވައިސް ލޯޑެއް ނުކުރެވުނު',
      renameCardTitle: 'ޑިވައިސްގެ ނަން ބަދަލުކުރޭ',
      detailAppVersion: 'އެޕް ވަރޝަން',
      detailScaleVersion: 'ސްކޭލް ވަރޝަން',
      detailEnrolled: 'އެންރޯލްކުރި ތާރީޚް',
      notEnrolled: 'އެންރޯލްކޮށްފައި ނުވޭ',
      detailRevoked: 'ބާތިލްކުރި ތާރީޚް',
      aliasCardTitle: 'ރެފަރެންސް އޭލިއަސްތައް',
      aliasCardSubtitle: 'މި ޑިވައިސްގެ ލޯކަލް ކެޓަލޮގް id ތަކުގެ މާނަ',
      aliasNote:
        'ކެޓަލޮގް ސިންކުވުމުގެ ކުރިން ނެގުނު ސެޝަނެއްގައި ޑިވައިސްގެ އަމިއްލަ id ތައް ހުންނާނެ. Ingest އެއިން އެއްޗެއް ދުވަހަކުވެސް ހީކޮށްލައިގެން ކުރިއަށް ނުދާނެ — {{code}} ފީލްޑް ބުނެ ފެއިލްވާނެ، އަދި މި މެޕިންގ ހަދަނީ ގަސްދުގައި، މިތާ.',
      aliasErrorTitle: 'އޭލިއަސް ރައްކާނުކުރެވުނު',
      aliasKindLabel: 'ބާވަތް',
      aliasRefLabel: 'ޑިވައިސް ރެފަރެންސް',
      aliasRefHint: 'ޑިވައިސް ފޮނުވާ ގޮތަށް ފައްސަވާ — vs-1, ft-3.',
      aliasTargetLabel: 'ދޭހަކުރެވެނީ',
      saveAlias: 'އޭލިއަސް ރައްކާކުރޭ',
      aliasColKind: 'ބާވަތް',
      aliasColRef: 'ޑިވައިސް ރެފަރެންސް',
      aliasColTarget: 'ދޭހަކުރެވެނީ',
      aliasSavedTitle: 'އޭލިއަސް ރައްކާކުރެވިއްޖެ',
      aliasSavedBody: 'އެ ރެފަރެންސް ހިމެނޭ ސެޝަންތައް މިހާރު ދޭހަކުރެވޭނެ.',
      aliasEmptyTitle: 'އޭލިއަސްއެއް ނެތް',
      aliasEmptyDescription:
        'މި ޑިވައިސް ފޮނުވަނީ ސާވަރ id ތައް، ނުވަތަ އަދި ދޭހަނުކުރެވޭ ރެފަރެންސެއް ފޮނުވާފައެއް ނުވޭ.',
      revokeConfirmMessage:
        'ޓޯކަން ވަގުތުން މަސައްކަތްކުރުން ހުއްޓޭނެ، އަދި އަލުން އެންރޯލްކުރަންދެން ސިންކްކުރެއް ނުކުރެވޭނެ.',
      revokeErrorTitle: 'ބާތިލްކުރެއް ނުކުރެވުނު',
      renameSuccessTitle: 'ޑިވައިސްގެ ނަން ބަދަލުކުރެވިއްޖެ',
      renameErrorTitle: 'ނަން ބަދަލެއް ނުކުރެވުނު',
    },

    health: {
      title: 'ޑިވައިސްތަކުގެ ހާލަތު',
      subtitle: 'ހާޓްބީޓް، ސިންކްކުރަން ހުރި އެންޓްރީ، އަދި ކަނެކްޝަން ކުއަލިޓީ',
      deviceRegistry: 'ޑިވައިސް ރަޖިސްޓަރީ',
      metaText: 'ކޮންމެ މިނެޓަކުން އައުވޭ · ފަހުން ބެލީ {{when}}',
      errorTitle: 'ޑިވައިސްތަކުގެ ހާލަތު ލޯޑެއް ނުކުރެވުނު',
      statDevices: 'ޑިވައިސް',
      statOffline: 'އޮފްލައިން',
      statPending: 'ސިންކްކުރަން ހުރި އެންޓްރީ',
      statPendingHelp:
        'ޑިވައިސްއެއްގައި ނެގުނު ބަރުދަންތައް، ސާވަރަށް އަދި ނުފޯރާ. ޑިވައިސްގައި ރައްކާތެރި، އެކަމަކު އަދި ބިލްކުރެއް ނުކުރެވޭނެ.',
      statLowBattery: 'ބެޓަރި މަދު',
      tilesAriaLabel: 'ފްލީޓް ޚުލާސާ',
      loadingLabel: 'ޑިވައިސްތަކުގެ ހާލަތު ލޯޑްވަނީ',
      emptyTitle: 'ރިޕޯޓްކުރާ ޑިވައިސްއެއް ނެތް',
      emptyDescription: 'ރަޖިސްޓަރީން ޑިވައިސްއެއް އެންރޯލްކުރައްވާ، ނުވަތަ ކަލެކްޝަން ޕޮއިންޓް ފިލްޓަރ ފުޅާކުރައްވާ.',
      online: 'އޮންލައިން',
      offline: 'އޮފްލައިން',
      lastHeartbeat: 'ފަހު ހާޓްބީޓް {{when}}',
      batteryLabel: 'ބެޓަރި',
      factPending: 'ފޮނުވަން ހުރި',
      factLink: 'ކަނެކްޝަން',
    },

    csvImport: {
      title: 'CSV އިމްޕޯޓް',
      subtitle: 'ScalApp ގެ tallies.csv ސާވަރގެ ޕާރސަރާ ދިމާވޭތޯ ބައްލަވާ',
      clear: 'ސާފުކުރޭ',
      infoTitle: 'މި ސްކްރީނުން ފައިލް ބަލައި، ފޮނުވައެއް ނުކުރޭ',
      infoBody:
        'CSV އިމްޕޯޓަކީ ކަލެކްޓަރ ސާރފޭސް އޮޕަރޭޝަނެއް ({{endpoint}})، ޑިވައިސް ޓޯކަނުން އޮތެންޓިކޭޓްކުރެވޭ، ވީމާ ސްޓާފް ސެޝަނަކުން ފޮނުވެއެއް ނުކުރެވޭނެ. ތިރީގައި ހުރި ހުރިހާ އެއްޗެއް ހިސާބުކުރެވިފައިވަނީ މިތާ، ސާވަރު ބޭނުންކުރާ ހަމަ އެ ހެޑަރާއި ފައިސާ އަދި ބަރުދަން ބަދަލުކުރުންތަކުން، ވީމާ މި ބެލުމުން ފާސްވާ ފައިލެއް ސާވަރުވެސް ބަލައިގަންނާނެ.',
      chooseFileCardTitle: 'ފައިލެއް ހޮއްވަވާ',
      dropText: '{{filename}} މިތަނަށް ދޫކުރައްވާ، ނުވަތަ',
      chooseFile: 'ފައިލެއް ހޮއްވަވާ',
      formatSummary: 'ބަލައިގަންނަ ކޮލަމްތައް، ތަރުތީބުން',
      formatNote:
        'މި ފައިލްގައި ފައިސާ ހުންނަނީ ޑެސިމަލް MVR ސްޓްރިންގެ ގޮތުގައި — JSON ގޮތާ ދެކޮޅަށް، އެއީ އިންޓިޖަރ ލާރި ގެންދާ ގޮތް. މިތާ ބަދަލުކުރެވެނީ ޑިޖިޓްތަކުގެ ސީދާ އިންޓިޖަރ ހިސާބުކުރުމުން؛ ތިންވަނަ ޑެސިމަލް ބުރިކޮށްލެވެނީ، ބައުންޑްކުރެވުމަކާނުލާ.',
      refusedTitle: 'ފައިލް ބަލައިނުގަނެވުނު',
      unreadableFile: 'ފައިލް ޓެކްސްޓެއްގެ ގޮތުގައި ކިޔައެއް ނުލެވުނު. މިއީ CSV އެއްތޯ؟',
      tilesAriaLabel: 'ފައިލްގައި ހުރި އެއްޗެއް',
      statSessions: 'ސެޝަން',
      statEntryRows: 'އެންޓްރީ ރޯވް',
      statManual: 'މެނުއަލް ބަރުދަންތައް',
      statFindings: 'ފާހަގަހުރި ސެޝަން',
      problemsTitle: 'ކިޔައެއް ނުލެވުނު ރޯވްތައް',
      sessionsCardTitle: 'މި ފައިލްގައި ހުރި ސެޝަންތައް',
      tableLabel: 'ޓެލީ ފައިލުން ޕާރސްކުރެވުނު ސެޝަންތައް',
      colBillNo: 'ބިލް ނަންބަރު',
      colReportedSite: 'ރިޕޯޓްކުރި ސައިޓް',
      colFinalized: 'ފައިނަލްކުރި ތާރީޚް',
      colFindings: 'ފާހަގަތައް',
      emptyTitle: 'މި ފައިލްގައި ސެޝަނެއް ނެތް',
      manualBadge: '{{count}} މެނުއަލް',
      findingBadgeOne: '{{count}} ފާހަގަ',
      findingBadgeMany: '{{count}} ފާހަގަ',
      findingsCardTitle: 'ފާހަގަތައް',
      findingsCardSubtitle: 'ސާވަރ ބަލައިނުގަންނާނެ ކަންތައްތައް',
      findingHead: '{{ref}} · {{vessel}}',
      noFileTitle: 'ފައިލެއް ލޯޑްކޮށްފައެއް ނުވޭ',
      noFileDescription: 'ScalApp ޑިވައިސްއަކުން ނެގުނު tallies.csv އެއް ހޮއްވަވާ.',
    },

    conflicts: {
      title: 'ޑޭޓާ ފުށުއެރުންތައް',
      subtitle: 'ދެ ފަހަރު ތަފާތު ބައިތަކާއެކު ފޮނުވުނު ސެޝަންތައް',
      scopeLabel: 'ސްކޯޕް',
      scopeOpen: 'ހުޅުވިފައި ހުރި އެކަނި',
      openBannerTitleOne: '{{count}} ފުށުއެރުމެއް ބަލައި ނިމުމަށް ބާކީ',
      openBannerTitleMany: '{{count}} ފުށުއެރުން ބަލައި ނިމުމަށް ބާކީ',
      openBannerBody:
        'ރައްކާކުރެވިފައިވާ ސެޝަނަކީ ފުރަތަމަ ލިބުނު އެއްޗެވެ. މިތާ ފައިނޭންޝަލް ރެކޯޑެއް ބަދަލުވެފައެއް ނުވޭ؛ ދެވަނަ ފޮނުވުން ކަރަންޓީނުކުރެވި، އޮޑިޓް އެލާމެއް ފޮނުވިއްޖެ.',
      errorTitle: 'ފުށުއެރުންތައް ލޯޑެއް ނުކުރެވުނު',
      emptyTitle: 'ފުށުއެރުމެއް ނެތް',
      emptyDescription:
        'ދެ ފަހަރު ފޮނުވުނު ކޮންމެ ސެޝަނެއްވެސް ފޮނުވުނީ އެއްގޮތަށް، އެއީ ރީޕްލޭއެއްގައި ފެންނަން ޖެހޭ ގޮތް.',
      colDetected: 'ފެނުނު ތާރީޚް',
      colStoredHash: 'ރައްކާކުރެވުނު ހޭޝް',
      colRejectedHash: 'ބަލައިނުގަނެވުނު ހޭޝް',
      colState: 'ހާލަތު',
      actionInspect: 'ބައްލަވާ',
      actionReviewed: 'ބެލިއްޖެ',
      modalTitle: 'ކަރަންޓީނުކުރެވުނު ޑޭޓާ',
      factStoredHash: 'ރައްކާކުރެވުނު ޑޭޓާގެ ހޭޝް',
      factRejectedHash: 'ބަލައިނުގަނެވުނު ޑޭޓާގެ ހޭޝް',
      payloadNote:
        'ތިރީގައި ހުރީ ބަލައިނުގަނެވުނު ޑޭޓާއެވެ. ޑިވައިސް ފޮނުވަން މަސައްކަތްކުރި އެއްޗެއް ސުޕަވައިޒަރަކަށް ފެންނާނެހެން ދައްކާފައި؛ ރައްކާކުރެވިފައިވާ ސެޝަނަށް ބަދަލެއް ނާދޭ. id ތައް އެނގޭ ސައިޓްތަކުގެ ނަން ކެޓަލޮގުން ދޭހަކުރެވޭނެ.',
      markReviewed: 'ބެލިއްޖެކަމަށް ފާހަގަކުރޭ',
      resolveConfirmTitle: 'މި ފުށުއެރުން ބެލިއްޖެކަމަށް ފާހަގަކުރައްވަނީތޯ؟',
      resolveConfirmMessage:
        'މިއިން ރެކޯޑުކުރެވެނީ ކަރަންޓީނުކުރެވުނު ޑޭޓާ ބައްލަވާލެއްވިކަން. ރައްކާކުރެވިފައިވާ ސެޝަނަށް ބަދަލެއް ނާންނާނެ — ފުރަތަމަ ލިބުނު ޑޭޓާއަކީ ރެކޯޑް، ކަރަންޓީނުކުރެވުނު ޑޭޓާ އޮޑިޓަށްޓަކައި ބާއްވާނެ.',
      resolveConfirmLabel: 'ބެލިއްޖެކަމަށް ފާހަގަކުރޭ',
      resolveDetailDetected: 'ފެނުނު ތާރީޚް',
      resolveSuccessTitle: 'ފުށުއެރުން ބެލިއްޖެކަމަށް ފާހަގަކުރެވިއްޖެ',
      resolveErrorTitle: 'ފުށުއެރުން އައުކުރެއް ނުކުރެވުނު',
    },
  },

  reports: {
    audit: {
      list: {
        title: 'އޮޑިޓް ޓްރެއިލް',
        subtitle: 'އެޕެންޑް-އޯންލީ، ހޭޝް-ޗެއިންޑް، PII މާސްކްކޮށްފައި',
        actorLabel: 'ފަރާތުގެ ބާވަތް',
        actorAny: 'ކޮންމެވެސް ފަރާތެއް',
        actorStaff: 'ސްޓާފް',
        actorPartner: 'ޕާޓްނަރ',
        actorSystem: 'ސިސްޓަމް',
        entityTypeLabel: 'އެންޓިޓީ ބާވަތް',
        entityLabel: 'އެންޓިޓީ',
        actionLabel: 'ޢަމަލު',
        errorTitle: 'އޮޑިޓް ޓްރެއިލް ލޯޑެއް ނުކުރެވުނު',
        tableLabel: 'އޮޑިޓް އިވެންޓްތައް',
        colWhen: 'ވަގުތު',
        colWho: 'ފަރާތް',
        colEntityId: 'އެންޓިޓީ ID',
        colRequest: 'ރިކުއެސްޓް',
        colAddress: 'އެޑްރެސް',
        colHash: 'ހޭޝް',
        emptyTitle: 'ދިމާވާ އޮޑިޓް އިވެންޓެއް ނެތް',
        emptyDescription: 'ތާރީޚް ރޭންޖް ފުޅާކުރައްވާ، ނުވަތަ ޢަމަލުގެ ފިލްޓަރ ސާފުކުރައްވާ.',
        noteBefore: 'ޗެއިން ސައްޙަކުރެވެނީ',
        noteAfter:
          '، މިތާ ނުވެ. މި ސްކްރީނުން ދައްކަނީ ކޮންމެ އިވެންޓެއްގެ ހޭޝް އަދި އޭގެ ކުރީގެ ހޭޝް ހެއްކެއްގެ ގޮތުގައި؛ އަލުން ހިސާބުކުރިކަމަށް ދަޢުވާއެއް ނުކުރެއެވެ.',
      },
      detail: {
        fallbackTitle: 'އޮޑިޓް އިވެންޓް',
        breadcrumbEvent: 'އިވެންޓް',
        errorTitle: 'މި އޮޑިޓް އިވެންޓް ލޯޑެއް ނުކުރެވުނު',
        eventCardTitle: 'އިވެންޓް',
        factOccurred: 'ހިނގި ވަގުތު',
        factRequestId: 'ރިކުއެސްޓް ID',
        changedTitle: 'ބަދަލުވި ބައިތައް',
        beforeTitle: 'ކުރިން',
        afterTitle: 'ފަހުން',
        maskedSubtitle: 'ސާވަރުން PII މާސްކްކޮށްފައި',
        chainTitle: 'ޗެއިން',
        chainSubtitle: 'ސައްޙަކުރެވެނީ fcms verify-integrity އިން، މި ސްކްރީނުން ނޫން',
        prevHash: 'ކުރީގެ ހޭޝް',
        selfHash: 'މި އިވެންޓްގެ ހޭޝް',
        genesis: 'ފުރަތަމަ',
      },
    },

    pack: {
      title: 'މެނޭޖްމަންޓް ޕެކް',
      periodLabel: 'މުއްދަތު',
      periodDaily: 'ދުވަހީ',
      periodWeekly: 'ހަފްތާވީ',
      periodMonthly: 'މަހުވީ',
      endingLabel: 'ނިމޭ ތާރީޚް',
      errorTitle: 'މެނޭޖްމަންޓް ޕެކް ލޯޑެއް ނުކުރެވުނު',
      periodTotalsLabel: 'މުއްދަތުގެ ޖުމުލަ',
      tileCollected: 'ބަލައިގަނެވުނު',
      tilePurchaseValue: 'ގަތް މަހުގެ އަގު',
      tileBillsIssued: 'ދޫކުރި ބިލްތައް',
      tilePayouts: 'ފައިސާ ދޫކުރުން',
      tileSessions: 'ސެޝަންތައް',
      tileAverageWait: 'ސަފުގައި ހޭދަވި އެވެރެޖް ވަގުތު',
      averageWaitHelp:
        'ކޮންމެ ދުވަހަކު ދޫކުރި ޓޯކަންގެ ޢަދަދުން ބަރުދަންދެވިފައި، ވީމާ މަދު ޓޯކަނާއެކު ދިގު އިންތިޒާރެއް އޮތް ހަމަހިމޭން ދުވަހެއް، ނަތީޖާ މައްޗަށް ބާރު ނުފޯރުވާނެ.',
      sectionsLabel: 'މެނޭޖްމަންޓް ޕެކްގެ ބައިތައް',
      tabCollection: 'ބަލައިގަތުން',
      tabBills: 'ބިލްތައް',
      tabPayouts: 'ފައިސާ ދޫކުރުން',
      tabSportsShare: 'ސްޕޯޓްސް ފިޝިންގގެ ހިއްސާ',
      tabFishMix: 'މަހުގެ ބާވަތްތައް',
      tabQueue: 'ސަފު',
      tableCollectionLabel: 'ދުވަހީ ބަލައިގަތުން',
      tableBillsLabel: 'ދުވަހުން ދުވަހަށް ބިލްތައް',
      tablePayoutsLabel: 'ދުވަހުން ދުވަހަށް ފައިސާ ދޫކުރުން',
      tableSportsLabel: 'ދުވަހުން ދުވަހަށް ސްޕޯޓްސް ފިޝިންގގެ ހިއްސާ',
      tableQueueLabel: 'ދުވަހުން ދުވަހަށް ސަފު',
      emptyCollection: 'މި މުއްދަތުގައި ބަލައިގަނެވުނު އެއްޗެއް ނެތް',
      emptyBills: 'މި މުއްދަތުގައި ބިލެއް ނެތް',
      emptyPayouts: 'މި މުއްދަތުގައި ފައިސާ ދޫކުރުމެއް ނެތް',
      emptySports: 'މި މުއްދަތުގައި ސްޕޯޓްސް ފިޝިންގ ބާވަތެއް ނެތް',
      emptyMix: 'ބައިކުރާނެ މަހެއް ނެތް',
      emptyQueue: 'މި މުއްދަތުގައި ސަފުގެ ހަރަކާތެއް ނެތް',
      mixCardTitle: 'ބާވަތުން ބަރުދަން',
      mixChartLabel: 'މުއްދަތުގައި މަހުގެ ބާވަތުން ސާފު ބަރުދަން',
      colDay: 'ދުވަސް',
      colChannel: 'ޗެނަލް',
      colSessions: 'ސެޝަންތައް',
      colValue: 'އަގު',
      colType: 'ބާވަތް',
      colBillsCount: 'ބިލްތައް',
      colRuns: 'ބުރުތައް',
      colPayees: 'ފައިސާ ލިބުނު ފަރާތްތައް',
      colSports: 'ސްޕޯޓްސް',
      colRegular: 'އާދައިގެ',
      colShare: 'ހިއްސާ',
      colIssued: 'ދޫކުރި',
      colServed: 'ޚިދުމަތްދިން',
      colNoShow: 'ހާޒިރުނުވި',
      colAverageWait: 'އެވެރެޖް އިންތިޒާރު',
      colP90: '90 ވަނަ ޕަސެންޓައިލް',
    },

    transactions: {
      title: 'މުޢާމަލާތް ހޯދުން',
      subtitle: 'ރިޕޯޓިންގ ރީޑް މޮޑެލްތަކުން، ހޯދޭ ކޮންމެ ބިލެއް',
      exportsMeta: 'އެކްސްޕޯޓްތަކުގައި ބޭނުންކުރެވެނީ މި ފިލްޓަރތައް، އަދި ދިމާވާ ހުރިހާ ރޯވއެއް ހިމެނޭ، ހަމައެކަނި މި ސަފުހާ ނޫން.',
      anyType: 'ކޮންމެވެސް ބާވަތެއް',
      anyChannel: 'ކޮންމެވެސް ޗެނަލެއް',
      anyStatus: 'ކޮންމެވެސް ހާލަތެއް',
      typeLabel: 'ބާވަތް',
      channelLabel: 'ޗެނަލް',
      channelRegular: 'އާދައިގެ',
      channelSports: 'ސްޕޯޓްސް',
      statusDraft: 'ޑްރާފްޓް',
      statusFinalized: 'ފައިނަލްކޮށްފައި',
      statusVoided: 'ބާތިލްކޮށްފައި',
      billNoLabel: 'ބިލް ނަންބަރު',
      fishermanHint: 'ހުރިހާ މަސްވެރިންނަށްޓަކައި ހުސްކޮށްލައްވާ.',
      searchErrorTitle: 'ހޯދުން ފުރިހަމައެއް ނުވި',
      tableLabel: 'ކުރީގެ ބިލް މުޢާމަލާތްތައް',
      emptyTitle: 'ދިމާވާ މުޢާމަލާތެއް ނެތް',
      emptyDescription: 'ތާރީޚް ރޭންޖް ފުޅާކުރައްވާ، ނުވަތަ ބިލް ނަންބަރު ފިލްޓަރ ސާފުކުރައްވާ.',
      colDay: 'ދުވަސް',
      colSettledFrom: 'ސެޓްލްކުރި ތަން',
      colSubtotal: 'ސަބްޓޯޓަލް',
      colDiscount: 'ޑިސްކައުންޓް',
      colFinalized: 'ފައިނަލްކުރި ތާރީޚް',
      thisPage: 'މި ސަފުހާ',
      chipSelected: 'ހޮވިއްޖެ',
      rangeLabel: 'ރޭންޖް',
      exportStartedTitle: 'އެކްސްޕޯޓް ފެށިއްޖެ',
      exportStartedBody: 'ފިލްޓަރކުރެވުނު ހުރިހާ ސެޓެއް، ހަމައެކަނި މި ސަފުހާ ނޫން.',
      exportErrorTitle: 'އެކްސްޕޯޓް ފުރިހަމައެއް ނުވި',
    },

    vessels: {
      title: 'ދޯނިތަކުގެ ހަރަކާތް',
      errorTitle: 'ދޯނިތަކުގެ ހަރަކާތް ލޯޑެއް ނުކުރެވުނު',
      periodTotalsLabel: 'މުއްދަތުގެ ޖުމުލަ',
      tileVesselsActive: 'ހަރަކާތްކުރި ދޯނިތައް',
      tilePurchaseValue: 'ގަތް މަހުގެ އަގު',
      perVessel: 'ކޮންމެ ދޯންޏެއް',
      perVesselPerDay: 'ކޮންމެ ދޯންޏެއް ކޮންމެ ދުވަހަކު',
      rollupTableLabel: 'މުއްދަތުގައި ދޯނިތަކުގެ ހަރަކާތް ޖަމާކުރެވިފައި',
      dailyTableLabel: 'ދުވަހުން ދުވަހަށް ދޯނިތަކުގެ ހަރަކާތް',
      emptyTitle: 'މި މުއްދަތުގައި ދޯނީގެ ހަރަކާތެއް ނެތް',
      showEveryDay: 'ކޮންމެ ދުވަހެއް ދައްކާ',
      rollUp: 'ދޯންޏަކުން ދޯންޏަކަށް ޖަމާކުރޭ',
      colDay: 'ދުވަސް',
      colSessions: 'ސެޝަންތައް',
      colValue: 'އަގު',
      colActiveDays: 'ހަރަކާތްކުރި ދުވަސް',
    },
  },

  assistant: {
    chat: {
      title: 'އެސިސްޓަންޓް',
      subtitle: 'ސީދާ ރިޕޯޓިންގ ވިއުތަކުން ބިނާކުރެވޭ ޖަވާބުތައް، ނިސްބަތާއެކު',
      conversationLabel: 'ވާހަކަ',
      dailyBrief: 'ދުވަހުގެ ޚުލާސާ',
      newConversation: 'އައު ވާހަކަ',
      conversationTurns: '{{when}} · {{count}} ބަދަލު',
      alertTitle: 'މިއިން ފެންނާނެ އަދި ނުފެންނާނެ ބައިތައް',
      alertBodyBefore: 'އެސިސްޓަންޓް ކިޔަނީ',
      alertBodyAfter:
        'ރިޕޯޓިންގ ވިއުތައް، ރަޖިސްޓަރީކުރެވިފައިވާ ގްރާފް ޓޫލްތައް މެދުވެރިކޮށެވެ، އެ ސްކީމާގައި SELECT އޮތް ޑޭޓާބޭސް ރޯލެއްގެ ދަށުން، އެހެން އެއްވެސް ބާރެއް ނެތި. މިއަށް hot ޓޭބަލަކަށް ވަދެއެއް ނުލެވޭ، ލިޔެއެއް ނުލެވޭ، އަދި ފިލްޓަރކުރެވިފައި ވަނީ ތިޔަބޭފުޅާގެ ސައިޓް ސްކޯޕަށް އެކަނި. ގްރާފަށް ސުވާލަކަށް ޖަވާބު ނުދެވޭނަމަ، އެކަން ބުނެ އެންމެ ކައިރި މިންގަނޑު ބުނެދޭނެ — ފައިސާގެ އަދަދެއް ދުވަހަކުވެސް ހީކޮށްފައި ނުބުނާނެ.',
      errorTitle: 'އެސިސްޓަންޓާ ގުޅޭ ގޮތެއް ނުވި',
      cardTitle: 'ވާހަކަ',
      emptyTitle: 'ސުވާލެއް ކުރައްވާ',
      emptyDescription: '"ފާއިތުވި މަހާ އަޅާބަލާއިރު، މިމަހު ކޮޑެއްދޫ ކިހިނެއް ދިޔައީ؟"',
      roleUser: 'ތިޔަބޭފުޅާ',
      roleAssistant: 'އެސިސްޓަންޓް',
      warningFlaggedDefault: 'މި ޖަވާބުގައި ބުނެފައި ވަނީ، ޓޫލްގެ އެއްވެސް ނަތީޖާއެއްގައި ނެތް ފައިސާގެ އަދަދެކެވެ.',
      warningHitLimit:
        'ޓޫލް ލޫޕް، މޮޑެލް އަދިވެސް ޕްލޭންކުރަނިކޮށް، އޭގެ ހަދުގައި ހުއްޓިއްޖެ. ޖަވާބު ފުރިހަމައެއް ނުވެ.',
      citationsSectionTitle: 'މި އަދަދުތައް ނެގުނު ތަން',
      citationsTableLabel: 'މި ޖަވާބަށް އެސިސްޓަންޓް ބޭނުންކުރި ޑޭޓާ',
      colMetric: 'މިންގަނޑު',
      colReadFrom: 'ބޭނުންކުރި ވިއު',
      colUnit: 'ޔުނިޓް',
      colRange: 'މުއްދަތު',
      colGroupedBy: 'ގްރޫޕްކުރި ގޮތް',
      colRows: 'ރޯވް',
      truncatedBadge: 'ކުރުކުރެވިފައި',
      noCitations: 'މި ޖަވާބުގައި ޓޫލް ޑޭޓާއެއް ބުނެފައި ނުވެއެވެ. މީގައި ހުރި ކޮންމެ އަދަދެއް ވެސް ސަކުވާވެރިކަމާއެކު ބައްލަވާ.',
      toolCallsOne: '{{count}} ޓޫލް ކޯލް',
      toolCallsMany: '{{count}} ޓޫލް ކޯލް',
      rowsCount: '{{count}} ރޯވް',
      failedBadge: 'ފެއިލްވެއްޖެ',
      rowsHash: 'ރޯވްގެ sha256 {{hash}}',
      workingLabel: 'ހިނގަނީ',
      workingText: 'ގްރާފް ޓޫލްތައް މެދުވެރިކޮށް ކުރިއަށްދަނީ. މިއީ ބަހަކަށްފަހު ބަހެއް ނޫން، ފުރިހަމަވަންދެން ކުރިއަށްދާ ކަމެކެވެ.',
      questionLabel: 'ތިޔަބޭފުޅާގެ ސުވާލު',
      questionPlaceholder: 'ފާއިތުވި މަހާ އަޅާބަލާއިރު، މިމަހު ކޮޑެއްދޫ ކިހިނެއް ދިޔައީ؟',
      askButton: 'ސުވާލުކުރޭ',
      askErrorTitle: 'ސުވާލަށް ޖަވާބެއް ނުދެވުނު',
    },

    modal: {
      launcherLabel: 'އެސިސްޓަންޓާ ސުވާލުކުރޭ',
      threadLabel: 'އެސިސްޓަންޓާއެކު ކުރެވޭ ވާހަކަ',
      openFullView: 'އެސިސްޓަންޓްގެ ފުރިހަމަ ޞަފްޙާ ހުޅުވާ',
      turnsBadge: '{{count}} ބަދަލު',
      emptyTitle: 'ރިޕޯޓިންގ ވިއުތަކުގައި ހުރި ކޮންމެ ކަމަކާ ބެހޭގޮތުން ސުވާލުކުރައްވާ',
      emptyDescription:
        'ކޮންމެ ޖަވާބެއްގައި ވެސް، އެ ޖަވާބު ނެގުނު ވިއުތަކާއި ތާރީޚީ މުއްދަތު ބުނެދޭނެއެވެ. މިތާ އެއްވެސް އަދަދެއް ހީކޮށްފައި ބުނެފައެއް ނުވެއެވެ.',
      starterHeading: 'މީގެ ތެރެއިން ސުވާލެއް ބައްލަވާ',
      starter1: 'ފާއިތުވި މަހާ އަޅާބަލާއިރު، މިމަހު ކޮޑެއްދޫ ކިހިނެއް ދިޔައީ؟',
      starter2: 'ފާއިތުވި ހަފުތާގައި އެންމެ ގިނަ މަސް ބަލައިގަތީ ކޮން ކަލެކްޝަން ޕޮއިންޓަކުން؟',
      starter3: 'މިއަދު ކޮންމެ ކަލެކްޝަން ޕޮއިންޓެއްގައި ސަފުގައި ހޭދަވި އެވެރެޖް ވަގުތަކީ ކޮބާ؟',
      starter4: 'މިވަގުތު އޮފްލައިން ޑިވައިސްއެއް އެބަހުރި ހެއްޔެވެ؟',
      shortcutHint: 'ފާހަގަ: ކޮންމެ ތަނެއްގައި ހުރެ ވެސް Ctrl K ފިއްތާލުމުން މިއެތި ހުޅުވޭނެއެވެ.',
      thinkingPlanning: 'ކިޔާނެ މިންގަނޑުތައް ކަނޑައަޅަނީ…',
      thinkingQuerying: 'ރިޕޯޓިންގ ވިއުތައް ކިޔަނީ…',
      thinkingComposing: 'ޖަވާބު ތައްޔާރުކުރަނީ…',
      thinkingElapsed: '{{seconds}}s ހޭދަވެއްޖެ',
      citedFromOne: '{{count}} ރިޕޯޓިންގ ވިއުއިން ކިޔުނު',
      citedFromMany: '{{count}} ރިޕޯޓިންގ ވިއުއިން ކިޔުނު',
      provenanceShow: 'މި އަދަދުތައް ނެގުނު ތަން ދައްކާ',
      provenanceHide: 'މި އަދަދުތައް ނެގުނު ތަން ފޮރުވާ',
      colFilters: 'ފިލްޓަރކުރި ގޮތް',
      colScope: 'ސްކޯޕް',
      unitKg: 'kg',
      unitLaari: 'laari',
      unitMinutes: 'މިނިޓް',
      unitCount: 'ޢަދަދު',
      unitPercent: 'އިންސައްތަ',
      disabledTitle: 'މި ސިސްޓަމުގައި އެސިސްޓަންޓް ނިއްވާލެވިފައި',
      disabledDescription:
        'މިއާ ނުލައި ވެސް ބެކް އޮފީހުގެ އެހެން ހުރިހާ ބައެއް މަސައްކަތްކުރާނެއެވެ. ASSISTANT_ENABLED ޖައްސަވައިދެއްވުމަށް އެޑްމިނިސްޓްރޭޓަރެއްގެ ކިބައިން އެދިވަޑައިގަންނަވާ.',
      providerDownTitle: 'މޮޑެލްއާ ގުޅޭ ގޮތެއް ނުވި',
      providerDownBody:
        'ރިޕޯޓިންގ ޑޭޓާގައި މައްސަލައެއް ނެތް — ޖަވާބު ނުދިނީ އެސިސްޓަންޓްގެ ފުށަގައި ހުރި މޮޑެލް ފޯރުކޮށްދޭ ފަރާތުންނެވެ. ކުޑަ ވަގުތުކޮޅަކަށްފަހު އަލުން ސުވާލުކުރައްވާ.',
      retryQuestion: 'އަލުން ސުވާލުކުރޭ',
      composerHint: 'Enter އިން ފޮނުވޭ · Shift + Enter އިން އައު ފޮޅުވަތެއް',
    },

    brief: {
      title: 'ދުވަހުގެ ޚުލާސާ',
      dayLabel: 'ދުވަސް',
      composeNow: 'މިހާރު ތައްޔާރުކުރޭ',
      composedByJobFor: '07:00 ގެ ވަޒީފާއިން {{day}} އަށް ތައްޔާރުކުރެވުނު',
      loadErrorTitle: 'ޚުލާސާ ލޯޑެއް ނުކުރެވުނު',
      loadingLabel: 'ޚުލާސާ ލޯޑްވަނީ',
      notFoundTitle: 'އެދުވަހަށް ޚުލާސާއެއް ނެތް',
      notFoundDescription: '07:00 ގެ ވަޒީފާ އަދި ހިނގާފައި ނުވޭ، ނުވަތަ އެދުވަހަކީ ކުރިއަށް އޮތް ދުވަހެއް.',
      composeItNow: 'މިހާރު ތައްޔާރުކުރޭ',
      notNarratedTitle: 'މި ލިޔުން މޮޑެލް އިން ލިޔެފައެއް ނުވެ',
      notNarratedBody:
        'މި ޚުލާސާ ތައްޔާރުކުރިއިރު މޮޑެލް ލިބެން ނެތުމުން، ޖުމްލަތައް ތައްޔާރުކުރެވުނީ ޓޫލްގެ ނަތީޖާތަކުން ސީދާ ގޮތަކަށެވެ. އަދަދުތަކަށް އަސަރެއް ނުކުރޭ — ދެގޮތުގައިވެސް ހަމަ އެ ޓޫލްތަކުން ނަގާ އަދަދުތަކެވެ.',
      flaggedTitle: 'މި ޚުލާސާ ފްލެގްކުރެވިއްޖެ',
      flaggedDefaultReason: 'މިއިން ބުނެފައި ވަނީ، ޓޫލްގެ ނަތީޖާއެއްގައި ނެތް ފައިސާގެ އަދަދެކެވެ.',
      flaggedKeptNote: 'ޚުލާސާ ޑިލީޓް ނުކޮށް، ސަބަބާއެކު ބަހައްޓާފައި ވަނީ، ފަހުން ރިވިއުކުރެވޭގޮތަށެވެ.',
      headlineFiguresLabel: 'މުހިއްމު އަދަދުތައް',
      briefCardTitle: 'ޚުލާސާ',
      narratedBadge: 'ބަޔާންކުރެވިފައި',
      renderedBadge: 'އަމިއްލައަށް ތައްޔާރުކުރެވިފައި',
      deliveredTo: 'ފޮނުވުނު {{count}} ފަރާތަށް',
      composedStamp: 'ތައްޔާރުކުރީ {{when}}',
      recomposedStamp: 'އެންމެ ފަހުން އަލުން ތައްޔާރުކުރީ {{when}}',
      provenanceTitle: 'ނިސްބަތް',
      provenanceSubtitle: 'މި ޚުލާސާ ތައްޔާރުކުރި ވާހަކަ',
      openAssistant: 'އެސިސްޓަންޓް ހުޅުވާ',
      provenanceNote:
        'ކޮންމެ އަދަދެއްގެ ފުށަގައި ހުރި ޓޫލް ކޯލްތައް، އެ ވާހަކައިގައި ރެކޯޑްކުރެވިފައި ވެއެވެ، ކިޔުނު ވިއު، ރޯވްގެ ޢަދަދު އަދި ރޯވްތަކުގެ ހޭޝްއާއެކު.',
      composedToast: 'ޚުލާސާ ތައްޔާރުކުރެވިއްޖެ',
      composeErrorTitle: 'ޚުލާސާ ތައްޔާރެއް ނުކުރެވުނު',
    },
  },

  masterdata: {
    includeArchived: 'އަރުޝީފުކުރެވިފައިވާ ވެސް ދައްކާ',

    /**
     * Nouns follow the wording already used for each entity's own screen
     * title, so a fisherman reading "މަސްވެރިޔާ އުފެއްދިއްޖެ" sees the same
     * word the sidebar used to get here.
     *
     * Thaana has no letter case, so `{{Noun}}` and `{{noun}}` resolve to the
     * same string here; the two tokens exist for English's sake and both are
     * carried so the token-parity check stays satisfied.
     */
    nouns: {
      vessel: { singular: 'ދޯނި', plural: 'ދޯނިތައް' },
      fishType: { singular: 'މަހުގެ ބާވަތް', plural: 'މަހުގެ ބާވަތްތައް' },
      grade: { singular: 'ގްރޭޑް', plural: 'ގްރޭޑްތައް' },
      fisherman: { singular: 'މަސްވެރިޔާ', plural: 'މަސްވެރިން' },
      scale: { singular: 'ސްކޭލް', plural: 'ސްކޭލްތައް' },
      collectionPoint: { singular: 'ކަލެކްޝަން ޕޮއިންޓް', plural: 'ކަލެކްޝަން ޕޮއިންޓްތައް' },
    },

    resource: {
      newAction: 'އައު {{noun}}',
      searchPlaceholder: '{{plural}} ހޯދާ',
      chipIncluding: 'ހިމަނާފައި',
      chipArchived: 'އަރުޝީފުކުރެވިފައި',
      listErrorTitle: '{{noun}} ލިސްޓް ލޯޑެއް ނުކުރެވުނު',
      emptyTitle: 'ދިމާވާ {{plural}} ނެތް',
      emptyDescriptionWritable: 'އައު އެއްޗެއް އުފައްދާ، ނުވަތަ ފިލްޓަރތައް ސާފުކުރޭ.',
      emptyDescriptionReadOnly: 'އިތުރަށް ބެލުމަށް ފިލްޓަރތައް ސާފުކުރޭ.',
      colState: 'ހާލަތު',
      badgeActive: 'ހަރަކާތްތެރި',
      badgeArchived: 'އަރުޝީފު',
      archiveAction: 'އަރުޝީފުކުރޭ',
      restoreAction: 'އަނބުރާ ގެންނަވާ',
      newTitle: 'އައު {{noun}}',
      editTitle: '{{noun}} އެޑިޓްކުރޭ',
      createSubmit: '{{noun}} އުފައްދާ',
      saveErrorTitle: '{{noun}} ރައްކާއެއް ނުކުރެވުނު',
      createdToast: '{{Noun}} އުފެއްދިއްޖެ',
      updatedToast: '{{Noun}} އަޕްޑޭޓްކުރެވިއްޖެ',
      archivedToast: '{{Noun}} އަރުޝީފުކުރެވިއްޖެ',
      restoredToast: '{{Noun}} އަނބުރާ ގެނެވިއްޖެ',
      archiveConfirmTitle: 'މި {{noun}} އަރުޝީފުކުރަންތޯ؟',
      archiveConfirmMessage:
        'ޕިކަރތަކުންނާއި އައު ރެކޯޑްތަކުން ނުފެންނާނެ، އަދި މިހާރު ވެސް މިއަށް ދާވާ ކޮންމެ ޑޮކިއުމަންޓެއް އެގޮތުގައި ދެމިހުންނާނެ. އެއްވެސް އެއްޗެއް ޑިލީޓެއް ނުވާނެ — އަރުޝީފުކުރުމަކީ އަނބުރާ ގެނެވޭ ކަމެއް.',
      archiveConfirmAction: '{{noun}} އަރުޝީފުކުރޭ',
      archiveErrorTitle: '{{noun}} އަރުޝީފެއް ނުކުރެވުނު',
      restoreErrorTitle: '{{noun}} އަނބުރާ ނުގެނެވުނު',
    },
    vessels: {
      list: {
        title: 'ދޯނިތައް',
        subtitle: 'ރަޖިސްޓަރީގައި ހިމެނޭ ދޯނިތަކާއި ކަލެކްޓަރ ދޯނިތައް',
        colRegistration: 'ރެޖިސްޓްރޭޝަން',
        registrationHint: 'ބިލެއްގައި، އަދި ސްކޭލް ޑިވައިސްއަކުން ދޯނި ދަންނަނީ މީގެ ޒަރީޢާއިން.',
        colCapacity: 'ކެޕޭސިޓީ',
        colLength: 'ދިގުމިން (މ)',
        lengthLabel: 'ދިގުމިން، މީޓަރުން',
        colLegacyRef: 'ކުރީގެ ރެފަރެންސް',
        colOwner: 'ވެރިފަރާތް',
        ownerLabel: 'ވެރިފަރާތް',
        ownerHint: 'މަހުގެ ބިލު ދައްކާ މަސްވެރިޔާ. ވެރިފަރާތެއް ނެތް ދޯންޏަކަށް ބިލު ނުކުރެވޭނެ.',
        noOwner: 'ވެރިފަރާތެއް ނެތް',
        crewAction: 'ފަޅުވެރިން',
      },
    },

    fishTypes: {
      list: {
        title: 'މަހުގެ ބާވަތްތައް',
        subtitle: 'ކޮންމެ އެންޓްރީއެއް، ކޮންމެ ރޭޓެއް ވެސް ދާވާ ބާވަތުގެ ކެޓަލޮގް',
        codeHint: 'ކުރު، ބަދަލުނުވާ ކޯޑެއް — ސްކޭލް ޑިވައިސް ސިންކްކުރާ ކެޓަލޮގްގައި ފެންނާނެ.',
        scientificName: 'ސައިންޓިފިކް ނަން',
        gradesAction: 'ގްރޭޑްތައް',
      },
    },

    fishGrades: {
      list: {
        pageTitle: 'ގްރޭޑްތައް — {{fishType}}',
        screenTitle: '{{fishType}} ގެ ގްރޭޑްތައް',
        subtitle: 'ބާވަތުގެ ތެރޭގައި ނޫނީ މާނައެއް ނެތް',
        breadcrumbFishTypes: 'މަހުގެ ބާވަތްތައް',
        breadcrumbGrades: 'ގްރޭޑްތައް',
        colOrder: 'ތަރުތީބު',
        colDefault: 'ޑިފޯލްޓް',
        defaultBadge: 'ޑިފޯލްޓް',
        sortOrderLabel: 'ތަރުތީބުގެ ނަންބަރު',
        sortOrderHint: 'ޕިކަރެއްގައި މި ގްރޭޑް ފެންނަ ތަން. ކުޑަ ނަންބަރު ކުރިއަށް.',
        defaultLabel: 'ޑިފޯލްޓް ގްރޭޑް',
        defaultHint:
          'ޑިވައިސް ފޮނުވާ އެންޓްރީގައި ގްރޭޑެއް ނެތްނަމަ ބޭނުންކުރެވޭ — ScalApp ގައި ގްރޭޑްގެ ބާވަތެއް ނުހުރޭ.',
      },
    },

    fishermen: {
      list: {
        title: 'މަސްވެރިން',
        subtitle: 'ކޮންމެ ވޮލެޓެއް، ބިލެއް، ފައިސާ ދޫކުރުމެއްގެ ފަހަތުގައިވާ ރަޖިސްޓަރީ',
        colNationalId: 'ދިވެހި ރައްޔިތެއްކަން',
        nationalIdHint: 'ރައްކާކުރެވިފައި ވަނީ އެންކްރިޕްޓްކޮށްފައި. ސްޕޯޓްސް ރަޖިސްޓްރޭޝަން ޔަގީންކުރަނީ މީގެ މައްޗަށެވެ.',
        colPhone: 'ފޯނު',
        phoneHint: 'ޕޯޓަލްގެ އެއް ފަހަރު ބޭނުންކުރެވޭ ކޯޑަށާއި އިއުލާންތަކަށް ބޭނުންކުރެވެއެވެ.',
        addressLabel: 'އެޑްރެސް',
        colBank: 'ބޭންކް',
        colAccount: 'އެކައުންޓް',
        bankAccountLabel: 'ބޭންކް އެކައުންޓް',
        bankAccountHint: 'ފައިސާ ދޫކުރުމުގެ ބުރުތަކުން ފައިސާ ފޮނުވާ ތަން. ރައްކާކުރެވިފައި ވަނީ އެންކްރިޕްޓްކޮށްފައި، ފައިސާ ދޫކުރުމުގެ ފައިލްގައި މާސްކްކޮށްފައި.',
        colSports: 'ސްޕޯޓްސް',
        sportsLabel: 'ސްޕޯޓްސް މަސްވެރިޔާ',
        sportsBadge: 'ސްޕޯޓްސް',
        statusSuspended: 'ސަސްޕެންޑްކޮށްފައި',
      },
    },

    scales: {
      list: {
        title: 'ސްކޭލްތައް',
        subtitle: 'ކޮންމެ ކަލެކްޝަން ޕޮއިންޓެއްގައި ހުރި ބަރުދަން ބެލޭ ސާމާނު',
        colSerial: 'ސީރިއަލް',
        serialLabel: 'ސީރިއަލް ނަންބަރު',
        colModel: 'މޮޑެލް',
        modelPlaceholder: 'WPL R10',
      },
    },

    collectionPoints: {
      list: {
        title: 'ކަލެކްޝަން ޕޮއިންޓްތައް',
        subtitle: 'މަސް ބާލައި، ބަރުދަން ބަލައި، ބިލްކުރާ ތަން',
        codeHint: 'ކުރު، ޚާއްޞަ ކޯޑެއް. މިތަނުން ދޭ ކޮންމެ ޑޮކިއުމަންޓް ނަންބަރެއްގައި ވެސް ހިމެނޭ، ވީމާ ފަހުން ބަދަލެއް ނުކުރެވޭނެ.',
        colType: 'ބާވަތް',
        typeCollectorVessel: 'ކަލެކްޓަރ ދޯނި',
        typeJetty: 'ޖެޓީ',
        typeCollectionCentre: 'ކަލެކްޝަން ސެންޓަރ',
        typeProcessingFacility: 'ޕްރޮސެސިންގ ފެސިލިޓީ',
        colTimezone: 'ޓައިމް ޒޯން',
        timezoneHint: 'IANA ނަން. ސަބަބެއް ނެތްނަމަ Indian/Maldives.',
        colCreated: 'އުފެއްދި ތާރީޚް',
      },
    },

    crew: {
      pageTitle: 'ފަޅުވެރިން — {{vessel}}',
      subtitle: 'މިހާރު ތިބި ބައެއް، ކުރިން ތިބި ބައެއް',
      breadcrumbVessels: 'ދޯނިތައް',
      breadcrumbCrew: 'ފަޅުވެރިން',
      errorTitle: 'ފަޅުވެރިންގެ ލިސްޓް ލޯޑެއް ނުކުރެވުނު',
      addCardTitle: 'ފަޅުވެރިއެއް އިތުރުކުރޭ',
      addErrorTitle: 'ފަޅުވެރިޔާ އިތުރެއް ނުކުރެވުނު',
      role: 'ދައުރު',
      rolePlaceholder: 'ކެޕްޓަން',
      addButton: 'އިތުރުކުރޭ',
      crewCardTitle: 'ފަޅުވެރިން',
      emptyTitle: 'ފަޅުވެރިއެއް ރެކޯޑްކޮށްފައެއް ނެތް',
      emptyDescription: 'މި ދޯނިން މަސްވެރިކަންކުރާ ބައެއް އިތުރުކުރައްވާ.',
      tableLabel: 'މި ދޯނީގެ ފަޅުވެރިން',
      currentBadge: 'މިހާރު',
      endAction: 'ނިންމާ',
      addedToast: 'ފަޅުވެރިޔާ އިތުރުކުރެވިއްޖެ',
      endConfirmTitle: 'މި ފަޅުވެރިކަން ނިންމަނީތޯ؟',
      endConfirmMessage:
        'ރެކޯޑްގައި ނިމުނު ތާރީޚް ހިމެނިފައި ބާއްވާނެ. މީގެ ޙަވާލާ ދޭ އެއްވެސް އެއްޗެއް ބަދަލެއް ނުވާނެ — އެ ފަޅުވެރިޔާ ހުރި ދުވަހު ބާލި ބިލެއް ދާނީ ބިލެއްގެ ގޮތުގައި.',
      endConfirmLabel: 'ފަޅުވެރިކަން ނިންމާ',
      endDetailEnding: 'ނިމޭ ތާރީޚް',
      endedToast: 'ފަޅުވެރިކަން ނިމިއްޖެ',
      endErrorTitle: 'ފަޅުވެރިކަން ނިންމައެއް ނުކުރެވުނު',
    },
  },

  settings: {
    security: {
      title: 'ޓޫ-ފެކްޓަރ އޮތެންޓިކޭޝަން',
      subtitle: 'ޕާސްވޯޑާއެކު، އޮތެންޓިކޭޓަރ އެޕަކުން ލިބޭ ކޯޑެއް',
      on: 'އޮން',
      off: 'އޮފް',
      genericErrorTitle: 'އެކަން ކާމިޔާބެއް ނުވި',
      onCardTitle: 'ޓޫ-ފެކްޓަރ އޮންވެފައި',
      onCardBody: 'ކޮންމެ ސައިން-އިންއެއްގައި ވެސް ޕާސްވޯޑަށްފަހު، އޮތެންޓިކޭޓަރ އެޕުން 6 ޑިޖިޓް ކޯޑެއް ބޭނުންވާނެ.',
      turnOff: 'ޓޫ-ފެކްޓަރ ނިއްވާ',
      offCardTitle: 'ޓޫ-ފެކްޓަރ އޮންކުރޭ',
      whyTitle: 'ދެ މިނިޓް ހޭދަކުރަން ޙައްޤު ސަބަބެއް',
      whyBody:
        'ބެކް-އޮފީސް އެކައުންޓްތަކުން ކަލެކްޝަން ޕޮއިންޓްތަކުގައި ހިއްސާވާ ޑިވައިސްތަކުން ސައިން-އިންވެ، ބިލްފައިނަލްކޮށް، ރޭޓް ޕަބްލިޝްކޮށް، ފައިސާ ދޫކުރުމުގެ ބުރުތައް ފާސްކުރެވިދާނެ. އެހެން މީހަކުގެ މެންދުރުފަހަކާ، މި ކަންތައްތަކާ ދެމެދު ހުރީ ހަމައެކަނި ޕާސްވޯޑެއް.',
      beginEnrolment: 'ރެޖިސްޓްރޭޝަން ފަށާ',
      finishCardTitle: 'ރެޖިސްޓްރޭޝަން ފުރިހަމަކުރޭ',
      finishCardSubtitle: 'ސީކްރެޓް އެޕަށް ލައްވާ، ފަހުން ކޯޑެއް ޔަގީންކުރައްވާ',
      secretLabel: 'ސީކްރެޓް',
      secretInstruction:
        'މިހާރު މި ސީކްރެޓް ތިޔަބޭފުޅާގެ އޮތެންޓިކޭޓަރ އެޕަށް ލައްވާ. މިއަށްފަހު ދެވަނަ ފަހަރަށް ނުދައްކާނެ — އަލުން ރެޖިސްޓްރޭޝަން ފެށިއްޔާ ތަފާތު ސީކްރެޓެއް ދޭނެ.',
      uriIntro: 'ނުވަތަ، ތިޔަބޭފުޅާގެ އެޕުން ބަލައިގަނެފިއްޔާ، މި URI ބޭނުންކުރައްވާ',
      notFinishedTitle: 'ކޯޑެއް ޔަގީންނުކުރަނީސް ރެޖިސްޓްރޭޝަން ފުރިހަމައެއް ނުވާނެ',
      notFinishedBody:
        'އުފެއްދި، ދުވަހަކުވެސް ޔަގީންނުކުރެވުނު ސީކްރެޓަކުން، ފަހު ސައިން-އިންގައި ތިޔަބޭފުޅާ ބަންދުވެދާނެ. ތިރީގައި އެޕުން ކޯޑެއް ލައްވާ.',
      codeLabel: 'އެޕުން ލިބުނު ކޯޑް',
      confirmAndTurnOn: 'ޔަގީންކޮށް އޮންކުރޭ',
      turnedOnToast: 'ޓޫ-ފެކްޓަރ އޮތެންޓިކޭޝަން އޮންވެއްޖެ',
      turnedOnToastBody: 'ކޮންމެ ސައިން-އިންއެއްގައި ވެސް ކޯޑަކަށް އަހާނެ.',
      turnOffConfirmTitle: 'ޓޫ-ފެކްޓަރ އޮތެންޓިކޭޝަން ނިއްވަނީތޯ؟',
      turnOffConfirmMessage:
        'ތިޔަބޭފުޅާގެ އެކައުންޓް ރައްކާތެރިކުރެވޭނީ ހަމައެކަނި ޕާސްވޯޑަކުން. ކަލެކްޝަން ޕޮއިންޓެއްގައި ހިއްސާވާ ޑިވައިސްއެއްގައި މިއީ ރައްކާތެރިކަން ބޮޑުތަނުން ދަށްވުމެއް.',
      turnOffConfirmLabel: 'ނިއްވާ',
      turnedOffToast: 'ޓޫ-ފެކްޓަރ އޮތެންޓިކޭޝަން ނިއްވިއްޖެ',
      turnOffErrorTitle: 'ނިއްވައެއް ނުކުރެވުނު',
    },

    mySessions: {
      title: 'އަހަރެންގެ ސެޝަންތައް',
      subtitle: 'މި އެކައުންޓް ސައިން-އިންވެފައިވާ ކޮންމެ ތަނެއް',
      signOutEverywhere: 'ހުރިހާ ތަނުން ސައިން އައުޓް',
      errorTitle: 'ތިޔަބޭފުޅާގެ ސެޝަންތައް ލޯޑެއް ނުކުރެވުނު',
      emptyTitle: 'ސެޝަނެއް ނެތް',
      emptyDescription: 'މިކަން ވެގެންވާނީ ނުވާ ކަމަކަށް — ސަފުހާ އައުކުރައްވާ.',
      tableLabel: 'މި އެކައުންޓްގެ ސެޝަންތައް',
      address: 'އެޑްރެސް',
      colSignedIn: 'ސައިން-އިންވި ތާރީޚް',
      colExpires: 'ނިމޭ ތާރީޚް',
      unknownDevice: 'ނޭނގޭ ޑިވައިސް',
      stateRevoked: 'ބާތިލްކޮށްފައި',
      stateActive: 'އެކްޓިވް',
      revokeConfirmTitle: 'މި ސެޝަން ސައިން-އައުޓް ކުރައްވަނީތޯ؟',
      revokeConfirmMessage: 'ބޭނުންކުރަނީ ކާކުކަމަކުވިޔަސް، ފަހަރައި ސައިން-އައުޓް ވެ، އަލުން އޮތެންޓިކޭޓް ކުރަން ޖެހޭނެ.',
      revokeConfirmLabel: 'ސެޝަން ބާތިލްކުރޭ',
      revokedToast: 'ސެޝަން ބާތިލްކުރެވިއްޖެ',
      revokeErrorTitle: 'ސެޝަން ބާތިލެއް ނުކުރެވުނު',
      revokeAllConfirmTitle: 'ހުރިހާ ތަނުން ސައިން-އައުޓް ކުރައްވަނީތޯ؟',
      revokeAllConfirmMessage:
        'ތިޔަބޭފުޅާ މިހާރު ބޭނުންކުރަމުންދާ ސެޝަން ވެސް ހިމެނިގެން، ހުރިހާ ސެޝަނެއް ބާތިލްކުރެވޭނެ. ސައިން-އިން ސަފުހާއަށް ބަދަލުކުރެވޭނެ.',
      revokeAllConfirmLabel: 'ހުރިހާ ތަނުން ސައިން އައުޓް',
      activeSessionsLabel: 'އެކްޓިވް ސެޝަންތައް',
      revokedAllToastOne: '{{count}} ސެޝަން ބާތިލްކުރެވިއްޖެ',
      revokedAllToastMany: '{{count}} ސެޝަން ބާތިލްކުރެވިއްޖެ',
      revokeAllErrorTitle: 'ސެޝަންތައް ބާތިލެއް ނުކުރެވުނު',
      note:
        'ސާވަރުގައި ރައްކާކުރެވިފައި ވަނީ ސެޝަން ޓޯކަންގެ SHA-256 އެކަނި، ވީމާ ޑޭޓާބޭސް ލީކްވިޔަސް ބޭނުންކުރެވޭ ސެޝަނެއް ނުލިބޭނެ. ބާތިލްކުރުން ވަގުތުން ފެށިގެން ހިނގާނެ.',
    },
  },

  integrations: {
    partnerLabel: 'ޕާޓްނަރ',
    everyPartner: 'ހުރިހާ ޕާޓްނަރެއް',
    choosePartner: 'ޕާޓްނަރެއް ހޮއްވާލައްވާ',
    doneCopiedSecret: 'ނިމިއްޖެ — ސީކްރެޓް ކޮޕީކުރެވިއްޖެ',

    apiKeys: {
      list: {
        title: 'API ކީތައް',
        subtitle: '/partner/v1 އަށް ޕާޓްނަރ ކްރެޑެންޝަލްތައް — HMAC ސޮއިކޮށްފައި، ސްކޯޕްކޮށްފައި، ބާތިލްކުރެވޭ',
        requestLogAction: 'ރިކުއެސްޓް ލޮގް',
        issueKeyAction: 'ކީ ދޫކުރޭ',
        errorTitle: 'API ކީތައް ލޯޑެއް ނުކުރެވުނު',
        tableLabel: 'ޕާޓްނަރ API ކީތައް',
        colKey: 'ކީ',
        colKeyId: 'ކީ id',
        colScopes: 'ސްކޯޕްތައް',
        colRateLimit: 'ރޭޓް ލިމިޓް',
        colLastUsed: 'އެންމެ ފަހުން ބޭނުންކުރީ',
        colExpires: 'މުއްދަތު ހަމަވާ ތާރީޚް',
        emptyTitle: 'API ކީއެއް ނެތް',
        emptyDescription: 'ރަޖިސްޓަރީކުރެވިފައިވާ ޕާޓްނަރަކަށް ކީއެއް ދޫކުރައްވާ.',
        noneBadge: 'ނެތް',
        usageAction: 'ބޭނުންކުރި ގޮތް',
        stateActive: 'އެކްޓިވް',
        stateExpired: 'މުއްދަތު ހަމަވެފައި',
        stateRevoked: 'ބާތިލްކޮށްފައި',
        createModalTitle: 'API ކީއެއް ދޫކުރޭ',
        createErrorTitle: 'ކީ ދޫކުރެއް ނުކުރެވުނު',
        secretLabel: 'ސީކްރެޓް',
        secretInstruction:
          'މި ސީކްރެޓުން މި ޕާޓްނަރ ފޮނުވާ ކޮންމެ ރިކުއެސްޓެއް ސޮއިކުރެވެއެވެ، އަދި މީ ދައްކާނީ އެންމެ އެއް ފަހަރު. ސާވަރުގައި ރައްކާކުރެވިފައި ވަނީ argon2 ހޭޝް އެކަނި، ވީމާ ދެވަނަ ފަހަރަށް ދެއްކޭކަށެއް ނެތް — ސީކްރެޓް ގެއްލިއްޖެނަމަ ކުރެވޭ ކަމަކީ އައު ކީއެއް ދޫކުރުން.',
        issuedKeyIdLabel: 'ކީ id — X-Api-Key ގެ ގޮތުގައި ފޮނުވޭ',
        issuedNameLabel: 'ނަން',
        issuedScopesLabel: 'ސްކޯޕްތައް',
        scopeWarningTitle: 'ޕާޓްނަރ API ޑޮކިއުމަންޓުން ސްކޯޕް ލިސްޓް ކިޔައެއް ނުނުކުރެވުނު',
        scopeWarningBody:
          'ތިރީގައި ސްކޯޕްތައް، ސްޕޭސް ނުވަތަ ކޮމާއަކުން ވަކިކޮށް، އަތުން ލިޔުއްވާ. ސާވަރ ފަރާތުން ދެގޮތަށްވެސް ވެލިޑޭޓްކުރެވޭނެ.',
        partnerNoOptionsHint: 'ޕާޓްނަރެއް ރަޖިސްޓަރީކޮށްފައި ނެތް. ފުރަތަމަ އެއް ރަޖިސްޓަރީކުރައްވާ.',
        keyNameLabel: 'ކީގެ ނަން',
        keyNameHint: 'މި ކީ ބޭނުންވަނީ ކޮންކަމަކަށްކަން — “Fuel supplier production”.',
        scopesLabel: 'ސްކޯޕްތައް',
        scopeUsedBy: '{{operations}} ބޭނުންކުރެއެވެ',
        scopesTextHint: 'ސްޕޭސް ނުވަތަ ކޮމާއަކުން ވަކިކުރައްވާ، މިސާލަކަށް: catalog.read fuel.issue',
        expiresOnLabel: 'މުއްދަތު ހަމަވާ ތާރީޚް',
        expiresOnHint: 'މުއްދަތެއް ނެތް ކީއަކަށް ހުސްކޮށް ބައްވާ.',
        rateLimitLabel: 'މިނިޓަކަށް ރޭޓް ލިމިޓް',
        ipAllowlistLabel: 'IP އެލޯލިސްޓް',
        ipAllowlistHint: 'އިޚްތިޔާރީ. ސްޕޭސް ނުވަތަ ކޮމާއަކުން ވަކިކުރައްވާ. ހުސްކޮށް ބޭއްވުމުން ކޮންމެ އެޑްރެހަކުން ވެސް ބޭނުންކުރެވޭނެ.',
        revokeConfirmTitle: 'މި ކީ ބާތިލްކުރައްވަނީތޯ؟',
        revokeConfirmMessage:
          'މި ޕްރޮސެސްގައި ކީ ވަގުތުން ބޭނުންނުކުރެވޭ ގޮތަށްވާނެ. ކީ ބަލާ ކެޝްގެ މުއްދަތު ދިހަ ސިކުންތު ކަމުން، އެހެން API ރެޕްލިކާއެއްގައި ދިހަ ސިކުންތު ވަންދެން ބޭނުންކުރެވިދާނެ — މިއީ ލިޔެފައިވާ، ގަސްދުގައި ބޭއްވި މުއްދަތެއް، ބަގެއް ނޫން.',
        revokeConfirmLabel: 'ކީ ބާތިލްކުރޭ',
        revokeDetailKey: 'ކީ',
        revokeDetailKeyId: 'ކީ id',
        revokedToast: 'ކީ ބާތިލްކުރެވިއްޖެ',
        revokeErrorTitle: 'ކީ ބާތިލެއް ނުކުރެވުނު',
      },

      detail: {
        titleFallback: 'API ކީ',
        breadcrumb: 'API ކީތައް',
        breadcrumbCurrent: 'ކީ',
        revokedBadge: 'ބާތިލްކޮށްފައި',
        errorTitle: 'މި ކީ ލޯޑެއް ނުކުރެވުނު',
        tilesLabel: 'ބޭނުންކުރި ޖުމުލަ',
        statRequests: 'ރިކުއެސްޓް',
        statSuccessful: 'ކާމިޔާބުވި',
        statClientErrors: 'ކްލައިންޓް އެރަރ',
        statClientErrorsHelp:
          'ބައިވަރު 4xx އަރައިގެންދާނަމަ، ޢާންމުކޮށް ސަބަބަކީ ޕާޓްނަރ ފަރާތުގައި ސޮއިކުރުމުގައި، ކްލޮކް-ސްކިއުއެއްގައި ނުވަތަ ސްކޯޕެއްގައި ހުރި މައްސަލައެއް.',
        statServerErrors: 'ސާވަރ އެރަރ',
        chartCardTitle: 'ދުވާލަކަށް ރިކުއެސްޓް',
        chartCardSubtitle: 'ސްޓޭޓަސް ކްލާހުން ބައިކޮށްފައި',
        chartLabel: 'މި ކީއަށް ދުވާލަކަށް ލިބުނު ރިކުއެސްޓް ޢަދަދު، ރެސްޕޮންސް ސްޓޭޓަސް ކްލާހުން ބައިކޮށްފައި',
        chartEmptyTitle: 'މި ކީ ބޭނުންކޮށްފައެއް ނުވެ',
        chartEmptyDescription: 'މީގައި ސޮއިކޮށްފައި އެއްވެސް އެއްޗެއް ނުވެ.',
        seriesSuccess: 'ކާމިޔާބު (2xx)',
        seriesClientError: 'ކްލައިންޓް އެރަރ (4xx)',
        seriesServerError: 'ސާވަރ އެރަރ (5xx)',
        keyCardTitle: 'ކީ',
        detailKeyId: 'ކީ id (X-Api-Key)',
        detailScopes: 'ސްކޯޕްތައް',
        detailRate: 'ރޭޓް ލިމިޓް',
        detailRateValue: 'މިނިޓަކަށް {{value}}',
        detailAllowlist: 'IP އެލޯލިސްޓް',
        detailAnyAddress: 'ކޮންމެ އެޑްރެހެއް',
        detailExpires: 'މުއްދަތު ހަމަވާ ތާރީޚް',
        detailLastUsed: 'އެންމެ ފަހުން ބޭނުންކުރީ',
        detailRevoked: 'ބާތިލްކުރި ތާރީޚް',
        recentRequestsCardTitle: 'އެންމެ ފަހުގެ ރިކުއެސްޓްތައް',
        tableLabel: 'މި ކީއިން ކުރި އެންމެ ފަހުގެ ޕާޓްނަރ ރިކުއެސްޓްތައް',
        colWhen: 'ވަގުތު',
        colMethod: 'މެތޮޑް',
        colPath: 'ޕާތު',
        colLatency: 'ލޭޓެންސީ',
        colSource: 'މަޞްދަރު',
        colRequestId: 'ރިކުއެސްޓް id',
        latencyValue: '{{value}} ms',
        emptyTitle: 'ރިކުއެސްޓެއް ލޮގްކޮށްފައި ނެތް',
        emptyDescription: 'މި ކީއިން ސޮއިކޮށްފައި އެއްވެސް އެއްޗެއް ނުވެ.',
      },
    },

    webhooks: {
      list: {
        title: 'ވެބްހުކްތައް',
        subtitle: 'ޕްލެޓްފޯމް އިވެންޓްތައް ފޮނުވާ ތަނާއި، ވި ގޮތް',
        registerEndpoint: 'އެންޑްޕޮއިންޓް ރަޖިސްޓަރީކުރޭ',
        errorTitle: 'ވެބްހުކް އެންޑްޕޮއިންޓްތައް ލޯޑެއް ނުކުރެވުނު',
        tableLabel: 'ވެބްހުކް އެންޑްޕޮއިންޓްތައް',
        colEndpoint: 'އެންޑްޕޮއިންޓް',
        colEvents: 'އިވެންޓްތައް',
        emptyTitle: 'ވެބްހުކް އެންޑްޕޮއިންޓެއް ނެތް',
        emptyDescription: 'ބިލެއް ފައިނަލްވުމުން ޕާޓްނަރަށް އެންގުމަށްޓަކައި އެއް ރަޖިސްޓަރީކުރައްވާ.',
        stateActive: 'އެކްޓިވް',
        stateInactive: 'އިންއެކްޓިވް',
        deliveriesAction: 'ފޮނުވިތައް',
        createModalTitle: 'ވެބްހުކް އެންޑްޕޮއިންޓެއް ރަޖިސްޓަރީކުރޭ',
        createErrorTitle: 'އެންޑްޕޮއިންޓް ރަޖިސްޓަރީއެއް ނުކުރެވުނު',
        secretLabel: 'ސައިނިންގ ސީކްރެޓް',
        secretInstruction:
          'މި ސީކްރެޓުން މި އެންޑްޕޮއިންޓަށް ފޮނުވާ ކޮންމެ ފޮނުވުމެއް ސޮއިކުރެވެއެވެ، މީގެ ސަބަބުން އެއީ އަހަރެމެންގެ ފަރާތުން ފޮނުވިކަން ޕާޓްނަރަށް ޔަގީންކުރެވެއެވެ. މީ ދައްކާނީ އެންމެ އެއް ފަހަރު. ބަދަލުކުރުމުން ކުރީގެ ސީކްރެޓް ވަގުތުން ބޭނުންނުކުރެވޭ ގޮތަށްވާނެ.',
        issuedEndpointLabel: 'އެންޑްޕޮއިންޓް:',
        urlLabel: 'އެންޑްޕޮއިންޓް URL',
        urlHint: 'http ނުވަތަ https ކަމުގައި، އަދި ޕްރައިވެޓް، ލޫޕްބެކް ނުވަތަ މެޓަޑޭޓާ އެޑްރެހަކަށް ރިޒޯލްވެގެން ނުވާނެ.',
        eventsLabel: 'އިވެންޓްތައް',
        createdToast: 'ވެބްހުކް ރަޖިސްޓަރީކުރެވިއްޖެ',
      },

      detail: {
        title: 'ވެބްހުކް އެންޑްޕޮއިންޓް',
        breadcrumb: 'ވެބްހުކްތައް',
        breadcrumbCurrent: 'އެންޑްޕޮއިންޓް',
        inactiveBadge: 'އިންއެކްޓިވް',
        rotateAction: 'ސީކްރެޓް ބަދަލުކުރޭ',
        deactivateAction: 'ޑިއެކްޓިވޭޓްކުރޭ',
        errorTitle: 'މި އެންޑްޕޮއިންޓް ލޯޑެއް ނުކުރެވުނު',
        newSecretCardTitle: 'އައު ސައިނިންގ ސީކްރެޓް',
        secretLabel: 'ސައިނިންގ ސީކްރެޓް',
        secretInstruction: 'މީ ދެއްވި ވަގުތު ކުރީގެ ސީކްރެޓް ބޭނުންނުކުރެވޭ ގޮތަށްވެއްޖެ. މިހާރު ޕާޓްނަރަށް މި ދެއްވާ.',
        cardTitle: 'އެންޑްޕޮއިންޓް',
        detailEndpoint: 'އެންޑްޕޮއިންޓް',
        detailDescription: 'ތަފްޞީލު',
        detailEvents: 'ސަބްސްކްރައިބްކޮށްފައިވާ އިވެންޓްތައް',
        detailActive: 'އެކްޓިވް',
        detailLastChange: 'އެންމެ ފަހުގެ ބަދަލު',
        inactiveAlertTitle: 'ފޮނުވުން ހުއްޓިފައި',
        inactiveAlertBody: 'މި އެންޑްޕޮއިންޓް އިންއެކްޓިވް. އިވެންޓްތައް ފޮނުވަމުން ނުދޭ؛ ތިރީގައިވަނީ ތާރީޚީ ލޮގެއް.',
        rotateConfirmTitle: 'ސައިނިންގ ސީކްރެޓް ބަދަލުކުރައްވަނީތޯ؟',
        rotateConfirmMessage:
          'އައު ސީކްރެޓެއް ދޭ، ކުރީގެ ސީކްރެޓް ވަގުތުން ބޭނުންނުކުރެވޭ ގޮތަށްވާނެ. ކުރީގެ ސީކްރެޓުން ސޮއިކުރެވިފައި ފޮނުވޭ ކޮންމެ ފޮނުވުމެއް، ޕާޓްނަރ އައުކުރަންދެން ވެރިފިކޭޝަން ފެއިލްވާނެ، ވީމާ ފުރަތަމަ ޕާޓްނަރާ ބަދަލާމެދު ވާހަކަދައްކާ.',
        rotateConfirmLabel: 'ސީކްރެޓް ބަދަލުކުރޭ',
        rotateErrorTitle: 'ސީކްރެޓް ބަދަލެއް ނުކުރެވުނު',
        deactivateConfirmTitle: 'މި އެންޑްޕޮއިންޓް ޑިއެކްޓިވޭޓްކުރައްވަނީތޯ؟',
        deactivateConfirmMessage:
          'ފޮނުވުން ހުއްޓޭނެ. އެންޑްޕޮއިންޓް ދުވަހަކުވެސް ފުހެވިގެން ނުދާނެ — ފޮނުވުމުގެ ލޮގުން މީގެ ޙަވާލާ ދޭނެ — ވީމާ އަލުން އެކްޓިވްކޮށްލައިގެން މިކަން ފިލުވޭނެ.',
        deactivateConfirmLabel: 'ޑިއެކްޓިވޭޓްކުރޭ',
        deactivatedToast: 'އެންޑްޕޮއިންޓް ޑިއެކްޓިވޭޓްކުރެވިއްޖެ',
        deactivateErrorTitle: 'އެންޑްޕޮއިންޓް ޑިއެކްޓިވޭޓެއް ނުކުރެވުނު',
        deliveryErrorTitle: 'ފޮނުވުމުގެ ލޮގް ލޯޑެއް ނުކުރެވުނު',
        deliveryCardTitle: 'ފޮނުވުމުގެ ލޮގް',
        deliveryCardSubtitle: 'ކޮންމެ މަސައްކަތެއް، ވަކި ދާރެއް',
        tableLabel: 'ވެބްހުކް ފޮނުވުމުގެ މަސައްކަތްތައް',
        colAttempted: 'މަސައްކަތްކުރި ތާރީޚް',
        colEvent: 'އިވެންޓް',
        colAttempt: 'މަސައްކަތް',
        colResponse: 'ރެސްޕޮންސް',
        colDuration: 'ދިގުމިން',
        colNextRetry: 'ދެން ޓްރައިކުރާ ވަގުތު',
        colError: 'އެރަރ',
        failedBadge: 'ފެއިލްވެއްޖެ',
        latencyValue: '{{value}} ms',
        replayAction: 'އަލުން ފޮނުވާ',
        emptyTitle: 'ފޮނުވުމެއް އަދި ނެތް',
        emptyDescription: 'މި އެންޑްޕޮއިންޓް ރަޖިސްޓަރީކުރި ފަހުން ސަބްސްކްރައިބްކުރި އެއްވެސް ކަމެއް ހިނގާފައެއް ނުވެ.',
        editModalTitle: 'އެންޑްޕޮއިންޓް އެޑިޓްކުރޭ',
        editUrlLabel: 'އެންޑްޕޮއިންޓް URL',
        editDescriptionLabel: 'ތަފްޞީލު',
        eventsLabel: 'އިވެންޓްތައް',
        saveErrorTitle: 'އެންޑްޕޮއިންޓް ރައްކާއެއް ނުކުރެވުނު',
        savedToast: 'އެންޑްޕޮއިންޓް އައުކުރެވިއްޖެ',
        replayConfirmTitle: 'މި ފޮނުވުން އަލުން ފޮނުވަނީތޯ؟',
        replayConfirmMessage:
          'އަސްލު އިވެންޓް އައު މަސައްކަތެއްގެ ގޮތުގައި އަލުން ފޮނުވޭނެ، މަސައްކަތުގެ ނަންބަރު ކުރިއަށްދާނީ މި ފޮނުވުމުގެ ތާރީޚުން، ލޮގް އެއް ސިލްސިލާއެއްގެ ގޮތުގައި ފެންނަން.',
        replayConfirmLabel: 'ފޮނުވުން އަލުން ފޮނުވާ',
        replayDetailEvent: 'އިވެންޓް',
        replayDetailAttempt: 'މަސައްކަތް',
        deliveredToastTitle: 'ފޮނުވިއްޖެ',
        deliveredToastBody: 'HTTP {{status}}، {{duration}} ms',
        replayFailedTitle: 'އަލުން ފޮނުވުމުގެ މަސައްކަތްކުރެވުނު، ފެއިލްވެއްޖެ',
        replayErrorTitle: 'ފޮނުވުން އަލުން ފޮނުވައެއް ނުކުރެވުނު',
      },
    },

    partners: {
      list: {
        title: 'ޕާޓްނަރުން',
        subtitle: 'API ކީ ބާއްވާ، ވެބްހުކް ލިބޭ ފަރާތްތައް',
        apiKeysAction: 'API ކީތައް',
        registerAction: 'ޕާޓްނަރ ރަޖިސްޓަރީކުރޭ',
        errorTitle: 'ޕާޓްނަރުން ލޯޑެއް ނުކުރެވުނު',
        tableLabel: 'ޕާޓްނަރ ފަރާތްތައް',
        colContact: 'ކޮންޓެކްޓް',
        colEmail: 'އީމެއިލް',
        colPhone: 'ފޯނު',
        emptyTitle: 'ޕާޓްނަރެއް ރަޖިސްޓަރީކޮށްފައި ނެތް',
        emptyDescription: 'API ކީއެއް ދޫކުރުމުގެ ކުރިން އެއް ރަޖިސްޓަރީކުރައްވާ.',
        stateActive: 'އެކްޓިވް',
        stateArchived: 'އާކައިވްކޮށްފައި',
        keysAction: 'ކީތައް',
        archiveAction: 'އާކައިވްކުރޭ',
        restoreAction: 'އަނބުރާ ގެނޭ',
        createModalTitle: 'ޕާޓްނަރ ރަޖިސްޓަރީކުރޭ',
        editModalTitle: 'ޕާޓްނަރ އެޑިޓްކުރޭ',
        saveErrorTitle: 'ޕާޓްނަރ ރައްކާއެއް ނުކުރެވުނު',
        nameLabel: 'ނަން',
        kindPlaceholder: 'fuel_supplier',
        kindHint: 'ކުރު، މެޝިން-ފަދަ ލޭބަލެއް. ޕާޓްނަރ ޕްރޮފައިލްތައް ގްރޫޕްކުރެވެނީ މީގެ ޒަރީޢާއިން.',
        contactNameLabel: 'ކޮންޓެކްޓް ނަން',
        contactEmailLabel: 'ކޮންޓެކްޓް އީމެއިލް',
        contactPhoneLabel: 'ކޮންޓެކްޓް ފޯނު',
        registerSubmit: 'ޕާޓްނަރ ރަޖިސްޓަރީކުރޭ',
        updatedToast: 'ޕާޓްނަރ އައުކުރެވިއްޖެ',
        registeredToast: 'ޕާޓްނަރ ރަޖިސްޓަރީކުރެވިއްޖެ',
        archiveConfirmTitle: 'މި ޕާޓްނަރ އާކައިވްކުރައްވަނީތޯ؟',
        archiveConfirmMessage:
          'އައު ކީތަކާއި އައު ވެބްހުކްތަކުގެ ޕިކަރުތަކުން ފެންނާނުވެ ދާނެ. މިހާރު ހުރި ކީތައް ބޭނުންކުރެވެމުންދާނެ — އެއީ ބޭނުންވަނީނަމަ ވަކިވަކިން ބާތިލްކުރައްވާ.',
        archiveConfirmLabel: 'ޕާޓްނަރ އާކައިވްކުރޭ',
        archivedToast: 'ޕާޓްނަރ އާކައިވްކުރެވިއްޖެ',
        archiveErrorTitle: 'ޕާޓްނަރ އާކައިވެއް ނުކުރެވުނު',
        restoredToast: 'ޕާޓްނަރ އަނބުރާ ގެނެވިއްޖެ',
        restoreErrorTitle: 'ޕާޓްނަރ އަނބުރާ ނުގެނެވުނު',
      },
    },

    requestLog: {
      title: 'ޕާޓްނަރ ރިކުއެސްޓް ލޮގް',
      subtitle: 'ސޮއިކުރެވިފައި ފޮނުވި ކޮންމެ ކޯލެއް، ވި ގޮތާއި ލޭޓެންސީ',
      errorTitle: 'ރިކުއެސްޓް ލޮގް ލޯޑެއް ނުކުރެވުނު',
      everyKey: 'ހުރިހާ ކީއެއް',
      keyLabel: 'ކީ',
      pathLabel: 'ޕާތު',
      pathPlaceholder: '/partner/v1/fuel',
      sinceLabel: 'ފެށޭ ތާރީޚް',
      untilLabel: 'ނިމޭ ތާރީޚް',
      rowsLabel: 'ދާރުތައް',
      ceilingAlertTitle: 'އެންމެ އައު {{limit}} ދާރު ދައްކަނީ',
      ceilingAlertBody:
        'އިތުރު ދާރު ހުރެދާނެ. ތާރީޚް ރޭންޖް ކުޑަކުރައްވާ ނުވަތަ ދާރު ޢަދަދު އިތުރުކުރައްވާ — މި އެންޑްޕޮއިންޓް ދޭނީ ސީލިންގައި، ޕޭޖިނޭޓްކޮށްފައި ނުވާ ބައެއް.',
      tableLabel: 'ޕާޓްނަރ API ރިކުއެސްޓް ލޮގް',
      colWhen: 'ވަގުތު',
      colKey: 'ކީ',
      colMethod: 'މެތޮޑް',
      colPath: 'ޕާތު',
      colLatency: 'ލޭޓެންސީ',
      colSource: 'މަޞްދަރު',
      colRequestId: 'ރިކުއެސްޓް id',
      latencyValue: '{{value}} ms',
      emptyTitle: 'ދާވާ ރިކުއެސްޓެއް ނެތް',
      emptyDescription: 'ތާރީޚް ރޭންޖް ބޮޑުކުރައްވާ، ނުވަތަ ކީ ފިލްޓަރ ސާފުކުރައްވާ.',
      chipPathStartsWith: 'ފެށެނީ',
      chipSince: 'ފެށޭ ތާރީޚް',
      chipUntil: 'ނިމޭ ތާރީޚް',
    },

    erpExport: {
      title: 'ERP އަދި އެކްސްޕޯޓް',
      subtitle: 'އެކައުންޓިންގ ސިސްޓަމަށް ޖާނަލް ބެޗްތައް، އަދި ސަރުކާރަށް ފޮނުވާ ބާނާ މަހުގެ ރިޕޯޓް',
      unmappedAlertTitle: 'ބައެއް އެކައުންޓް ބާވަތްތަކުގައި GL މެޕިންގއެއް ނެތް',
      unmappedLead:
        'GL ކޯޑެއް ނެތް އެކައުންޓަކުން ހިމެނޭ ޖާނަލް ލައިނެއް، ހުސް ކޮލަމަކާއެކު އެކްސްޕޯޓްވެ، އެކައުންޓެންޓަށް ދިމާވާނީ ހީސާބުކުރަން. މެޕްކޮށްފައި ނުވަނީ:',
      mapCardTitle: 'GL އެކައުންޓް މެޕް',
      mapCardSubtitle: 'ވޮލެޓް އެކައުންޓް ބާވަތުން އެކައުންޓިންގ ކޯޑަށް',
      mapErrorTitle: 'އެކައުންޓް މެޕް ލޯޑެއް ނުކުރެވުނު',
      mapEmptyTitle: 'އެކައުންޓެއް މެޕްކޮށްފައި ނެތް',
      mapEmptyDescription: 'ފުރަތަމަ އެކްސްޕޯޓްގެ ކުރިން ކޮންމެ ވޮލެޓް އެކައުންޓް ބާވަތެއް GL ކޯޑަކަށް މެޕްކުރައްވާ.',
      tableLabel: 'ERP GL އެކައުންޓް މެޕިންގ',
      colAccountKind: 'ވޮލެޓް އެކައުންޓް ބާވަތް',
      colGlCode: 'GL ކޯޑް',
      colGlName: 'GL އެކައުންޓް ނަން',
      colLastChange: 'އެންމެ ފަހުގެ ބަދަލު',
      accountKindLabel: 'އެކައުންޓް ބާވަތް',
      glCodeLabel: 'GL ކޯޑް',
      glNameLabel: 'GL އެކައުންޓް ނަން',
      saveMapping: 'މެޕިންގ ރައްކާކުރޭ',
      mappingSavedToast: 'މެޕިންގ ރައްކާކުރެވިއްޖެ',
      mapSaveErrorTitle: 'މެޕިންގ ރައްކާއެއް ނުކުރެވުނު',
      journalCardTitle: 'ERP ޖާނަލް އެކްސްޕޯޓް',
      journalCardSubtitle: 'ޕޯސްޓްކުރެވުނު ކޮންމެ ޖާނަލް ލައިނަކަށް ދާރެއް',
      downloadCsv: 'CSV ޑައުންލޯޑްކުރޭ',
      journalNote:
        'މި ރޭންޖްގައި ފައިނަލްކުރެވުނު ޑޮކިއުމަންޓްތައް ހިމެނޭ. ޖާނަލް ރީޑަރެއް ވަޔަރ ނުކުރެވިފައިވާ ޑިޕްލޯއިމަންޓެއްގައި {{code}} ދޭނެ، މި ސަފުހާ ރިޕޯޓްކުރާނީ އެ ކަން، ހުސް ފައިލެއް ޑައުންލޯޑްކުރުމުގެ ބަދަލުގައި.',
      journalDownloadedToast: 'ޖާނަލް އެކްސްޕޯޓް ޑައުންލޯޑްކުރެވިއްޖެ',
      journalErrorTitle: 'ERP އެކްސްޕޯޓް ފުރިހަމައެއް ނުވި',
      govCardTitle: 'ސަރުކާރަށް ފޮނުވާ ބާނާ މަހުގެ ތަފާސް ހިސާބު',
      govCardSubtitle: 'ކޮންމެ މަހަކަށް، ކޮންމެ ބާވަތަކަށް ދާރެއް',
      periodLabel: 'މުއްދަތު',
      periodHint: 'އަހަރާއި މަސް، 2026-08 ފަދައިން.',
      govDownloadedToast: 'ބާނާ މަހުގެ ތަފާސް ހިސާބު ޑައުންލޯޑްކުރެވިއްޖެ',
      govErrorTitle: 'ބާނާ މަހުގެ ތަފާސް ހިސާބުގެ އެކްސްޕޯޓް ފުރިހަމައެއް ނުވި',
      historyErrorTitle: 'އެކްސްޕޯޓް ތާރީޚް ލޯޑެއް ނުކުރެވުނު',
      historyCardTitle: 'އެކްސްޕޯޓް ތާރީޚް',
      historyCardSubtitle: 'ދެއްކިފައިވާ އެއްޗެއް، އަދި އޭގެ ޗެކްސަމް',
      historyTableLabel: 'ކުރީގެ ERP އަދި ސަރުކާރު އެކްސްޕޯޓްތައް',
      colGenerated: 'ދެއްކި ތާރީޚް',
      colAdapter: 'އެޑަޕްޓަރ',
      colPeriod: 'މުއްދަތު',
      colRows: 'ދާރު',
      colSha: 'SHA-256',
      historyEmptyTitle: 'އަދި އެކްސްޕޯޓެއް ދައްކާފައި ނުވެ',
    },

    apiDocs: {
      fallbackTitle: 'ޕާޓްނަރ API',
      versionLabel: 'ވަރޝަން {{version}}',
      downloadAction: 'OpenAPI ޑައުންލޯޑްކުރޭ',
      reloadAction: 'އަލުން ލޯޑްކުރޭ',
      errorTitle: 'API ޑޮކިއުމަންޓް ލޯޑެއް ނުކުރެވުނު',
      loadingLabel: 'API ޑޮކިއުމަންޓް ލޯޑްވަނީ',
      specTitle: 'މި ސަފުހާ އެއީ ސްޕެސިފިކޭޝަން',
      specBody:
        'ތިރީގައިވާ ހުރިހާ އެއްޗެއް ދައްކަނީ ސާވަރ އެކްސްޕޯޓްކުރާ {{path}} އިން. ޕާޓްނަރ އޮޕަރޭޝަނެއް މިތާ ނެތްނަމަ، އެއީ ނެތް އެއްޗެއް؛ މިތާ ހުރިނަމަ، ދައްކާ ސްކޯޕް މިޑްލްވެއަރ ފޯރުކުރުވާނެ.',
      tabsLabel: 'API ޑޮކިއުމަންޓޭޝަން ބައިތައް',
      tabEndpoints: 'އެންޑްޕޮއިންޓްތައް',
      tabAuth: 'އޮތެންޓިކޭޝަން',
      tabSchemas: 'ސްކީމާތައް',
      filterLabel: 'ފިލްޓަރ',
      filterPlaceholder: 'ޕާތު، ސަމަރީ، އޮޕަރޭޝަން ނުވަތަ ސްކޯޕުން ފިލްޓަރކުރޭ',
      noEndpointTitle: 'ދާވާ އެންޑްޕޮއިންޓެއް ނެތް',
      noEndpointDescription: 'މުޅި ސަރފޭސް ބައްލަވަން ފިލްޓަރ ސާފުކުރައްވާ.',
      colName: 'ނަން',
      colIn: 'ތާ',
      colType: 'ބާވަތް',
      colField: 'ފީލްޑް',
      parametersHeading: 'ޕެރެމީޓަރ',
      requestBodyHeading: 'ރިކުއެސްޓް ބޮޑީ',
      responseHeading: 'ރެސްޕޮންސް',
      requiredBadge: 'ލާޒިމް',
      yesValue: 'އާނ',
      noValue: 'ނޫން',
      authIntroTitle: 'ޕާޓްނަރެއް އޮތެންޓިކޭޓްކުރާ ގޮތް',
      authIntro1:
        'ކޮންމެ {{path}} ރިކުއެސްޓެއްގައި ވެސް ތިން ހެޑަރ ހިމެނޭ: {{h1}} (ސީކްރެޓް ނޫން، ކީ id)، {{h2}} (unix ސިކުންތު)، އަދި {{h3}}.',
      authFormula: 'ސިގްނޭޗަރ އަކީ {{formula}}، ކެނޮނިކަލް ސްޓްރިންގ އަކީ {{canonical}}.',
      authListSkew:
        'ސާވަރ ގަޑިއިން ފަސް މިނިޓަށްވުރެ ދުރު ރިކުއެސްޓެއް {{code}} އިން ދޫކޮށްލެވޭނެ. ފޮނުވާ ފަރާތުގެ ގަޑި ސިންކްކުރައްވާ؛ ބައިކޮޅުގައި ވިންޑޯ ބޮޑުނުކުރައްވާ، ސަބަބަކީ ނުކުރެވޭނެ.',
      authListReplay:
        'ތަކުރާރުވާ ސިގްނޭޗަރއެއް {{code}} އިން ދޫކޮށްލެވޭނެ. ނޮންސް ކެޝްގެ ސަބަބުން ނަގާފައިވާ ރިކުއެސްޓެއް ބޭކާރުވެއެވެ.',
      authListIdempotency:
        'ކޮންމެ ޕާޓްނަރ write އެއްގައި ވެސް {{header}} ބޭނުންވޭ. އެއް ކީއިން އަލުން ޓްރައިކުރުމުން، އަސްލު ރެސްޕޮންސް ބައިޓަކުން ބައިޓަކަށް އަލުން ފޮނުވޭނެ؛ އެއް ކީ، ތަފާތު ބޮޑީއަކާއެކު، {{code}} އިން ދޫކޮށްލެވޭނެ، ރިޕްލޭއެއްގެ ގޮތުގައި ނުބަލައި.',
      authListScope:
        'ކީއެއްގައި ސްކޯޕްތަކެއް ހިމެނޭ. އެއިން ބޭރު ކޯލެއް {{code}} އިން ދޫކޮށްލެވޭނެ — އޮޕަރޭޝަން ހުރޭ، ކްރެޑެންޝަލް އެއަކާ ނުފެރެއެއޭ.',
      walkthroughTitle: 'ސޮއިކުރުމުގެ ވޯކްތްރޫ',
      walkthroughSubtitle: 'ކޮޕީކުރައްވާ، ފޭސްޓްކުރައްވާ، ތިޔަބޭފުޅާގެ ކީ ބައްލަވާ',
      copiedToast: 'މިސާލު ކޮޕީކުރެވިއްޖެ',
      copyFailedToastTitle: 'އޮޓޯކޮށް ކޮޕީއެއް ނުކުރެވުނު',
      copyFailedToastBody: 'މިސާލު ހޮއްވާލައްވާ، އަތުން ކޮޕީކުރައްވާ.',
      securitySchemesTitle: 'ސެކިއުރިޓީ ސްކީމް',
      noSchemasTitle: 'މި ޑޮކިއުމަންޓުގައި ނަން ދެވިފައިވާ ސްކީމާއެއް ނެތް',
      noSchemasDescription: 'އެއިގެ އޮޕަރޭޝަންތަކުން ބުނެދެނީ ބައި-ލޯޑްތައް، ސީދާ.',
    },
  },

  sports: {
    quotaTokens: {
      title: 'ކޯޓާ ޓޯކަން',
      subtitle: 'ޕަބްލިޝްކުރެވިފައިވާ ޝަރުތަކާ ދާވާގޮތަށް ބާލޭ ޙައްޤު',
      whoseTitle: 'ކާކުގެ ޓޯކަން؟',
      chooseFishermanTitle: 'މަސްވެރިޔެއް ހޮއްވާލައްވާ',
      chooseFishermanDescription: 'ޓޯކަން ބާއްވަނީ މީހެއް، ވީމާ ލިސްޓް ދައްކަނީ ކޮންމެ މަސްވެރިޔަކަށް ވަކިވަކިން.',
      errorTitle: 'ޓޯކަންތައް ލޯޑެއް ނުކުރެވުނު',
      tableLabel: 'މި މަސްވެރިޔާ ބާއްވާ ކޯޓާ ޓޯކަންތައް',
      colRequirement: 'ޝަރުތު',
      colAllocated: 'ދިން މިންވަރު',
      colRedeemed: 'ބޭނުންކުރި',
      colChannel: 'ޗެނަލް',
      emptyTitle: 'ޓޯކަނެއް ނެތް',
      emptyDescription: 'މި މަސްވެރިޔާ ބާއްވަނީ ކޯޓާ ޓޯކަނެއް ނެތް. ޕަބްލިޝްކުރެވިފައިވާ ޝަރުތަކުން އެއް ދޫކުރައްވާ.',
      billAction: 'ބިލް',
      cancelConfirmTitle: 'މި ކޯޓާ ޓޯކަން ކެންސަލްކުރައްވަނީތޯ؟',
      cancelConfirmMessage: 'ދިން މިންވަރު ޝަރުތަށް އަނބުރާ ދާނެ، މަސްވެރިޔާއަކަށް މިއަށްފަހު މި ޓޯކަނުން ބާލައެއް ނުގަނެވޭނެ.',
      cancelConfirmLabel: 'ޓޯކަން ކެންސަލްކުރޭ',
      cancelDetailAllocated: 'ދިން މިންވަރު',
      cancelledToast: 'ޓޯކަން ކެންސަލްކުރެވިއްޖެ',
      cancelledToastBody: 'މިންވަރު ޝަރުތަށް އަނބުރާ ލެއިވިއްޖެ.',
      cancelErrorTitle: 'ޓޯކަން ކެންސަލެއް ނުކުރެވުނު',
      note:
        'ޓޯކަނެއް ބޭނުންކުރެވެނީ ކަލެކްޝަން ޕޮއިންޓްގައި، އެއް މުޢާމަލާތެއްގައި ސެޝަނަކާއި ސްޕޯޓްސް ބިލެއް އުފެދިގެން. މި ސަފުހާއިން ދޫކުރެވޭ، ކެންސަލްކުރެވޭ؛ ބޭނުމެއް ނުކުރެވޭ.',
    },

    requirements: {
      list: {
        title: 'ބާލައިގަތުމުގެ ޝަރުތުތައް',
        subtitle: 'ސްޕޯޓްސް އަދި ދަތުރުވެރި މަސްވެރިންގެ ފަރާތުން HQ ބާލައިގަންނަ ބައި',
        newAction: 'އައު ޝަރުތެއް',
        errorTitle: 'ޝަރުތުތައް ލޯޑެއް ނުކުރެވުނު',
        anyStatus: 'ކޮންމެވެސް ހާލަތެއް',
        statusDraft: 'ޑްރާފްޓް',
        statusPublished: 'ޕަބްލިޝްކޮށްފައި',
        statusClosed: 'ބަންދުކޮށްފައި',
        tableLabel: 'ސްޕޯޓްސް ބާލައިގަތުމުގެ ޝަރުތުތައް',
        colTarget: 'ޓާގެޓް',
        colRemaining: 'ބާކީ',
        colFilled: 'ފުރިހަމަވި މިންވަރު',
        colWindow: 'ވިންޑޯ',
        filledLabel: 'ފުރިހަމަވި މިންވަރު',
        emptyTitle: 'މި ފިލްޓަރތަކާ ދާވާ ޝަރުތެއް ނެތް',
        emptyDescription: 'ސްޕޯޓްސް ބާލައިގަތުމަށް ސައިޓެއް ހުޅުވަން އެއް ޕަބްލިޝްކުރައްވާ.',
        createModalTitle: 'އައު ބާލައިގަތުމުގެ ޝަރުތެއް',
        createErrorTitle: 'ޝަރުތު އުފައްދައެއް ނުކުރެވުނު',
        createNote:
          'ޑްރާފްޓެއްގެ ގޮތުގައި އުފެއްދިއްޖެ. ޕަބްލިޝްކުރެވުމުން ނޫނީ، ޕަބްލިކް ސަފުހާތަކުން ފެންނާނުވެ، ކޯޓާ ޓޯކަނެއް ވެސް ދޫކުރެވިގެން ނުވާނެ.',
        chooseSite: 'ކަލެކްޝަން ޕޮއިންޓެއް ހޮއްވާލައްވާ',
        targetWeightLabel: 'ޓާގެޓް ބަރުދަން',
        ratePerKgLabel: 'ކިލޯއަކަށް ރޭޓް',
        windowOpensLabel: 'ވިންޑޯ ހުޅުވޭ ތާރީޚް',
        windowClosesLabel: 'ވިންޑޯ ބަންދުވާ ތާރީޚް',
        createSubmit: 'ޑްރާފްޓް އުފައްދާ',
        createdToast: 'ޝަރުތު ޑްރާފްޓެއްގެ ގޮތުގައި އުފެއްދިއްޖެ',
        createdToastBody: 'ޢާންމުކުރުމަށް ޕަބްލިޝްކުރައްވާ.',
      },

      detail: {
        titleFallback: 'ޝަރުތު',
        breadcrumb: 'ބާލައިގަތުމުގެ ޝަރުތުތައް',
        breadcrumbCurrent: 'ޝަރުތު',
        publishAction: 'ޕަބްލިޝްކުރޭ',
        issueTokenAction: 'ކޯޓާ ޓޯކަން ދޫކުރޭ',
        closeAction: 'ބަންދުކުރޭ',
        errorTitle: 'މި ޝަރުތު ލޯޑެއް ނުކުރެވުނު',
        draftAlertTitle: 'އަދި ޕަބްލިޝްކޮށްފައި ނުވެ',
        draftAlertBody: 'ޑްރާފްޓެއް ޕަބްލިކް ސަފުހާތަކުން ފެންނާނުވެ، ކޯޓާ ޓޯކަނެއް ވެސް ދޫކުރެވިގެން ނުވާނެ.',
        tilesLabel: 'ޝަރުތުގެ ޢަދަދުތައް',
        statTarget: 'ޓާގެޓް',
        statRemaining: 'ބާކީ',
        statRemainingHelp: 'ދޫކުރެވިފައިވާ ކޯޓާ ޓޯކަންތަކާ ދާވާގޮތަށް ސާވަރުން ބަލަހައްޓާ ޢަދަދު. ބްރައުޒަރުގައި ހިސާބުކުރެވިގެން ނުވޭ.',
        statRatePerKg: 'ކިލޯއަކަށް ރޭޓް',
        statFilled: 'ފުރިހަމަވި މިންވަރު',
        cardTitle: 'ޝަރުތު',
        detailWindowOpens: 'ވިންޑޯ ހުޅުވޭ ތާރީޚް',
        detailWindowCloses: 'ވިންޑޯ ބަންދުވާ ތާރީޚް',
        detailPublished: 'ޕަބްލިޝްކުރި ތާރީޚް',
        publishConfirmTitle: 'މި ޝަރުތު ޕަބްލިޝްކުރައްވަނީތޯ؟',
        publishConfirmMessage:
          'ޕަބްލިކް ސަފުހާތަކުން ފެންނާނެ، ކޯޓާ ޓޯކަން ދޫކުރެވެން ފަށާނެ. ސްޕޯޓްސް މަސްވެރިޔާއަށް މިތާ ބާލޭކަން އެނގޭ ގޮތަކީ ޕަބްލިޝްކުރުން.',
        publishConfirmLabel: 'ޕަބްލިޝްކުރޭ',
        publishDetailSite: 'ސައިޓް',
        publishDetailTarget: 'ޓާގެޓް',
        publishedToast: 'ޝަރުތު ޕަބްލިޝްކުރެވިއްޖެ',
        publishErrorTitle: 'ޕަބްލިޝްކުރެއް ނުކުރެވުނު',
        closeConfirmTitle: 'މި ޝަރުތު ބަންދުކުރައްވަނީތޯ؟',
        closeConfirmMessage: 'އިތުރު ކޯޓާ ޓޯކަނެއް ދޫކުރެވިގެން ނުވާނެ. ދޫކުރެވިފައިވާ ޓޯކަންތައް، ދިން މިންވަރު ބާއްވައިގެން، ބޭނުންކުރެވިދާނެ.',
        closeConfirmLabel: 'ޝަރުތު ބަންދުކުރޭ',
        closeDetailRemaining: 'ބާކީ',
        closedToast: 'ޝަރުތު ބަންދުކުރެވިއްޖެ',
        closeErrorTitle: 'ޝަރުތު ބަންދެއް ނުކުރެވުނު',
        issueModalTitle: 'ކޯޓާ ޓޯކަނެއް ދޫކުރޭ',
        issueModalSubtitle: 'ބާކީ ހުރި މިންވަރުން ގޭޓްކޮށްފައި',
        issueErrorTitle: 'ޓޯކަން ދޫކުރެއް ނުކުރެވުނު',
        fishermanHint: 'ސްޕޯޓްސް ރަޖިސްޓްރޭޝަން އެކްޓިވްކޮށް ހުންނަން ޖެހޭނެ.',
        allocatedWeightLabel: 'ދޭ ބަރުދަން',
        allocatedWeightHint: 'ބާކީ ހުރި މިންވަރަށްވުރެ ބޮޑުވެގެން ނުވާނެ — ސާވަރ ބަލައި، {{code}} އިން ދޫކޮށްލާނެ.',
        validForHoursLabel: 'ބާރުކުރާ މުއްދަތު (ގަޑިއިރު)',
        issueSubmit: 'ޓޯކަން ދޫކުރޭ',
        issuedToast: '{{weight}} kg އަށް ކޯޓާ ޓޯކަން ދޫކުރެވިއްޖެ',
        note:
          'ޓޯކަނެއް ބޭނުންކުރުމުން، {{channel}} ޓެގްކުރެވިފައިވާ ސެޝަނަކާއި ބިލެއް އުފެދޭ، އެއަށްފަހު އެއީ އާދައިގެ ފައިޕްލައިނުން ދާނެ — އެއް ފައިނަލައިޒް، އެއް ލެޖަރ، އެއް ފައިސާ ދޫކުރުމުގެ ބުރު.',
      },
    },

    registrations: {
      title: 'ސްޕޯޓްސް ރަޖިސްޓްރޭޝަން',
      subtitle: 'އެއް މަސްވެރިޔެއްގެ ރަޖިސްޓްރޭޝަން ބައްލަވާ',
      findCardTitle: 'ރަޖިސްޓްރޭޝަނެއް ބައްލަވާ',
      findNote:
        'ރަޖިސްޓްރޭޝަން ބައްލަވަނީ ބްރައުޒްކުރުމުގެ ބަދަލުގައި، ކޮންމެ މަސްވެރިޔަކަށް ވަކިވަކިން. ކޮންމެ ރަޖިސްޓްރޭޝަނެއްގައި ވެސް ވެރިފައިކުރެވިފައިވާ ދިވެހި ރައްޔިތެއްކަމުގެ ނަންބަރާއި ފޯނު ނަންބަރު ހިމެނޭ، ގަސްދުގައި މީގެ ލިސްޓެއް ދެއްކުމުގެ ފީޗަރއެއް ނުހުރޭ.',
      notFoundErrorTitle: 'ރަޖިސްޓްރޭޝަނެއް ނުފެނުނު',
      cardTitle: 'ރަޖިސްޓްރޭޝަން',
      detailNidVerified: 'ދިވެހި ރައްޔިތެއްކަން ވެރިފައިކުރެވިފައި',
      detailVerifiedOn: 'ވެރިފައިކުރި ތާރީޚް',
      detailPhoneVerified: 'ފޯނު ވެރިފައިކުރެވިފައި',
      notVerified: 'ވެރިފައިކޮށްފައި ނުވެ',
      detailLastChange: 'އެންމެ ފަހުގެ ބަދަލު',
      suspendAction: 'ރަޖިސްޓްރޭޝަން ސަސްޕެންޑްކުރޭ',
      suspendConfirmTitle: 'މި ސްޕޯޓްސް ރަޖިސްޓްރޭޝަން ސަސްޕެންޑްކުރައްވަނީތޯ؟',
      suspendConfirmMessage:
        'މި މަސްވެރިޔާއަށް އިތުރު ކޯޓާ ޓޯކަނެއް ދޫކުރެވިގެން ނުވާނެ. ދޫކުރެވިފައިވާ ޓޯކަންތަކަށް އަސަރެއް ނުކުރާނެ — އެއީ ބޭނުންވަނީނަމަ ވަކިވަކިން ކެންސަލްކުރައްވާ.',
      suspendConfirmLabel: 'ރަޖިސްޓްރޭޝަން ސަސްޕެންޑްކުރޭ',
      suspendedToast: 'ރަޖިސްޓްރޭޝަން ސަސްޕެންޑްކުރެވިއްޖެ',
      suspendErrorTitle: 'ރަޖިސްޓްރޭޝަން ސަސްޕެންޑެއް ނުކުރެވުނު',
      chooseFishermanTitle: 'މަސްވެރިޔެއް ހޮއްވާލައްވާ',
      chooseFishermanDescription: 'ނަމެއް ނުވަތަ ދިވެހި ރައްޔިތެއްކަމުގެ ނަންބަރެއް ޓައިޕްކުރަން ފައްޓަވާ.',
      notFoundTitle: 'މި މަސްވެރިޔާގެ ރަޖިސްޓްރޭޝަނެއް ނެތް',
      notFoundDescription: 'އެ ފަރާތުން ސްޕޯޓްސް ރަޖިސްޓްރޭޝަން ފުރިހަމަކޮށްފައެއް ނުވެ.',
    },
  },

  auth: {
    login: {
      title: 'ސައިން އިން',
      errorTitle: 'ސައިން-އިންވެއެއް ނުކުރެވުނު',
      usernameLabel: 'ޔޫސަރނަން',
      passwordLabel: 'ޕާސްވޯޑް',
      submit: 'ސައިން އިން',
    },

    totp: {
      title: 'ޓޫ-ފެކްޓަރ ކޯޑް',
      subtitle: 'ތިޔަބޭފުޅާގެ އޮތެންޓިކޭޓަރ އެޕުން 6 ޑިޖިޓް ކޯޑް ލައްވާ.',
      errorTitle: 'އެ ކޯޑް ބަލައިނުގަނެވުނު',
      codeLabel: 'އޮތެންޓިކޭޝަން ކޯޑް',
      verify: 'ޔަގީންކުރޭ',
      startOver: 'އަލުން ފައްޓަވާ',
    },
  },
}

export default dv
